import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate, formatDateTime, formatDateTimeSeconds, formatFileSize, validatePlate } from "@/lib/api";
import MobileInputWithWA from "@/components/MobileInputWithWA";
import PaymentMethodSelect from "@/components/PaymentMethodSelect";
import { useFirstTimeHint } from "@/lib/useFirstTimeHint";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { PolicyTypeBadge } from "@/components/PolicyTypeBadge";
import { AttachmentsHoverPreview } from "@/components/AttachmentsHoverPreview";
import { QuoteDetailsForm } from "@/components/QuoteDetailsForm";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { useAttachmentPreview } from "@/hooks/useAttachmentPreview";
import { prefillQuoteDetails } from "@/lib/quotePrefill";
import { Plus, Sparkles, Trash2, Printer, MessageSquare, Send, Pencil, FileText, Paperclip, Download, ChevronDown, ChevronUp, Maximize2, Minimize2, ShieldCheck, Zap, Car, Pin, PinOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DropZone } from "@/components/DropZone";
import { askMultiFileAction } from "@/components/MultiFileUploadDialog";
import { CopyButton } from "@/components/CopyButton";
import { CoveragePicker } from "@/components/CoveragePicker";
import { ClientCombobox } from "@/components/ClientCombobox";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { MoneyInput } from "@/components/MoneyInput";
import { ScanButton } from "@/components/ScanButton";
import { CameraButton } from "@/components/CameraButton";
import { usePolicyTypes } from "@/lib/usePolicyTypes";
import { EmailPreviewDialog, shouldSkipEmailPreview } from "@/components/EmailPreviewDialog";
import { QuoteCommunications } from "@/components/QuoteCommunications";
import { DatePicker } from "@/components/DatePicker";
import ApplyPdfTemplateButton from "@/components/ApplyPdfTemplateButton";
import CollapsiblePanel from "@/components/CollapsiblePanel";
import { downloadWithProgress } from "@/lib/downloadWithProgress";

const buildWhatsAppLink = (client, quote) => {
  const rawPhone = client?.mobile || client?.phone;
  if (!rawPhone) return null;
  let normalized = rawPhone.replace(/[^\d+]/g, "");
  if (!normalized.startsWith("+")) {
    if (normalized.startsWith("00")) normalized = "+" + normalized.slice(2);
    else if (normalized.startsWith("3")) normalized = "+39" + normalized; // Italian mobiles
    else normalized = "+" + normalized;
  }
  const wa = normalized.replace("+", "");
  const typeLabel = TYPE_LABEL[quote.type] || quote.type;
  const msg = encodeURIComponent(
    `Ciao ${client.name}, ti scrivo a proposito della tua richiesta di preventivo ${typeLabel}. ` +
    `Ho qualche aggiornamento per te — fammi sapere quando hai un minuto.`,
  );
  return `whatsapp://send?phone=${wa}&text=${msg}`;
};

// Fallback statico per helper module-level (la lista dinamica viene caricata da /api/policy-types).
const TYPES = [
  { v: "auto", l: "Auto" }, { v: "casa", l: "Casa" }, { v: "vita", l: "Vita" },
  { v: "salute", l: "Salute" }, { v: "infortuni", l: "Infortuni" },
  { v: "viaggio", l: "Viaggio" }, { v: "responsabilita_civile", l: "RC" },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.v, t.l]));
const STATUSES = ["nuovo", "in_lavorazione", "accettato", "rifiutato"];
const empty = { client_id: "", type: "auto", coverage_request: "", estimated_premium: "", details: {}, agent_id: "", internal_notes: "" };

const DETAIL_LABELS = {
  // Dati personali (estratti da libretto/CI/polizza)
  owner_name: "Nome intestatario", owner_surname: "Cognome intestatario",
  owner_fiscal_code: "Codice fiscale", owner_birth_date: "Data di nascita",
  owner_birth_place: "Luogo di nascita", owner_address: "Indirizzo (via)",
  owner_house_number: "N. civico", owner_cap: "CAP", owner_city: "Città",
  owner_province: "Provincia", owner_email: "Email", owner_phone: "Cellulare / Telefono",
  // Veicolo (dati tecnici in italiano)
  vehicle_plate: "Targa", vehicle_brand: "Marca", vehicle_model: "Modello",
  vehicle_version: "Versione/Allestimento", vehicle_year: "Anno immatricolazione",
  vehicle_first_registration: "Data prima immatricolazione",
  fuel: "Alimentazione", displacement: "Cilindrata (cc)",
  kw: "Potenza (KW)", cv: "Potenza (CV)",
  seats: "Numero posti", max_mass: "Massa massima (t)",
  co2_emissions: "Emissioni CO₂ (g/km)", euro_class: "Classe Euro",
  vehicle_use: "Uso del veicolo (libretto)", vehicle_category: "Categoria veicolo",
  chassis_number: "Numero telaio (VIN)", next_review_date: "Data prossima revisione",
  ownership_date: "Data intestazione (PRA)", previous_owners: "Precedenti intestatari",
  annual_km: "Km annui", use: "Uso del veicolo", parking: "Custodia", anti_theft: "Antifurto",
  // Conducente
  birth_date: "Data di nascita conducente", youngest_driver_age: "Età conducente più giovane",
  driving_type: "Tipo di guida", education: "Titolo di studio", profession: "Professione",
  license_first_issue: "Data primo rilascio patente",
  // Storico assicurativo
  merit_class: "Classe di merito", years_insured: "Anni assicurato",
  claims_last_5y: "Sinistri ultimi 5 anni", current_company: "Compagnia attuale",
  current_policy_expiry: "Scadenza polizza attuale",
  // Legge Bersani / RC Familiare
  bersani_applicable: "Eredita classe (Bersani/RC Familiare)",
  bersani_type: "Tipo agevolazione",
  bersani_relative_kinship: "Grado di parentela",
  bersani_relative_class: "Classe ereditata",
  bersani_relative_plate: "Targa veicolo familiare",
  bersani_relative_company: "Compagnia familiare",
  // 🆕 Feb 2026 — Nuovi campi Bersani (wizard/preventivo detail)
  bersani_ref_plate: "Targa veicolo di riferimento",
  bersani_ref_company: "Compagnia veicolo di riferimento",
  bersani_ref_merit_class: "Classe merito veicolo di riferimento",
  bersani_ref_owner: "Intestatario veicolo di riferimento",
  bersani_ref_owner_name: "Nome intestatario",
  bersani_ref_owner_surname: "Cognome intestatario",
  bersani_ref_owner_fiscal_code: "Codice fiscale intestatario",
  bersani_ref_owner_birth_date: "Data di nascita intestatario",
  bersani_ref_owner_birth_place: "Luogo di nascita intestatario",
  bersani_ref_owner_gender: "Sesso intestatario",
  // 🆕 Feb 2026 — Situazione assicurativa + Tipo soggetto
  insurance_status: "Situazione assicurativa",
  subject_type: "Tipo soggetto",
  // 🆕 Feb 2026 — Traduzione campi Azienda + Legale Rappresentante
  owner_ragione_sociale: "Ragione sociale intestatario",
  owner_partita_iva: "Partita IVA intestatario",
  ragione_sociale: "Ragione sociale",
  partita_iva: "Partita IVA",
  pec_email: "Email PEC",
  sdi_code: "Codice SDI",
  codice_fiscale_societario: "Codice fiscale societario",
  legale_rappresentante_name: "Nome legale rappresentante",
  legale_rappresentante_surname: "Cognome legale rappresentante",
  legale_rappresentante_fiscal_code: "Codice fiscale legale rappresentante",
  legale_rappresentante_birth_date: "Data di nascita legale rappresentante",
  legale_rappresentante_birth_place: "Luogo di nascita legale rappresentante",
  legale_rappresentante_birth_province: "Provincia di nascita legale rappresentante",
  legale_rappresentante_gender: "Sesso legale rappresentante",
  legale_rappresentante_role: "Ruolo legale rappresentante",
  legale_rappresentante_nationality: "Nazionalità legale rappresentante",
  legale_rappresentante_email: "Email legale rappresentante",
  legale_rappresentante_mobile: "Cellulare legale rappresentante",
  legale_rappresentante_phone: "Telefono legale rappresentante",
  vehicle_plate_override: "Targa manuale (override)",
  // Altri preventivi
  other_quotes_done: "Altri preventivi richiesti", other_quotes_amount: "Miglior preventivo (€)",
  other_quotes_frazionamento: "Frazionamento offerto",
  // Casa
  property_type: "Tipo immobile", property_address: "Indirizzo immobile", property_sqm: "Superficie (m²)",
  property_year: "Anno costruzione", property_value: "Valore (€)", coverage_options: "Garanzie richieste",
  // Vita
  insured_capital: "Capitale (€)", duration_years: "Durata (anni)", beneficiaries: "Beneficiari", smoker: "Fumatore",
  // Salute
  family_members: "Componenti nucleo", preexisting_conditions: "Patologie preesistenti",
  // Infortuni
  activity: "Attività", invalidity_capital: "Capitale invalidità (€)", death_capital: "Capitale caso morte (€)",
  // Viaggio
  destination: "Destinazione", departure_date: "Partenza", return_date: "Ritorno", travelers_count: "N° viaggiatori",
  // RC
  rc_scope: "Ambito RC", rc_max_coverage: "Massimale (€)",
  notes: "Note",
  // 🆕 Feb 2026 — Etichette italiane per campi che compaiono nella sezione "Altri dati"
  policy_number: "Numero polizza",
  company: "Compagnia",
  company_id: "ID compagnia",
  broker_id: "ID broker/agenzia",
  frazionamento: "Frazionamento",
  start_date: "Data inizio",
  end_date: "Data scadenza",
  last_review_date: "Data ultima revisione",
  _libretto_extras: "Dati aggiuntivi libretto",
  vehicle_fuel: "Alimentazione",
  vehicle_kw: "Potenza (kW)",
  vehicle_displacement: "Cilindrata (cc)",
  vehicle_additional_fuel: "Alimentazione aggiuntiva",
  marital_status: "Stato civile",
  owner_marital_status: "Stato civile contraente",
  owner_gender: "Sesso contraente",
  requested_coverages: "Garanzie richieste (Wizard)",
  selected_coverages: "Garanzie selezionate",
  purchase_date: "Data acquisto",
  vehicle_purchase_date: "Data acquisto veicolo",
  bollo_due_date: "Scadenza bollo",
  bollo_amount: "Importo bollo (€)",
  driving_license_issued_at: "Rilascio patente",
  driving_license_expiry: "Scadenza patente",
  // 🆕 Codici interni da nascondere nella scheda pubblica
  _coverages_auto_text: "",
};

// Definizione sezioni con colori (UI card preventivo) — chiavi in ordine di visualizzazione
// Allineate al formato email/PDF inviato ai broker: header solid color + body bianco.
// 🆕 Feb 2026 — Refactor: 3 macro-sezioni (Dati personali & Conducente, Veicolo, Dati preventivo)
//    con dedup dei campi doppi (fuel/vehicle_fuel, kw/vehicle_kw, ecc.) — vedi HIDDEN_KEYS.
const DETAIL_SECTIONS = [
  { title: "Dati personali e conducente", headerBg: "bg-blue-800", border: "border-blue-200",
    keys: [
      // Anagrafica intestatario
      "owner_name", "owner_surname", "owner_fiscal_code",
      "owner_birth_date", "owner_birth_place",
      "owner_gender", "owner_marital_status", "profession",
      // Residenza / contatti
      "owner_address", "owner_house_number", "owner_cap", "owner_city", "owner_province",
      "owner_email", "owner_phone",
      // Dati conducente
      "driving_type", "education", "license_first_issue",
      "driving_license_issued_at", "driving_license_expiry",
      "youngest_driver_age", "birth_date",
      // Uso e custodia (accorpati con conducente/veicolo)
      "annual_km", "use", "parking", "anti_theft",
    ] },
  { title: "Veicolo", headerBg: "bg-green-600", border: "border-green-200",
    keys: [
      "vehicle_plate", "vehicle_brand", "vehicle_model", "vehicle_version",
      "vehicle_year", "vehicle_first_registration", "vehicle_purchase_date",
      "fuel", "vehicle_additional_fuel", "displacement", "kw", "cv",
      "seats", "max_mass", "co2_emissions", "euro_class",
      "vehicle_use", "vehicle_category", "chassis_number",
      "last_review_date", "next_review_date",
      "ownership_date", "previous_owners",
      "bollo_due_date", "bollo_amount",
    ] },
  { title: "Dati preventivo", headerBg: "bg-violet-600", border: "border-violet-200",
    keys: [
      "policy_number", "company", "insurance_status", "frazionamento",
      "start_date", "end_date",
      "requested_coverages",
    ] },
  { title: "Storico assicurativo (polizza attuale)", headerBg: "bg-purple-600", border: "border-purple-200",
    keys: ["merit_class", "years_insured", "claims_last_5y", "current_company", "current_policy_expiry"] },
  { title: "Bersani / RC Familiare", headerBg: "bg-rose-600", border: "border-rose-200",
    keys: [
      // Vecchi campi (legacy public quote)
      "bersani_applicable", "bersani_type", "bersani_relative_kinship",
      "bersani_relative_class", "bersani_relative_plate", "bersani_relative_company",
      // 🆕 Feb 2026 — Nuovi campi (wizard/preventivo detail)
      "bersani_ref_plate", "bersani_ref_company", "bersani_ref_merit_class",
      "bersani_ref_owner",
      "bersani_ref_owner_name", "bersani_ref_owner_surname",
      "bersani_ref_owner_fiscal_code",
      "bersani_ref_owner_birth_date", "bersani_ref_owner_birth_place",
      "bersani_ref_owner_gender",
    ] },
  { title: "Altri preventivi confrontati", headerBg: "bg-fuchsia-600", border: "border-fuchsia-200",
    keys: ["other_quotes_done", "other_quotes_amount", "other_quotes_frazionamento"] },
  { title: "Immobile (Casa)", headerBg: "bg-cyan-600", border: "border-cyan-200",
    keys: ["property_type", "property_address", "property_sqm", "property_year", "property_value", "coverage_options"] },
  { title: "Vita", headerBg: "bg-violet-600", border: "border-violet-200",
    keys: ["insured_capital", "duration_years", "beneficiaries", "smoker"] },
  { title: "Salute", headerBg: "bg-red-600", border: "border-red-200",
    keys: ["family_members", "preexisting_conditions"] },
  { title: "Infortuni", headerBg: "bg-orange-600", border: "border-orange-200",
    keys: ["activity", "invalidity_capital", "death_capital"] },
  { title: "Viaggio", headerBg: "bg-sky-600", border: "border-sky-200",
    keys: ["destination", "departure_date", "return_date", "travelers_count"] },
  { title: "Responsabilità civile", headerBg: "bg-slate-600", border: "border-slate-200",
    keys: ["rc_scope", "rc_max_coverage"] },
  { title: "Note", headerBg: "bg-amber-500", border: "border-amber-200",
    keys: ["notes"] },
];

const VAL_LABELS = {
  benzina: "Benzina", diesel: "Diesel", gpl: "GPL", metano: "Metano",
  elettrico: "Elettrico", elettrica: "Elettrica", ibrido: "Ibrido",
  ibrida: "Ibrida", ibrida_plug_in: "Ibrida plug-in",
  privato: "Privato", professionale: "Professionale", autonoleggio: "Autonoleggio",
  uso_proprio: "Uso proprio", conto_terzi: "Conto terzi",
  box: "Box / Garage", posto_auto_coperto: "Posto auto coperto",
  strada: "Strada / All'aperto", cortile: "Cortile privato",
  nessuno: "Nessuno", immobilizer: "Immobilizer di serie",
  allarme: "Allarme", satellitare: "Satellitare",
  esclusiva: "Esclusiva", esperta: "Esperta", libera: "Libera",
  licenza_media: "Licenza media", diploma: "Diploma",
  laurea_breve: "Laurea triennale", laurea_magistrale: "Laurea magistrale",
  master_dottorato: "Master / Dottorato",
  bersani: "Bersani (nuova immatricolazione)",
  rc_familiare: "RC Familiare",
  legge_bersani: "Legge Bersani",
  // 🆕 Feb 2026 — Situazione assicurativa
  already_insured: "Già assicurato",
  new_vehicle: "Nuovo veicolo da assicurare",
  // 🆕 Feb 2026 — Tipo soggetto
  private: "Privato",
  company: "Azienda",
  // 🆕 Sesso
  male: "Maschio",
  female: "Femmina",
  maschio: "Maschio",
  femmina: "Femmina",
  m: "Maschio",
  f: "Femmina",
  coniuge: "Coniuge / Convivente", genitore: "Genitore", figlio: "Figlio/a",
  fratello: "Fratello/Sorella", nonno: "Nonno/a", nipote: "Nipote",
  suocero: "Suocero/a", cognato: "Cognato/a",
  annuale: "Annuale", semestrale: "Semestrale",
  quadrimestrale: "Quadrimestrale", trimestrale: "Trimestrale", mensile: "Mensile",
  appartamento: "Appartamento", villa: "Villa/Casa indipendente",
  bifamiliare: "Bi/Trifamiliare", commerciale: "Locale commerciale",
  capofamiglia: "Capofamiglia", animali: "Animali domestici", altro: "Altro",
};

const QUOTE_DATE_KEYS = new Set([
  "owner_birth_date", "birth_date",
  "vehicle_first_registration", "next_review_date", "last_review_date",
  "ownership_date", "license_first_issue",
  "current_policy_expiry", "departure_date", "return_date",
  // 🆕 Feb 2026 — chiavi date aggiuntive nella sezione "Altri dati"
  "start_date", "end_date", "purchase_date", "vehicle_purchase_date",
  "bollo_due_date", "driving_license_issued_at", "driving_license_expiry",
]);

// 🆕 Regex per rilevare stringhe in formato ISO YYYY-MM-DD ovunque (anche dentro campi non elencati)
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]|$)/;

const formatItDate = (val) => {
  const s = String(val ?? "").trim();
  if (!s) return "—";
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  return s;
};

const formatDetailValue = (k, v) => {
  // 🆕 Feb 2026 — Bug fix "Altri dati": serializzazione robusta di oggetti/array di oggetti.
  //    Prima gli array/oggetti finivano come "[object Object]" perché passavano da String(x).
  const _fmtScalar = (x) => {
    if (x == null || x === "") return "";
    if (typeof x === "boolean") return x ? "Sì" : "No";
    const s = String(x).trim();
    if (ISO_DATE_RE.test(s)) return formatItDate(s);
    return VAL_LABELS[s.toLowerCase()] || (s.match(/^[a-z_]+$/) ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : s);
  };
  const _fmtItem = (x) => {
    if (x == null) return "";
    if (Array.isArray(x)) return x.map(_fmtItem).filter(Boolean).join(", ");
    if (typeof x === "object") {
      // Pattern estrazione libretto: [{key, value}, ...] → "key: value"
      if ("key" in x && "value" in x) {
        const kk = _fmtScalar(x.key);
        const vv = _fmtItem(x.value);
        return kk && vv ? `${kk}: ${vv}` : (vv || kk || "");
      }
      if ("label" in x && "value" in x) {
        const kk = _fmtScalar(x.label);
        const vv = _fmtItem(x.value);
        return kk && vv ? `${kk}: ${vv}` : (vv || kk || "");
      }
      // Generic object → "k1: v1 · k2: v2"
      return Object.entries(x)
        .filter(([, vv]) => vv !== null && vv !== "" && !(Array.isArray(vv) && vv.length === 0))
        .map(([kk, vv]) => `${_fmtScalar(kk)}: ${_fmtItem(vv)}`)
        .filter(Boolean).join(" · ");
    }
    return _fmtScalar(x);
  };
  if (Array.isArray(v)) return v.map(_fmtItem).filter(Boolean).join(", ");
  if (v && typeof v === "object") return _fmtItem(v);
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (k === "other_quotes_done") return v ? "Sì" : "No";
  if (QUOTE_DATE_KEYS.has(k)) return formatItDate(v);
  const s = String(v).trim();
  // 🆕 Feb 2026 — Auto-detect ISO date (YYYY-MM-DD) su qualsiasi campo → DD/MM/YYYY
  if (ISO_DATE_RE.test(s)) return formatItDate(s);
  return VAL_LABELS[s.toLowerCase()] || (s.match(/^[a-z_]+$/) ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : s);
};

const meaningfulDetails = (details) =>
  Object.entries(details || {}).filter(
    ([, v]) => v !== "" && v != null && !(Array.isArray(v) && v.length === 0)
  );

export default function Quotes() {
  const { canWrite } = usePermissions();
  const navigate = useNavigate();
  useFirstTimeHint("crm_dblclick_hint_v1", "💡 Suggerimento: doppio click sul nome del cliente per aprire la scheda in modifica.");
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const { options: DYN_TYPES, labelByCode: DYN_TYPE_LABEL, types: DYN_TYPES_RAW } = usePolicyTypes();
  // Usa la lista dinamica se disponibile, altrimenti il fallback statico module-level.
  const TYPES_LIVE = DYN_TYPES.length ? DYN_TYPES : TYPES;
  const TYPE_LABEL_LIVE = Object.keys(DYN_TYPE_LABEL).length ? DYN_TYPE_LABEL : TYPE_LABEL;
  const [items, setItems] = useState([]);
  // 🆕 Feb 2026 — hook riutilizzabile per anteprima inline PDF/immagini (usa react-pdf)
  const { openPreview: openAttachmentPreview, PreviewDialog: AttachmentPreviewDialog } = useAttachmentPreview();
  const [quoteOffersMap, setQuoteOffersMap] = useState({}); // 🆕 quote_id -> [offers]
  // 🆕 Feb 2026 — Overlay notifica nuove offerte da partner online + beep sonoro
  const [newPartnerOffersAlert, setNewPartnerOffersAlert] = useState([]); // array di { offer, quote } nuove da notificare
  const seenPartnerOfferIdsRef = useRef(null); // Set<string> delle offerte partner già viste al primo load (baseline)
  const audioCtxRef = useRef(null);
  const beepIntervalRef = useRef(null);
  // 🆕 Feb 2026 — Dialog dettaglio offerta ricevuta da partner online + marca "visualizzata"
  const [partnerOfferDetail, setPartnerOfferDetail] = useState(null); // offer object o null
  const openPartnerOfferDetail = async (offer) => {
    setPartnerOfferDetail(offer);
    // Se non è ancora stata visualizzata dall'agenzia, marcala come vista
    if (offer && !offer.viewed_by_agency) {
      try {
        await api.post(`/quote-offers/${offer.id}/mark-viewed`);
        // Aggiorna localmente il flag per far scomparire il "nuova" e decrementare il counter
        setQuoteOffersMap((m) => {
          const next = { ...m };
          const qid = offer.quote_id;
          if (next[qid]) {
            next[qid] = next[qid].map((o) => o.id === offer.id ? { ...o, viewed_by_agency: true } : o);
          }
          return next;
        });
      } catch { /* non bloccante */ }
    }
  };
  // 🆕 Dialog "Forza pagamento" su offerte collegate al preventivo
  const [forcePaidOffer, setForcePaidOffer] = useState(null);
  const [forcePaidMethod, setForcePaidMethod] = useState("bonifico");
  const [forcePaidBusy, setForcePaidBusy] = useState(false);
  const [trashView, setTrashView] = useState(false);
  const [trashCount, setTrashCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // OTP bulk delete
  const [bulkStage, setBulkStage] = useState(null); // null | "confirm" | "otp"
  const [bulkOtpEmail, setBulkOtpEmail] = useState("");
  const [bulkOtpCode, setBulkOtpCode] = useState("");
  const [bulkOtpInfo, setBulkOtpInfo] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Allegati pendenti + estrazione AI libretto
  const [pendingFiles, setPendingFiles] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  // (riepilogo AI rimosso su richiesta utente)
  const [expanded, setExpanded] = useState(null);
  // Collassamento card preventivi (sandwich): per default tutte collassate.
  const [collapsedIds, setCollapsedIds] = useState(new Set());
  const toggleCollapsed = (id) => setCollapsedIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const collapseAll = () => setCollapsedIds(new Set(items.map((q) => q.id)));
  const expandAll = () => setCollapsedIds(new Set());
  const allCollapsed = items.length > 0 && items.every((q) => collapsedIds.has(q.id));
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [companies, setCompanies] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [sendOpen, setSendOpen] = useState(null);
  // 🆕 Feb 2026 — Preferiti utente nel modale "Invia richiesta preventivo"
  // (broker/compagnie appuntati in cima alla lista). Persistiti su `/api/me/quote-pins`.
  const [quotePins, setQuotePins] = useState({ brokers: [], companies: [] });
  const quotePinsLoadedRef = useRef(false);
  const loadQuotePins = useCallback(async () => {
    try {
      const { data } = await api.get("/me/quote-pins");
      setQuotePins({
        brokers: Array.isArray(data?.brokers) ? data.brokers : [],
        companies: Array.isArray(data?.companies) ? data.companies : [],
      });
      quotePinsLoadedRef.current = true;
    } catch { /* silent: feature non critica */ }
  }, []);
  const toggleQuotePin = useCallback(async (kind, id) => {
    const key = kind === "broker" ? "brokers" : "companies";
    const curr = quotePins[key] || [];
    const isPinned = curr.includes(id);
    // Optimistic update
    setQuotePins((p) => ({
      ...p,
      [key]: isPinned ? p[key].filter((x) => x !== id) : [...(p[key] || []), id],
    }));
    try {
      const { data } = await api.put("/me/quote-pins", { type: kind, id, pinned: !isPinned });
      if (data && Array.isArray(data.brokers) && Array.isArray(data.companies)) {
        setQuotePins({ brokers: data.brokers, companies: data.companies });
      }
    } catch {
      // Rollback in caso di errore
      setQuotePins((p) => ({
        ...p,
        [key]: isPinned ? [...(p[key] || []), id] : p[key].filter((x) => x !== id),
      }));
    }
  }, [quotePins]);
  // Ordina una lista {id, name}: appuntati in cima (alfabetici) + resto alfabetico;
  // ritorna oggetti con flag `_pinned` e separatori impliciti via indice.
  const sortWithPins = useCallback((items, pinnedIds) => {
    const pinnedSet = new Set(pinnedIds || []);
    const pinned = items.filter((it) => pinnedSet.has(it.id)).sort((a, b) => (a.name || "").localeCompare(b.name || "", "it"));
    const rest = items.filter((it) => !pinnedSet.has(it.id)).sort((a, b) => (a.name || "").localeCompare(b.name || "", "it"));
    return { pinned, rest };
  }, []);
  // 🆕 Feb 2026 — Lock sincrono per prevenire invii duplicati a partner
  // (double-click sul pulsante "Invia a X partner" o sul "Invia ora" del preview).
  // useRef è sincrono e non aspetta il re-render, quindi blocca anche i click ravvicinatissimi.
  const partnerSendLockRef = useRef(false);
  const [partnerSendBusy, setPartnerSendBusy] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all"); // all | crm | portal | public
  const [editing, setEditing] = useState(null);  // id del preventivo in modifica, o null
  // 🆕 Autosave dialog preventivo (30s debounced)
  const [quoteAutoSaveAt, setQuoteAutoSaveAt] = useState(null);
  const quoteFormChangedRef = useRef(false);
  const [sends, setSends] = useState({});  // mappa quote_id → array di send-log
  const [sendsSummary, setSendsSummary] = useState({});  // {quote_id: {count, last_sent_at, pending_replies, stale_replies}}
  // 🆕 Anteprima email prima dell'invio (unificata via EmailPreviewDialog)
  const [emailPreviewCtx, setEmailPreviewCtx] = useState(null);
  // 🆕 Conteggio email Inbox collegate per preventivo (badge "📩 N nuove" in card lista)
  const [inboxCounts, setInboxCounts] = useState({});
  const [chatTemplates, setChatTemplates] = useState([]);
  const [templateUsageStats, setTemplateUsageStats] = useState({ by_template: {}, top_by_type: {} });
  // Espansione card preventivo: vedi `collapsedIds` sopra (sandwich collapse).
  // === Predittore template suggerito ===
  // Sulla base dello stato del preventivo, ritorna l'indice del template più adatto.
  // Match approssimativo sul titolo (insensibile a maiuscole) per non hard-codare gli ID.
  const suggestTemplate = (quote) => {
    if (!chatTemplates || chatTemplates.length === 0) return null;
    const summary = sendsSummary[quote?.id] || {};
    const created = quote?.created_at ? new Date(quote.created_at) : null;
    const ageDays = created ? Math.floor((Date.now() - created.getTime()) / 86400000) : null;
    const findByKw = (keyword) => chatTemplates.find((t) => (t.title || "").toLowerCase().includes(keyword.toLowerCase()));

    if ((summary.stale_replies || 0) > 0) {
      const tpl = findByKw("sollecit");
      if (tpl) return { tpl, reason: "Partner non risponde da oltre 5 giorni" };
    }
    if ((summary.count || 0) === 0 && (ageDays == null || ageDays <= 1)) {
      const tpl = findByKw("contattato");
      if (tpl) return { tpl, reason: "Nuova richiesta, da prendere in carico" };
    }
    if ((summary.count || 0) > 0 && (summary.pending_replies || 0) === 0) {
      const tpl = findByKw("documenti");
      if (tpl) return { tpl, reason: "Partner ha risposto — chiedi documenti al cliente" };
    }
    if ((quote?.status || "").toLowerCase().includes("pagata") || (quote?.status || "").toLowerCase().includes("polizza")) {
      const tpl = findByKw("emessa");
      if (tpl) return { tpl, reason: "Polizza emessa" };
    }
    // Fallback: AI adattiva → template più usato storicamente per questo tipo polizza
    const topForType = templateUsageStats?.top_by_type?.[quote?.type];
    if (topForType && topForType.count >= 3) {
      const tpl = chatTemplates.find((t) => t.id === topForType.template_id);
      if (tpl) return { tpl, reason: `Più usato per pratiche ${quote.type} (${topForType.count} volte)` };
    }
    return null;
  };

  // Logga l'utilizzo di un template per costruire insights e adattare i futuri suggerimenti
  const logTemplateUsage = (templateId, quote, suggestedId) => {
    api.post("/chat-templates/log-usage", {
      template_id: templateId,
      quote_id: quote?.id,
      quote_type: quote?.type,
      suggested_template_id: suggestedId || null,
    }).catch(() => { /* best effort */ });
  };

  // Wrapper unico per applicare template + log
  const applyTemplateAndLog = (template, quote, suggestedId) => {
    setReply(applyTemplateVars(template.body, quote));
    logTemplateUsage(template.id, quote, suggestedId);
  };

  // Sostituisce le variabili {nome_cliente}, {numero_preventivo}, ecc. nel template
  // usando i dati del preventivo corrente. Le variabili non risolte rimangono come placeholder.
  const TYPE_LABEL_IT = {
    auto: "auto", moto: "moto", casa: "casa", vita: "vita", salute: "salute",
    infortuni: "infortuni", viaggio: "viaggio", responsabilita_civile: "responsabilità civile",
  };
  const applyTemplateVars = (body, quote) => {
    if (!body) return "";
    const client = clients.find((c) => c.id === quote?.client_id);
    const today = new Date().toLocaleDateString("it-IT");
    const vars = {
      "{nome_cliente}": (client?.name || quote?.client_name?.split(" ")[0] || "").trim() || "Cliente",
      "{cognome_cliente}": (client?.surname || "").trim(),
      "{nome_completo_cliente}": client ? `${client.name || ""} ${client.surname || ""}`.trim() : (quote?.client_name || "Cliente"),
      "{numero_preventivo}": quote?.quote_number || "—",
      "{tipo_polizza}": TYPE_LABEL_IT[quote?.type] || quote?.type || "",
      "{data_oggi}": today,
      "{email_cliente}": client?.email || "",
    };
    let out = body;
    Object.entries(vars).forEach(([k, v]) => {
      out = out.split(k).join(v);
    });
    return out;
  };
  const [pendingExisting, setPendingExisting] = useState([]);  // allegati già caricati durante editing
  const [extractionDone, setExtractionDone] = useState(false);  // true dopo la prima estrazione AI in questo dialog
  const [extractionError, setExtractionError] = useState(null);  // {filename, reason} se l'estrazione è fallita o ha estratto 0 campi
  // 🆕 Dialog scelta intestatario quando il libretto contiene cointestatari
  const [ownerChoice, setOwnerChoice] = useState(null); // { owners: [{label, data:{owner_*..}}], filename, restExtracted }
  // 🆕 Pannello "sandwich" cointestatari nel form preventivo (default: collassato)
  const [coOwnersCollapsed, setCoOwnersCollapsed] = useState(true);
  // 🆕 Pannello "sandwich" Dati personali intestatario (default: espanso quando crei, collassato quando modifichi)
  const [personalCollapsed, setPersonalCollapsed] = useState(false);
  const [clientSummary, setClientSummary] = useState(null);  // mini-riepilogo cliente associato
  // 🆕 Controllo duplicati per targa veicolo
  const [dupCheckOpen, setDupCheckOpen] = useState(false);
  const [dupCheckResult, setDupCheckResult] = useState(null); // { plate, items: [...] }
  const [dupCheckBypassed, setDupCheckBypassed] = useState(false);
  const [dupCheckContext, setDupCheckContext] = useState("save"); // "save" | "extract"
  // Invio allegati preventivo via email
  const [qattSelected, setQattSelected] = useState({});
  const [qattSendOpen, setQattSendOpen] = useState(false);
  const [coveragesByCode, setCoveragesByCode] = useState({});  // code → label per chip pills
  const [qattSendForm, setQattSendForm] = useState({ to_email: "", subject: "", body: "" });
  const [qattSendBusy, setQattSendBusy] = useState(false);
  const [qattSendLogs, setQattSendLogs] = useState([]);
  // Statistiche apertura link PDF condiviso
  const [shareStats, setShareStats] = useState(null);  // { quote, summary, accesses } o null

  // Filtro veloce per stato (tabs/chip)
  const [statusFilter, setStatusFilter] = useState("all");
  // 🆕 Ricerca testuale su preventivi (numero, cliente, targa, marca/modello, email)
  const [search, setSearch] = useState("");

  const filteredItemsRaw = items.filter((q) => {
    if (sourceFilter === "public" && q.from_public !== true) return false;
    if (sourceFilter === "portal" && (q.from_portal !== true || q.from_public === true)) return false;
    if (sourceFilter === "crm" && (q.from_portal || q.from_public)) return false;
    // 🆕 Click-to-filter dalla Dashboard: "open" = preventivi ancora da lavorare (nuovo + in_lavorazione).
    if (statusFilter === "open") {
      if (!["nuovo", "in_lavorazione"].includes(q.status)) return false;
    } else if (statusFilter !== "all" && q.status !== statusFilter) return false;
    // 🆕 Filtro ricerca testuale
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      const client = clients.find((c) => c.id === q.client_id);
      const d = q.details || {};
      const haystack = [
        q.quote_number, q.type, q.status,
        client ? `${client.surname} ${client.name}` : "",
        d.vehicle_plate, d.vehicle_brand, d.vehicle_model,
        d.owner_first_name, d.owner_last_name, d.owner_email, d.owner_fiscal_code,
        d.driver_first_name, d.driver_last_name, d.driver_fiscal_code,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!haystack.includes(s)) return false;
    }
    return true;
  });
  // === Raggruppamento per targa: il preventivo più vecchio in testa al gruppo ===
  // Quando più preventivi hanno la stessa targa, li mostriamo consecutivi
  // ordinati dal più vecchio al più recente (utile per sequenze di rinnovo/storico).
  const _plateOf = (q) => {
    const p = (q?.details && (q.details.vehicle_plate || q.details.targa)) || q?.vehicle_plate || "";
    return String(p || "").toUpperCase().replace(/\s+/g, "").trim();
  };
  const _ts = (q) => {
    try { return new Date(q.created_at).getTime() || 0; } catch { return 0; }
  };
  // Ordine logico per stato (azione richiesta in cima, chiusi in fondo)
  const STATUS_ORDER = { nuovo: 0, in_lavorazione: 1, accettato: 2, rifiutato: 3 };
  const _stOrder = (q) => STATUS_ORDER[q?.status] ?? 99;
  const filteredItems = (() => {
    const groups = new Map(); // key -> array
    const order = []; // ordered keys
    filteredItemsRaw.forEach((q) => {
      const plate = _plateOf(q);
      // Quotes senza targa restano individuali (chiave univoca per id)
      const key = plate || `__solo__:${q.id}`;
      if (!groups.has(key)) { groups.set(key, []); order.push(key); }
      groups.get(key).push(q);
    });
    // Per ogni gruppo, ordina dal più vecchio al più recente
    groups.forEach((arr) => arr.sort((a, b) => _ts(a) - _ts(b)));
    // Ordina i gruppi: primo criterio lo STATO migliore del gruppo (es. nuovo > accettato),
    // secondo criterio la data del preventivo "rappresentante" del gruppo.
    order.sort((a, b) => {
      const arrA = groups.get(a);
      const arrB = groups.get(b);
      const minStA = Math.min(...arrA.map(_stOrder));
      const minStB = Math.min(...arrB.map(_stOrder));
      if (minStA !== minStB) return minStA - minStB;
      const lastA = arrA[arrA.length - 1];
      const lastB = arrB[arrB.length - 1];
      const ka = (arrA.length > 1) ? _ts(lastA) : _ts(arrA[0]);
      const kb = (arrB.length > 1) ? _ts(lastB) : _ts(arrB[0]);
      return kb - ka;
    });
    const flat = [];
    order.forEach((k) => {
      const arr = groups.get(k);
      arr.forEach((q, idx) => {
        flat.push({
          ...q,
          _plateGroupKey: k.startsWith("__solo__:") ? null : k,
          _plateGroupSize: arr.length,
          _plateGroupIndex: idx,
          _plateGroupIsFirst: idx === 0 && arr.length > 1,
        });
      });
    });
    return flat;
  })();
  const statusCounts = STATUSES.reduce((acc, s) => {
    acc[s] = items.filter((q) => q.status === s).length;
    return acc;
  }, { all: items.length });

  const openMessages = async (q) => {
    if (expanded === q.id) { setExpanded(null); return; }
    setExpanded(q.id);
    try {
      const { data } = await api.get(`/quotes/${q.id}/messages`);
      setMessages(data);
    } catch { /* silent: alcuni preventivi (es. da richiesta pubblica) potrebbero non avere messaggi */ }
    loadSends(q.id);
  };

  // 🆕 Dialog dedicato: storico invii al partner per un preventivo
  const [sendsHistoryOpen, setSendsHistoryOpen] = useState(null); // { quote, sends, loading }
  const [resendConfirm, setResendConfirm] = useState(null); // { qid, sendId, partnerName }

  const openSendsHistory = async (q) => {
    setSendsHistoryOpen({ quote: q, sends: [], loading: true });
    try {
      const { data } = await api.get(`/quotes/${q.id}/sends`);
      setSendsHistoryOpen({ quote: q, sends: Array.isArray(data) ? data : [], loading: false });
    } catch (e) {
      toast.error(formatApiError(e));
      setSendsHistoryOpen({ quote: q, sends: [], loading: false });
    }
  };

  const confirmResendSend = async () => {
    if (!resendConfirm) return;
    const { qid, sendId, partnerName } = resendConfirm;
    try {
      await api.post(`/quotes/${qid}/sends/${sendId}/resend`);
      toast.success(`📨 Reinviato a ${partnerName}`);
      // Refresh dialog storico se aperto
      if (sendsHistoryOpen?.quote?.id === qid) {
        const { data } = await api.get(`/quotes/${qid}/sends`);
        setSendsHistoryOpen((prev) => prev ? { ...prev, sends: Array.isArray(data) ? data : [] } : null);
      }
      loadSends(qid);
      const sum = await api.get("/quotes/sends-summary");
      setSendsSummary(sum.data || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResendConfirm(null); }
  };

  const sendReply = async (qid) => {
    if (!reply.trim()) return;
    try {
      const { data } = await api.post(`/quotes/${qid}/messages`, { body: reply });
      setMessages((prev) => [...prev, data]);
      setReply("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = useCallback(async () => {
    try {
      const [q, c, co, b, sum, tpl, usage, stats, offers, inbCnt] = await Promise.all([
        api.get(`/quotes${trashView ? "?trash=true" : ""}`),
        api.get("/clients"),
        api.get("/companies"),
        api.get("/brokers"),
        api.get("/quotes/sends-summary").catch(() => ({ data: {} })),
        api.get("/chat-templates").catch(() => ({ data: [] })),
        api.get("/chat-templates/usage-stats").catch(() => ({ data: { by_template: {}, top_by_type: {} } })),
        api.get("/quotes/stats").catch(() => ({ data: {} })),
        api.get("/quote-offers").catch(() => ({ data: [] })),
        api.get("/quotes/inbox-counts").catch(() => ({ data: {} })),
      ]);
      // 🆕 Filtra fuori i preventivi già trasformati in polizza: NON devono più
      // apparire nella sezione preventivi (sono visibili in /polizze).
      const visible = (q.data || []).filter((x) => !x.converted_policy_id);
      setItems(visible);
      // Default: tutte le card collassate per ridurre lo spazio verticale
      setCollapsedIds(new Set(visible.map((x) => x.id)));
      setClients(c.data);
      setCompanies(co.data);
      setBrokers(b.data);
      setSendsSummary(sum.data || {});
      setInboxCounts(inbCnt.data || {});
      setChatTemplates(tpl.data || []);
      setTemplateUsageStats(usage.data || { by_template: {}, top_by_type: {} });
      setTrashCount(Number(stats.data?.trash_count || 0));
      // 🆕 Mappa offerte per preventivo (esclude eliminate). Permette di mostrare
      // "Apri offerta" direttamente nell'elenco preventivi (linked al più recente).
      const offMap = {};
      for (const o of (offers.data || [])) {
        if (!o.quote_id || o.status === "eliminata") continue;
        if (!offMap[o.quote_id]) offMap[o.quote_id] = [];
        offMap[o.quote_id].push(o);
      }
      setQuoteOffersMap(offMap);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [trashView]);
  useEffect(() => { load(); }, [load]);

  // 🆕 Feb 2026 — Polling ogni 30s per rilevare nuove offerte da partner online
  useEffect(() => {
    const t = setInterval(() => { load(); }, 30000);
    return () => clearInterval(t);
  }, [load]);

  // 🆕 Feb 2026 — Diff del quoteOffersMap: identifica nuove offerte partner (submitted_by_partner, non ancora viewed)
  // e trigga l'overlay di notifica + beep sonoro.
  useEffect(() => {
    if (!quoteOffersMap || Object.keys(quoteOffersMap).length === 0) return;
    // Raccoglie tutte le offerte partner non ancora visualizzate
    const partnerOffersUnread = [];
    Object.values(quoteOffersMap).forEach((offers) => {
      (offers || []).forEach((o) => {
        if (o.submitted_by_partner && !o.viewed_by_agency) partnerOffersUnread.push(o);
      });
    });
    // Al primo load stabilisce la baseline (le offerte già pendenti NON triggano l'alert)
    if (seenPartnerOfferIdsRef.current === null) {
      seenPartnerOfferIdsRef.current = new Set(partnerOffersUnread.map((o) => o.id));
      return;
    }
    // Trova le offerte veramente NUOVE (non presenti nella baseline/seen)
    const seen = seenPartnerOfferIdsRef.current;
    const brand = partnerOffersUnread.filter((o) => !seen.has(o.id));
    if (brand.length > 0) {
      // Marca come "seen" per non ritriggerare al prossimo poll
      brand.forEach((o) => seen.add(o.id));
      // Correla ogni offerta al preventivo per ottenere il nome cliente
      const enriched = brand.map((o) => {
        const q = items.find((x) => x.id === o.quote_id);
        return { offer: o, quote: q || null };
      });
      setNewPartnerOffersAlert((prev) => [...prev, ...enriched]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteOffersMap]);

  // 🆕 Beep sonoro ripetuto finché l'overlay è aperto (Web Audio API, no asset esterni)
  useEffect(() => {
    if (newPartnerOffersAlert.length === 0) {
      if (beepIntervalRef.current) { clearInterval(beepIntervalRef.current); beepIntervalRef.current = null; }
      return;
    }
    const playBeep = () => {
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = audioCtxRef.current;
        if (ctx.state === "suspended") ctx.resume();
        // Suono "ding-dong" a 2 toni
        [{ f: 880, t: 0 }, { f: 660, t: 0.18 }].forEach(({ f, t }) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = f;
          gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
          gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + t + 0.03);
          gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.17);
          osc.connect(gain).connect(ctx.destination);
          osc.start(ctx.currentTime + t);
          osc.stop(ctx.currentTime + t + 0.2);
        });
      } catch { /* ignore AudioContext errors (es. browser policy) */ }
    };
    playBeep();
    beepIntervalRef.current = setInterval(playBeep, 2500);
    return () => { if (beepIntervalRef.current) { clearInterval(beepIntervalRef.current); beepIntervalRef.current = null; } };
  }, [newPartnerOffersAlert.length]);

  const dismissPartnerOffersAlert = () => setNewPartnerOffersAlert([]);

  // Auto-espande la card del preventivo passato via ?focus=<id> (dal Wizard "salva e apri").
  // - rimuove dal collapsedIds, scrolla la card in vista, applica un highlight visivo temporaneo
  // - poi ripulisce il query param per evitare riapertura ad ogni refresh
  const [searchParams, setSearchParams] = useSearchParams();
  const [flashId, setFlashId] = useState(null);

  // 🆕 Click-to-filter dalla Dashboard: ?filter=open (nuovi + in_lavorazione) o ?filter=<status>.
  // Il param viene rimosso dopo l'applicazione per non sovrascrivere le successive interazioni utente.
  useEffect(() => {
    const raw = searchParams.get("filter") || searchParams.get("status");
    if (!raw) return;
    const validFilters = ["all", "open", "nuovo", "in_lavorazione", "accettato", "rifiutato"];
    if (validFilters.includes(raw)) {
      setStatusFilter(raw);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    next.delete("status");
    setSearchParams(next, { replace: true });
  }, []);
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (!focusId || !items || items.length === 0) return;
    const q = items.find((x) => x.id === focusId);
    if (!q) return;
    setCollapsedIds((prev) => {
      const n = new Set(prev);
      n.delete(focusId);
      return n;
    });
    setFlashId(focusId);
    const t1 = setTimeout(() => {
      const el = document.getElementById(`quote-card-${focusId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    const t2 = setTimeout(() => setFlashId(null), 2500);
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    setSearchParams(next, { replace: true });
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [items, searchParams, setSearchParams]);

  // Carica labels garanzie per mappare i codici tecnici → nome leggibile nelle chip pills
  useEffect(() => {
    api.get("/policy-coverages").then(({ data }) => {
      const map = {};
      (data || []).forEach((c) => { map[c.code] = c.label; });
      setCoveragesByCode(map);
    }).catch(() => {});
  }, []);

  // 🆕 Feb 2026 — Carica lookup lists per Professione e Stato civile (usati nel dialog di modifica preventivo)
  const [professionOpts, setProfessionOpts] = useState([]);
  const [maritalOpts, setMaritalOpts] = useState([]);
  const [insuranceStatusOpts, setInsuranceStatusOpts] = useState([]);
  // 🆕 Feb 2026 — Attestato di rischio altro veicolo (campo obbligatorio su preventivi auto/moto)
  const [bersaniSourceOpts, setBersaniSourceOpts] = useState([]);
  useEffect(() => {
    api.get("/lookup-lists/profession").then(({ data }) => setProfessionOpts((data || []).filter((x) => x.is_active !== false))).catch(() => {});
    api.get("/lookup-lists/marital_status").then(({ data }) => setMaritalOpts((data || []).filter((x) => x.is_active !== false))).catch(() => {});
    api.get("/lookup-lists/insurance_status").then(({ data }) => setInsuranceStatusOpts((data || []).filter((x) => x.is_active !== false))).catch(() => {});
    api.get("/lookup-lists/bersani_source").then(({ data }) => setBersaniSourceOpts((data || []).filter((x) => x.is_active !== false))).catch(() => {});
  }, []);

  // Restore + permanent delete handlers per il cestino
  const restoreQuote = async (q) => {
    try {
      await api.post(`/quotes/${q.id}/restore`);
      toast.success("Richiesta ripristinata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const permanentDeleteQuote = async (q) => {
    if (!confirm(`Eliminare DEFINITIVAMENTE la richiesta ${q.quote_number || ""}? Non sarà più recuperabile.`)) return;
    try {
      await api.delete(`/quotes/${q.id}/permanent`);
      toast.success("Richiesta eliminata definitivamente");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---------- Bulk delete preventivi (OTP via email) ----------
  const toggleSelect = (id) => setSelectedIds((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const selectAllVisible = (rows, checked) => setSelectedIds((s) => {
    const n = new Set(s);
    rows.forEach((q) => { if (checked) n.add(q.id); else n.delete(q.id); });
    return n;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const requestBulkSoftDelete = async () => {
    if (selectedIds.size === 0) return;
    const n = selectedIds.size;
    const ok = window.confirm(
      `Vuoi spostare nel cestino ${n} ${n === 1 ? "preventivo" : "preventivi"}?\n\nPotrai ripristinarli dalla scheda "Cestino".`
    );
    if (!ok) return;
    setBulkBusy(true);
    let okCount = 0;
    let failCount = 0;
    try {
      const ids = Array.from(selectedIds);
      // Soft-delete in parallelo (DELETE /api/quotes/{id} sposta nel cestino)
      await Promise.all(ids.map(async (id) => {
        try { await api.delete(`/quotes/${id}`); okCount += 1; }
        catch { failCount += 1; }
      }));
      if (okCount) toast.success(`Spostati nel cestino ${okCount} ${okCount === 1 ? "preventivo" : "preventivi"}`);
      if (failCount) toast.error(`${failCount} eliminazioni fallite`);
      setBulkStage(null);
      clearSelection();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };
  const confirmBulkDelete = async () => {
    const code = (bulkOtpCode || "").trim();
    if (!/^\d{4,10}$/.test(code)) { toast.error("Inserisci il codice OTP"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/quotes/bulk-delete/confirm", { quote_ids: Array.from(selectedIds), otp: code });
      toast.success(`Eliminati ${data.deleted_count} preventivi`);
      setBulkStage(null);
      setBulkOtpCode("");
      clearSelection();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };

  // Flusso OTP per ELIMINAZIONE DEFINITIVA dal cestino (bulk o singolo)
  const requestPermanentDeleteOtp = async (ids) => {
    if (!ids || ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/quotes/bulk-delete/request-otp", { quote_ids: ids });
      setBulkStage("otp_permanent");
      setBulkOtpEmail(data.email || "");
      setBulkOtpInfo({ ...data, ids });
      if (data.ok) toast.success(`Codice OTP inviato a ${data.email}`);
      else toast.error("Invio OTP fallito (verifica SMTP)");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };
  const confirmPermanentDelete = async () => {
    const code = (bulkOtpCode || "").trim();
    if (!/^\d{4,10}$/.test(code)) { toast.error("Inserisci il codice OTP"); return; }
    const ids = bulkOtpInfo?.ids || Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/quotes/bulk-delete/confirm", { quote_ids: ids, otp: code });
      toast.success(`Eliminati definitivamente ${data.deleted_count} preventivi`);
      setBulkStage(null);
      setBulkOtpCode("");
      clearSelection();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };
  // Bulk handler che si adatta al contesto:
  // - vista "Attive" → soft-delete (sposta nel cestino, conferma semplice)
  // - vista "Cestino" → eliminazione definitiva con OTP
  const requestBulkAction = () => {
    if (selectedIds.size === 0) return;
    if (trashView) {
      requestPermanentDeleteOtp(Array.from(selectedIds));
    } else {
      requestBulkSoftDelete();
    }
  };
  // Wrapper anche per il singolo permanent delete dal cestino → flusso OTP
  const requestSinglePermanentOtp = (q) => {
    if (!confirm(`⚠️ ELIMINAZIONE DEFINITIVA del preventivo ${q.quote_number || q.id.slice(0,8)}.\n\nNon sarà più recuperabile. Riceverai un codice OTP via email per confermare.`)) return;
    requestPermanentDeleteOtp([q.id]);
  };

  const sendToPartner = async (q) => {
    // 🆕 Pre-carica gli allegati preventivo così l'utente vede subito quali file può inviare
    let availableAttachments = [];
    let selectedIds = [];
    try {
      const { data } = await api.get(`/quotes/${q.id}/attachments`);
      availableAttachments = Array.isArray(data) ? data : [];
      // Preselezione: tutti gli allegati per comodità
      selectedIds = availableAttachments.map((a) => a.id);
    } catch { /* silent: se non riesce, lista vuota */ }
    setSendOpen({
      quote: q,
      targets: [],  // array of { type: "broker"|"company", id: string }
      extra_notes: "",
      share_contacts: null,
      include_attachments: true,
      also_whatsapp: false,
      available_attachments: availableAttachments,
      selected_attachment_ids: selectedIds,
    });
    // 🆕 Feb 2026 — carica i broker/compagnie appuntati dell'utente
    if (!quotePinsLoadedRef.current) loadQuotePins();
  };

  const convertToPolicy = async (q) => {
    if (!window.confirm(`Trasformare il preventivo ${q.quote_number || q.id.slice(0, 8)} in polizza?\n\nVerranno copiati tutti i dati e gli allegati nella nuova polizza. Il preventivo verrà rimosso dalla sezione preventivi.`)) return;
    try {
      const { data } = await api.post(`/quotes/${q.id}/convert-to-policy`);
      toast.success(`Polizza creata · ${data.attachments_moved || 0} allegati spostati. Apertura scheda polizza…`);
      // 🆕 Rimuovi dall'elenco e poi naviga alla polizza appena creata (apre la card automaticamente)
      setItems((arr) => arr.filter((x) => x.id !== q.id));
      setTimeout(() => navigate(`/policies?focus=${data.policy_id}`), 100);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [acceptLinkDlg, setAcceptLinkDlg] = useState(null);
  const sendAcceptLink = async (q) => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/generate-accept-link`);
      setAcceptLinkDlg({ ...data, quote: q });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const copyAcceptLink = () => {
    if (!acceptLinkDlg?.url) return;
    navigator.clipboard.writeText(acceptLinkDlg.url);
    toast.success("Link copiato negli appunti");
  };

  const downloadQuoteRequestPdf = async (q) => {
    try {
      const res = await api.get(`/quotes/${q.id}/request-pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `richiesta_preventivo_${q.quote_number || q.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch (e) {
      toast.error(formatApiError(e) || "Errore scaricamento PDF");
    }
  };

  const toggleSendTarget = (type, id) => {
    setSendOpen((s) => {
      if (!s) return s;
      const idx = s.targets.findIndex((t) => t.type === type && t.id === id);
      const next = idx >= 0 ? s.targets.filter((_, i) => i !== idx) : [...s.targets, { type, id }];
      return { ...s, targets: next };
    });
  };

  const submitSendToPartner = async () => {
    if (!sendOpen?.targets?.length) { toast.error("Seleziona almeno un destinatario"); return; }
    if (sendOpen.quote?.type === "auto" && !(sendOpen.quote?.details?.driving_type || sendOpen.quote?.driving_type)) {
      toast.error("Invio bloccato: il campo \"Tipo di guida\" è obbligatorio per i preventivi auto. Modifica il preventivo e compilalo.");
      return;
    }
    // 🆕 Feb 2026 — Guardia sincrona anti-doppio invio: se un invio è già in corso, ignora.
    if (partnerSendLockRef.current) return;
    // 🆕 Controllo preventivo: identifica partner selezionati senza email/PEC
    const targetsWithoutEmail = sendOpen.targets.map((t) => {
      const list = t.type === "broker" ? brokers : companies;
      const p = list.find((x) => x.id === t.id);
      const hasEmail = !!(p?.email || p?.pec || p?.email_pec);
      return { ...t, partner: p, hasEmail };
    }).filter((t) => !t.hasEmail);
    if (targetsWithoutEmail.length > 0) {
      const names = targetsWithoutEmail.map((t) => t.partner?.name || "Sconosciuto").join(", ");
      const proceed = window.confirm(
        `⚠ ${targetsWithoutEmail.length} partner selezionat${targetsWithoutEmail.length === 1 ? "o non ha" : "i non hanno"} email/PEC:\n\n${names}\n\nGli invii a questi partner falliranno.\n\n• OK = procedi comunque (invia solo agli altri)\n• Annulla = torna alla selezione`
      );
      if (!proceed) return;
    }
    // 🆕 Mostra anteprima email prima dell'invio (usa il primo destinatario come rappresentativo).
    if (!shouldSkipEmailPreview("quote_to_partner")) {
      const first = sendOpen.targets[0];
      const buildPayload = (target) => ({
        target_type: target.type,
        target_id: target.id,
        quote_id: sendOpen.quote.id,
        extra_notes: sendOpen.extra_notes || null,
        share_contacts: sendOpen.share_contacts,
        set_partner_default: false,
        include_attachments: !!sendOpen.include_attachments && (sendOpen.selected_attachment_ids || []).length > 0,
        attachment_ids: sendOpen.include_attachments ? (sendOpen.selected_attachment_ids || []) : [],
        urgent: !!sendOpen.urgent,
      });
      setEmailPreviewCtx({
        typeKey: "quote_to_partner",
        title: `Anteprima richiesta preventivo a partner${sendOpen.targets.length > 1 ? ` (1° di ${sendOpen.targets.length})` : ""}`,
        fetchPreview: async () => {
          const { data } = await api.post("/partners/send-quote?dry_run=true", buildPayload(first));
          if (sendOpen.targets.length > 1) {
            const others = sendOpen.targets.slice(1).map((t) => {
              const list = t.type === "broker" ? brokers : companies;
              const p = list.find((x) => x.id === t.id);
              return p?.email || p?.pec || p?.email_pec || "(nessuna email)";
            });
            data.to = [...(data.to || []), ...others];
            data.meta = { ...(data.meta || {}), total_partners: sendOpen.targets.length };
          }
          return data;
        },
        onConfirm: () => doPartnerSend(),
      });
      return;
    }
    await doPartnerSend();
  };

  const doPartnerSend = async () => {
    // 🆕 Feb 2026 — Lock sincrono anti-doppio invio: se una chiamata è già in corso, esci subito.
    if (partnerSendLockRef.current) return;
    partnerSendLockRef.current = true;
    setPartnerSendBusy(true);
    try {
    const results = await Promise.allSettled(
      sendOpen.targets.map((t) => api.post("/partners/send-quote", {
        target_type: t.type,
        target_id: t.id,
        quote_id: sendOpen.quote.id,
        extra_notes: sendOpen.extra_notes || null,
        share_contacts: sendOpen.share_contacts,
        set_partner_default: false,
        // 🆕 Feb 2026 — invia solo gli allegati selezionati; se lista vuota, backend salta l'allegatura
        include_attachments: !!sendOpen.include_attachments && (sendOpen.selected_attachment_ids || []).length > 0,
        attachment_ids: sendOpen.include_attachments ? (sendOpen.selected_attachment_ids || []) : [],
        urgent: !!sendOpen.urgent,
      })),
    );
    const ok = results.filter((r) => r.status === "fulfilled");
    const ko = results.filter((r) => r.status === "rejected");
    if (ok.length) {
      const totalAtt = ok.reduce((acc, r) => acc + ((r.value?.data?.attachments || []).length), 0);
      toast.success(`Inviato a ${ok.length} partner${ok.length > 1 ? "" : ""}${totalAtt > 0 ? ` (${totalAtt} allegati totali)` : ""}`);

      // Se l'utente ha richiesto anche WhatsApp, genera il link condivisibile e apri wa.me
      if (sendOpen.also_whatsapp) {
        try {
          const shareRes = await api.post(`/quotes/${sendOpen.quote.id}/share-link`);
          const pdfUrl = shareRes.data?.url;
          if (!pdfUrl) throw new Error("URL non disponibile");
          const qn = sendOpen.quote.quote_number || "";
          const q = sendOpen.quote;
          const client = clients.find((c) => c.id === q.client_id);
          const cliFull = client ? `${client.surname || ""} ${client.name || ""}`.trim() : "";
          const tipo = (TYPES_LIVE.find((t) => t.v === q.type) || {}).l || q.type || "";
          // Per ogni destinatario fulfilled, recupera telefono e apri tab WhatsApp
          let waOpened = 0;
          let waSkipped = 0;
          sendOpen.targets.forEach((tgt, idx) => {
            if (results[idx].status !== "fulfilled") return;
            const list = tgt.type === "broker" ? brokers : companies;
            const p = list.find((x) => x.id === tgt.id);
            const rawPhone = (p?.mobile || p?.phone || "").toString();
            const digits = rawPhone.replace(/[^\d]/g, "");
            if (!digits) { waSkipped += 1; return; }
            // Normalizza prefisso italiano se manca
            const intl = digits.startsWith("00") ? digits.slice(2) : (digits.startsWith("39") ? digits : (digits.startsWith("3") || digits.startsWith("0") ? `39${digits.replace(/^0/, "")}` : digits));
            const msg = `Ciao ${p?.name || ""},\nti ho appena inviato per email la richiesta preventivo ${qn ? "n° " + qn : ""}${tipo ? " (" + tipo + ")" : ""}${cliFull ? " per il cliente " + cliFull : ""}.\n\nIn allegato qui il PDF stampabile: ${pdfUrl}\n\nGrazie!`;
            const waUrl = `whatsapp://send?phone=${intl}&text=${encodeURIComponent(msg)}`;
            window.open(waUrl, "_blank", "noopener,noreferrer");
            waOpened += 1;
          });
          if (waOpened > 0) toast.success(`📱 Aperti ${waOpened} WhatsApp${waSkipped > 0 ? ` (${waSkipped} senza telefono)` : ""}`);
          else if (waSkipped > 0) toast.warning(`Nessun WhatsApp aperto: i destinatari non hanno telefono.`);
        } catch (e) {
          toast.error("WhatsApp non aperto: " + (formatApiError(e) || "errore link"));
        }
      }
    }
    if (ko.length) {
      // 🆕 Mostra i dettagli dei fallimenti con nome partner + motivo esatto
      const failures = ko.map((r, i) => {
        // Trova il partner corrispondente all'invio fallito (stesso ordine di sendOpen.targets)
        const idx = results.indexOf(r);
        const tgt = sendOpen.targets[idx];
        const partnerObj = tgt ? (tgt.type === "broker"
          ? brokers.find((b) => b.id === tgt.id)
          : companies.find((c) => c.id === tgt.id)
        ) : null;
        const partnerName = partnerObj?.name || `Partner #${i + 1}`;
        const reason = r.reason?.response?.data?.detail || r.reason?.message || "Errore sconosciuto";
        return `• ${partnerName}: ${reason}`;
      }).join("\n");
      toast.error(
        `${ko.length} invio${ko.length > 1 ? "i falliti" : " fallito"}:\n${failures}`,
        { duration: 9000 }
      );
    }
    if (ok.length && !ko.length) setSendOpen(null);
    // Aggiorna il riepilogo invii (badge nelle card) e la cronologia espansa
    if (ok.length > 0 && sendOpen?.quote?.id) {
      try {
        const sum = await api.get("/quotes/sends-summary");
        setSendsSummary(sum.data || {});
        loadSends(sendOpen.quote.id);
      } catch { /* ignore */ }
    }
    } finally {
      // 🆕 Feb 2026 — Rilascio lock indipendentemente dall'esito
      partnerSendLockRef.current = false;
      setPartnerSendBusy(false);
    }
  };

  const openEdit = async (q) => {
    setEditing(q.id);
    quoteFormChangedRef.current = false;
    setQuoteAutoSaveAt(null);
    // 🆕 Feb 2026 — Fallback: se details.owner_* sono vuoti ma esiste un client_id valido,
    // pre-popola i campi anagrafici del contraente dai dati del cliente. Utile per:
    //  - preventivi creati prima del fix di popolamento owner_* dal wizard
    //  - preventivi creati manualmente col solo client_id
    let details = q.details ? { ...q.details } : {};
    // 🆕 Feb 2026 — Distinzione Persona/Azienda: se il preventivo non ha subject_type, cerca dal client
    const hasOwnerData = ["owner_name", "owner_surname", "owner_fiscal_code", "owner_email"].some((k) => (details[k] || "").toString().trim());
    const hasCompanyData = ["owner_ragione_sociale", "owner_partita_iva", "ragione_sociale", "partita_iva"].some((k) => (details[k] || "").toString().trim());
    if (q.client_id && !hasOwnerData && !hasCompanyData) {
      try {
        const { data } = await api.get(`/clients/${q.client_id}`);
        const c = data?.client || data || {};
        const isCompany = c.subject_type === "company";
        details = {
          ...details,
          subject_type: details.subject_type || c.subject_type || "private",
          // Persona (skip se azienda)
          owner_name: details.owner_name || (isCompany ? "" : (c.name || "")),
          owner_surname: details.owner_surname || (isCompany ? "" : (c.surname || "")),
          owner_fiscal_code: details.owner_fiscal_code || (isCompany ? "" : (c.fiscal_code || "")),
          owner_birth_date: details.owner_birth_date || (isCompany ? "" : (c.birth_date || "")),
          owner_birth_place: details.owner_birth_place || (isCompany ? "" : (c.birth_place || "")),
          owner_gender: details.owner_gender || (isCompany ? "" : (c.gender || "")),
          owner_marital_status: details.owner_marital_status || (isCompany ? "" : (c.marital_status || "")),
          owner_address: details.owner_address || c.address || "",
          owner_house_number: details.owner_house_number || c.house_number || "",
          owner_city: details.owner_city || c.city || "",
          owner_province: details.owner_province || c.province || "",
          owner_cap: details.owner_cap || c.cap || "",
          owner_email: details.owner_email || c.email || "",
          owner_phone: details.owner_phone || c.mobile || c.phone || "",
          // 🆕 Azienda
          owner_ragione_sociale: details.owner_ragione_sociale || details.ragione_sociale || (isCompany ? (c.ragione_sociale || c.name || "") : ""),
          owner_partita_iva: details.owner_partita_iva || details.partita_iva || (isCompany ? (c.partita_iva || "") : ""),
          pec_email: details.pec_email || (isCompany ? (c.pec_email || "") : ""),
          sdi_code: details.sdi_code || (isCompany ? (c.sdi_code || "") : ""),
          legale_rappresentante_name: details.legale_rappresentante_name || (isCompany ? (c.legale_rappresentante_name || "") : ""),
          legale_rappresentante_surname: details.legale_rappresentante_surname || (isCompany ? (c.legale_rappresentante_surname || "") : ""),
          legale_rappresentante_fiscal_code: details.legale_rappresentante_fiscal_code || (isCompany ? (c.legale_rappresentante_fiscal_code || "") : ""),
          legale_rappresentante_birth_date: details.legale_rappresentante_birth_date || (isCompany ? (c.legale_rappresentante_birth_date || "") : ""),
          legale_rappresentante_birth_place: details.legale_rappresentante_birth_place || (isCompany ? (c.legale_rappresentante_birth_place || "") : ""),
          legale_rappresentante_birth_province: details.legale_rappresentante_birth_province || (isCompany ? (c.legale_rappresentante_birth_province || "") : ""),
          legale_rappresentante_gender: details.legale_rappresentante_gender || (isCompany ? (c.legale_rappresentante_gender || "") : ""),
          legale_rappresentante_role: details.legale_rappresentante_role || (isCompany ? (c.legale_rappresentante_role || "") : ""),
          legale_rappresentante_nationality: details.legale_rappresentante_nationality || (isCompany ? (c.legale_rappresentante_nationality || "IT") : ""),
        };
      } catch { /* silent — se la fetch fallisce, apri comunque con quello che c'è */ }
    }
    setForm({
      client_id: q.client_id || "",
      type: q.type || "auto",
      coverage_request: q.coverage_request || "",
      estimated_premium: q.estimated_premium ?? "",
      details,
      internal_notes: q.internal_notes || "",
    });
    setPendingFiles([]);
    // 🆕 In modifica permetti comunque l'estrazione AI: i campi già valorizzati
    // saranno preservati (merge "fill if empty"). Vedi extractFromLibretto.
    setExtractionDone(false);
    setExtractionError(null);
    // Carica gli allegati già presenti per mostrarli (read-only) nel dialog
    try {
      const { data } = await api.get(`/quotes/${q.id}/attachments`);
      setPendingExisting(data || []);
    } catch { setPendingExisting([]); }
    // Carica storico invii email
    try {
      const { data } = await api.get(`/quotes/${q.id}/attachment-send-logs`);
      setQattSendLogs(data || []);
    } catch { setQattSendLogs([]); }
    setQattSelected({});
    setOpen(true);
  };

  // 🆕 Autosave preventivo: PUT silenzioso 30s dopo l'ultimo cambio del form, solo in modalità modifica.
  useEffect(() => {
    if (!open || !editing) return;
    if (!quoteFormChangedRef.current) {
      quoteFormChangedRef.current = true;
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const cleanDetails = Object.fromEntries(
          Object.entries(form.details || {}).filter(([, v]) => v !== "" && v != null && !(Array.isArray(v) && v.length === 0)),
        );
        const payload = {
          ...form,
          client_id: form.client_id || null,
          estimated_premium: form.estimated_premium ? parseFloat(form.estimated_premium) : null,
          details: cleanDetails,
        };
        await api.put(`/quotes/${editing}`, payload);
        setQuoteAutoSaveAt(new Date());
      } catch { /* silent */ }
    }, 30000);
    return () => clearTimeout(handle);
  }, [form, open, editing]);

  const openQuoteAttSend = () => {
    if (!editing) return;
    const ids = Object.entries(qattSelected).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) { toast.warning("Seleziona almeno un allegato"); return; }
    const q = items.find((it) => it.id === editing);
    const client = clients.find((c) => c.id === (q?.client_id || form.client_id));
    setQattSendForm({
      to_email: client?.email || "",
      subject: `Documenti preventivo ${q?.quote_number || ""}`.trim(),
      body: `Gentile ${client?.surname || ""} ${client?.name || ""},\n\nin allegato i documenti relativi al preventivo ${q?.quote_number || ""} (${q?.type || form.type || ""}).\n\nCordiali saluti.`,
    });
    setQattSendOpen(true);
  };

  const confirmQuoteAttSend = async () => {
    if (!editing || qattSendBusy) return;
    const ids = Object.entries(qattSelected).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length || !qattSendForm.to_email.includes("@")) {
      toast.error("Compila i campi obbligatori"); return;
    }
    setQattSendBusy(true);
    try {
      const { data } = await api.post(`/quotes/${editing}/send-attachments`, {
        attachment_ids: ids,
        to_email: qattSendForm.to_email,
        subject: qattSendForm.subject,
        body: qattSendForm.body,
      });
      toast.success(`Email inviata a ${data.recipient} (${data.sent} file)`);
      setQattSendOpen(false);
      setQattSelected({});
      const { data: logs } = await api.get(`/quotes/${editing}/attachment-send-logs`);
      setQattSendLogs(logs || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setQattSendBusy(false);
    }
  };

  // Carica mini-riepilogo cliente quando viene selezionato (per dialog)
  useEffect(() => {
    if (!open || !form.client_id) {
      setClientSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${form.client_id}/summary`);
        if (!cancelled) setClientSummary(data);
      } catch { if (!cancelled) setClientSummary(null); }
    })();
    return () => { cancelled = true; };
  }, [open, form.client_id]);

  // 🆕 Catalogo veicoli del cliente (aggregato da polizze, preventivi e vehicles[])
  const [vehiclesCatalogOpen, setVehiclesCatalogOpen] = useState(false);
  const [vehiclesCatalog, setVehiclesCatalog] = useState([]);
  const [vehiclesCatalogLoading, setVehiclesCatalogLoading] = useState(false);
  const openVehiclesCatalog = async () => {
    if (!form.client_id) { toast.warning("Seleziona prima un cliente"); return; }
    setVehiclesCatalogOpen(true);
    setVehiclesCatalogLoading(true);
    try {
      const { data } = await api.get(`/clients/${form.client_id}/vehicles-catalog`);
      setVehiclesCatalog(Array.isArray(data?.vehicles) ? data.vehicles : []);
    } catch (e) {
      toast.error(formatApiError(e));
      setVehiclesCatalog([]);
    } finally {
      setVehiclesCatalogLoading(false);
    }
  };
  const importVehicleIntoForm = (v) => {
    if (!v) return;
    setForm((f) => ({
      ...f,
      details: {
        ...(f.details || {}),
        vehicle_plate: v.plate || f.details?.vehicle_plate || "",
        vehicle_brand: v.brand || f.details?.vehicle_brand || "",
        vehicle_model: v.model || f.details?.vehicle_model || "",
        vehicle_first_registration: v.first_registration || f.details?.vehicle_first_registration || "",
        vehicle_purchase_date: v.purchase_date || f.details?.vehicle_purchase_date || "",
        vehicle_fuel: v.fuel || f.details?.vehicle_fuel || "",
        vehicle_body_type: v.body_type || f.details?.vehicle_body_type || "",
        chassis_number: v.chassis_number || f.details?.chassis_number || "",
        vehicle_fiscal_power: v.fiscal_power ?? f.details?.vehicle_fiscal_power ?? "",
        vehicle_kw: v.kw ?? f.details?.vehicle_kw ?? "",
        vehicle_displacement: v.displacement ?? f.details?.vehicle_displacement ?? "",
      },
    }));
    setVehiclesCatalogOpen(false);
    toast.success(`Veicolo ${v.plate} importato nel preventivo ✓`);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditing(null);
    setForm(empty);
    setPendingFiles([]);
    setPendingExisting([]);
    setExtractionDone(false);
    setExtractionError(null);
    setClientSummary(null);
  };

  const submit = async () => {
    try {
      // 🆕 Feb 2026 — Validazione obbligatorietà "Attestato di rischio altro veicolo" — solo se visibile
      // (visibile solo quando Situazione Assicurativa ha flag `enables_other_attestato`).
      const _attestatoVisible = (form.type === "auto" || form.type === "moto")
        && bersaniSourceOpts.length > 0
        && !!insuranceStatusOpts.find((o) => o.code === form.details?.insurance_status)?.enables_other_attestato;
      if (_attestatoVisible && !form.details?.bersani_source) {
        toast.error("Seleziona il campo \"Attestato di rischio altro veicolo\" prima di salvare.");
        return;
      }
      const cleanDetails = Object.fromEntries(
        Object.entries(form.details || {}).filter(([, v]) => v !== "" && v != null && !(Array.isArray(v) && v.length === 0)),
      );
      // 🆕 Il preventivo può essere salvato SENZA cliente: i dati del proprietario rimangono
      // in details.owner_* finché il preventivo non viene convertito in polizza.
      let clientId = form.client_id;
      if (!clientId && !(cleanDetails.owner_name || cleanDetails.owner_surname)) {
        toast.error("Seleziona un cliente esistente oppure compila almeno Nome e Cognome del proprietario nei Dati personali");
        return;
      }
      // 🆕 Controllo obbligatorietà targa: se la tipologia polizza prevede il campo
      // "vehicle_plate" (auto/moto o config dinamica con vehicle_plate selezionato),
      // il campo deve essere valorizzato prima di poter salvare.
      const _typeCfg = (DYN_TYPES_RAW || []).find((t) => t.code === form.type) || null;
      const _plateRequired = (
        form.type === "auto" ||
        form.type === "moto" ||
        (_typeCfg?.vehicle_fields_enabled && Array.isArray(_typeCfg?.vehicle_fields_selected) && _typeCfg.vehicle_fields_selected.includes("vehicle_plate"))
      );
      if (_plateRequired && !((cleanDetails.vehicle_plate || "").toString().trim())) {
        toast.error("Targa obbligatoria: compila il campo Targa nei Dati specifici prima di salvare il preventivo.");
        return;
      }
      // 🆕 Validazione formato targa: se valorizzata ma con formato non valido, richiede flag "Formato speciale"
      if ((cleanDetails.vehicle_plate || "").toString().trim() && !cleanDetails.vehicle_plate_override) {
        const _pv = validatePlate(cleanDetails.vehicle_plate);
        if (!_pv.ok) {
          toast.error(`Formato targa non valido: ${_pv.message}. Spunta "Formato speciale" per forzare il salvataggio.`);
          return;
        }
      }
      // 🆕 Email proprietario obbligatoria salvo flag "Non disponibile"
      if (!cleanDetails.owner_email_unavailable && !(cleanDetails.owner_email || "").trim()) {
        toast.error("Email proprietario obbligatoria — oppure spunta 'Non disponibile' accanto al campo");
        return;
      }
      // 🆕 Feb 2026 — Distingue contraente Azienda vs Persona Fisica.
      // Per aziende: NON sono obbligatori Cognome/Nome/CF/Data nascita persona,
      // MA sono sempre obbligatori Ragione Sociale e Partita IVA (anche se non
      // compaiono nella configurazione globale dei campi obbligatori).
      const _isCompanyContraente = ["company", "azienda"].includes((cleanDetails.subject_type || "").toString().toLowerCase());
      // 🆕 Campi obbligatori configurati per tipologia polizza (admin → Tipologie polizze)
      const _requiredFields = Array.isArray(_typeCfg?.required_fields) ? _typeCfg.required_fields : [];
      if (_requiredFields.length > 0 || _isCompanyContraente) {
        const _fieldLabels = {
          vehicle_plate: "Targa",
          vehicle_brand: "Marca",
          vehicle_model: "Modello",
          vehicle_first_registration: "Data prima immatricolazione",
          vehicle_fuel: "Alimentazione",
          vehicle_kw: "Potenza (kW)",
          vehicle_displacement: "Cilindrata (cc)",
          vehicle_chassis: "Numero telaio",
          chassis_number: "Numero telaio",
          vehicle_purchase_date: "Data acquisto veicolo",
          vehicle_seats: "Numero posti",
          vehicle_color: "Colore",
          owner_name: "Nome proprietario",
          owner_surname: "Cognome proprietario",
          owner_fiscal_code: "Codice fiscale proprietario",
          owner_birth_date: "Data di nascita proprietario",
          owner_email: "Email proprietario",
          owner_phone: "Telefono / cellulare proprietario",
          owner_address: "Indirizzo proprietario",
          owner_ragione_sociale: "Ragione Sociale",
          owner_partita_iva: "Partita IVA",
        };
        // 🆕 Feb 2026 — Filtra i campi obbligatori in base al tipo di soggetto:
        // - Azienda → escludi campi persona fisica (owner_name/surname/fiscal_code/birth_date)
        //           → aggiungi owner_ragione_sociale + owner_partita_iva (obbligatori)
        // - Persona → escludi campi azienda (owner_ragione_sociale/partita_iva)
        const _companyOnlyFields = new Set(["owner_ragione_sociale", "owner_partita_iva"]);
        const _privateOnlyFields = new Set(["owner_name", "owner_surname", "owner_fiscal_code", "owner_birth_date"]);
        let _effectiveRequired = _requiredFields.filter((f) => (
          _isCompanyContraente ? !_privateOnlyFields.has(f) : !_companyOnlyFields.has(f)
        ));
        if (_isCompanyContraente) {
          // Ragione Sociale e P.IVA sempre obbligatorie per aziende (anche fuori config globale)
          if (!_effectiveRequired.includes("owner_ragione_sociale")) _effectiveRequired.push("owner_ragione_sociale");
          if (!_effectiveRequired.includes("owner_partita_iva")) _effectiveRequired.push("owner_partita_iva");
        }
        const _missing = _effectiveRequired.filter((f) => {
          // 🆕 10/07/2026 — Rispetta i flag "Non disponibile" (owner_email_unavailable, owner_fiscal_code_unavailable, ecc.)
          if (cleanDetails[`${f}_unavailable`]) return false;
          // 🆕 Feb 2026 — Fallback su ragione_sociale/partita_iva (naming legacy) per compatibilità
          if (f === "owner_ragione_sociale" && (cleanDetails.ragione_sociale || "").toString().trim()) return false;
          if (f === "owner_partita_iva" && (cleanDetails.partita_iva || "").toString().trim()) return false;
          return !((cleanDetails[f] || "").toString().trim());
        });
        if (_missing.length > 0) {
          const _labels = _missing.map((f) => _fieldLabels[f] || f).join(", ");
          toast.error(`Campi obbligatori mancanti per ${TYPES_LIVE.find((t) => t.v === form.type)?.l || form.type}: ${_labels}`);
          return;
        }
      }
      // 🆕 Controllo duplicati per targa veicolo: se esiste già un altro preventivo
      // con la stessa targa, avvisa l'operatore e fagli scegliere se eliminarli.
      const plate = (cleanDetails.vehicle_plate || "").trim().toUpperCase();
      if (plate && !dupCheckBypassed) {
        try {
          const { data: dup } = await api.get("/quotes/check-duplicate-plate", {
            params: { plate, exclude_id: editing || undefined },
          });
          if (dup?.duplicates?.length > 0) {
            setDupCheckContext("save");
            setDupCheckResult({ plate, items: dup.duplicates });
            setDupCheckOpen(true);
            return; // si attende la decisione dell'operatore nel dialog
          }
        } catch { /* non bloccare il salvataggio per errori sul check */ }
      }
      const payload = {
        ...form,
        client_id: clientId || null,
        estimated_premium: form.estimated_premium ? parseFloat(form.estimated_premium) : null,
        details: cleanDetails,
      };
      let savedId = editing;
      if (editing) {
        await api.put(`/quotes/${editing}`, payload);
      } else {
        const { data: created } = await api.post("/quotes", payload);
        savedId = created?.id;
      }
      // Upload nuovi allegati
      if (savedId && pendingFiles.length > 0) {
        for (const f of pendingFiles) {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("filename", f.name);
          try {
            await api.post(`/quotes/${savedId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          } catch (uploadErr) {
            // 🆕 Feb 2026 — mostra l'errore reale invece di sopprimerlo silenziosamente
            const detail = formatApiError(uploadErr);
            console.error(`[Quotes.submit] Upload allegato "${f.name}" fallito:`, uploadErr);
            toast.error(`❌ Errore upload "${f.name}": ${detail}`);
          }
        }
      }
      const baseMsg = pendingFiles.length > 0
        ? `${editing ? "Modifiche salvate" : "Preventivo creato"} + ${pendingFiles.length} allegat${pendingFiles.length === 1 ? "o" : "i"}`
        : (editing ? "Modifiche salvate" : "Preventivo creato");
      if (editing) {
        toast.success(baseMsg);
      } else {
        // Preventivo appena creato → proponi follow-up in agenda
        toast.success(baseMsg, {
          action: {
            label: "📅 Follow-up 7gg",
            onClick: async () => {
              try {
                await api.post("/agenda/quick-followup", {
                  client_id: clientId, quote_id: savedId,
                  days: 7, reminder_minutes_before: 60,
                });
                toast.success("Follow-up creato in agenda");
              } catch { toast.error("Errore creazione follow-up"); }
            },
          },
          duration: 8000,
        });
      }
      closeDialog();
      load();
      setDupCheckBypassed(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeExistingAttachment = async (att) => {    if (!window.confirm(`Eliminare allegato "${att.filename}"?`)) return;
    try {
      await api.delete(`/quotes/${editing}/attachments/${att.id}`);
      setPendingExisting((arr) => arr.filter((a) => a.id !== att.id));
      toast.success("Allegato eliminato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // 🆕 Rinomina un allegato esistente
  const renameExistingAttachment = async (att) => {
    const current = att.filename || "allegato";
    const nuovo = window.prompt(`Rinomina allegato:`, current);
    if (nuovo === null) return;
    const trimmed = (nuovo || "").trim();
    if (!trimmed || trimmed === current) return;
    try {
      await api.patch(`/quotes/${editing}/attachments/${att.id}`, { filename: trimmed });
      setPendingExisting((arr) => arr.map((a) => a.id === att.id ? { ...a, filename: trimmed } : a));
      toast.success("Allegato rinominato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // 🆕 Feb 2026 — Upload immediato con dialog di rinomina prima del caricamento.
  // Coda file da uploadare uno alla volta con rename dialog interposto.
  const [renameQueue, setRenameQueue] = useState([]); // File[]
  const [renameCurrent, setRenameCurrent] = useState(null); // { file, proposedName, description }
  const [renameUploading, setRenameUploading] = useState(false);

  const startUploadWithRename = async (files) => {
    if (!editing) {
      // Fallback: se il preventivo non è ancora salvato, mantieni il vecchio comportamento (queue).
      setPendingFiles((arr) => [...arr, ...files]);
      toast.success(`${files.length} file${files.length === 1 ? " aggiunto" : " aggiunti"} in coda (salva il preventivo per caricarli)`);
      return;
    }
    setRenameQueue(files);
    // Apri dialog sul primo
    const [first, ...rest] = files;
    setRenameCurrent({ file: first, proposedName: first.name, description: "", classifying: true });
    setRenameQueue(rest);
    // 🆕 Classificazione AI in background — pre-compila descrizione
    classifyInBackground(first);
  };

  // 🆕 Feb 2026 — Chiamata AI di classificazione (non blocca l'UI)
  const classifyInBackground = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/attachments/classify", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 });
      // Aggiorna solo se il dialog è ancora aperto sullo stesso file
      setRenameCurrent((cur) => {
        if (!cur || cur.file !== file) return cur;
        return {
          ...cur,
          description: cur.description || data?.description || data?.category || "",
          category: data?.category || null,
          confidence: data?.confidence || 0,
          classifying: false,
        };
      });
    } catch {
      setRenameCurrent((cur) => (cur && cur.file === file ? { ...cur, classifying: false } : cur));
    }
  };

  const confirmRenameAndUpload = async () => {
    if (!renameCurrent || !editing) return;
    const { file, proposedName, description } = renameCurrent;
    const trimmed = (proposedName || file.name).trim() || file.name;
    setRenameUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("filename", trimmed);
      if (description) fd.append("description", description);
      await api.post(`/quotes/${editing}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Ricarica lista esistenti dal server per avere il record ufficiale (con id/size/created_at)
      try {
        const { data } = await api.get(`/quotes/${editing}/attachments`);
        setPendingExisting(data || []);
      } catch { /* ignore refresh error */ }
      toast.success(`✓ "${trimmed}" caricato`);
    } catch (e) {
      toast.error(`Upload fallito: ${formatApiError(e)}`);
    } finally {
      setRenameUploading(false);
    }
    // Passa al file successivo, se presente
    if (renameQueue.length > 0) {
      const [next, ...rest] = renameQueue;
      setRenameCurrent({ file: next, proposedName: next.name, description: "", classifying: true });
      setRenameQueue(rest);
      classifyInBackground(next);
    } else {
      setRenameCurrent(null);
    }
  };

  const skipCurrentRename = () => {
    if (renameQueue.length > 0) {
      const [next, ...rest] = renameQueue;
      setRenameCurrent({ file: next, proposedName: next.name, description: "", classifying: true });
      setRenameQueue(rest);
      classifyInBackground(next);
    } else {
      setRenameCurrent(null);
    }
  };

  // 🆕 Feb 2026 — Download / apertura in nuova tab di un allegato esistente del preventivo.
  const openExistingAttachment = async (att) => {
    const qid = att.quote_id || editing;
    if (!qid) { toast.error("ID preventivo mancante"); return; }
    // Download con barra di avanzamento reale (progress helper globale)
    await downloadWithProgress({
      label: att.filename || att.original_filename || "allegato",
      blobPath: `/quotes/${qid}/attachments/${att.id}/download`,
      filename: att.filename || att.original_filename,
    }).catch(() => { /* toast già mostrato dall'helper */ });
  };

  const loadSends = useCallback(async (qid) => {
    try {
      const { data } = await api.get(`/quotes/${qid}/sends`);
      setSends((m) => ({ ...m, [qid]: data || [] }));
    } catch { /* ignore */ }
  }, []);

  const markReplied = async (qid, sendId, replied) => {
    try {
      const url = replied
        ? `/quotes/${qid}/sends/${sendId}/unmark-replied`
        : `/quotes/${qid}/sends/${sendId}/mark-replied`;
      await api.post(url);
      toast.success(replied ? "Risposta annullata" : "Risposta segnalata");
      loadSends(qid);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resendSend = async (qid, sendId, partnerName) => {
    // 🆕 Usa il dialog di conferma unificato invece di window.confirm
    setResendConfirm({ qid, sendId, partnerName: partnerName || "questo partner" });
  };

  // Estrazione AI libretto → popola dati veicolo + cerca cliente per CF + crea campi dinamici
  // Skip dell'estrazione solo dal 2° file in poi (dopo la prima estrazione riuscita).
  // I file successivi vengono solo allegati per evitare di sovrascrivere i dati.
  // 🆕 Helper: in modifica preventivo, allega subito il file alla quote esistente.
  //    Per nuova quote (no editing) accumula in pendingFiles (verrà caricato al salvataggio).
  const attachFileToQuoteOrQueue = async (file, descr = "") => {
    if (!file) return;
    if (editing) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const url = `/quotes/${editing}/attachments${descr ? `?description=${encodeURIComponent(descr)}` : ""}`;
        await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
        // 🔁 Refresh lista allegati esistenti
        try {
          const { data: existing } = await api.get(`/quotes/${editing}/attachments`);
          setPendingExisting(existing || []);
        } catch { /* noop */ }
        toast.success(`📎 "${file.name}" allegato al preventivo`);
      } catch (e) {
        toast.warning(`Allegato fallito: ${formatApiError(e)} · Aggiunto in coda al salvataggio.`);
        setPendingFiles((arr) => [...arr, file]);
      }
    } else {
      setPendingFiles((arr) => [...arr, file]);
    }
  };

  const extractFromLibretto = async (file) => {
    if (!file) return;
    if (extractionDone) {
      await attachFileToQuoteOrQueue(file, "Allegato post-estrazione");
      return;
    }
    setExtracting(true);
    setExtractionError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/utils/extract-libretto", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Backend ritorna { kind: "libretto", extracted: { ...fields } }
      const d = (data && data.extracted) || {};
      // 🆕 Estrae i cointestatari (lista). Se presenti, mostriamo un dialog di scelta intestatario.
      const coOwnersRaw = Array.isArray(d.co_owners) ? d.co_owners : [];
      // Rimuovi `co_owners` dai campi piatti: non va nei details del form.
      delete d.co_owners;
      // Cerca cliente per CF intestatario
      let matchedClient = null;
      const cfRaw = d.owner_fiscal_code || d.fiscal_code || d.cf;
      if (cfRaw) {
        const cf = String(cfRaw).toUpperCase().trim();
        matchedClient = (clients || []).find((c) => (c.fiscal_code || "").toUpperCase() === cf);
        // Mismatch: cliente già selezionato ma con CF diverso da quello estratto
        if (form.client_id) {
          const currentCli = (clients || []).find((c) => c.id === form.client_id);
          const currentCF = (currentCli?.fiscal_code || "").toUpperCase().trim();
          if (currentCF && currentCF !== cf) {
            toast.warning(
              `⚠️ Intestatario documento diverso dal cliente selezionato. Documento: ${cf} · Cliente ${currentCli?.surname || ""} ${currentCli?.name || ""}: ${currentCF}. Verifica prima di salvare.`,
              { duration: 12000 },
            );
          }
        }
      }
      // 🆕 Veicolo cointestato → costruisci la lista di possibili intestatari e chiedi all'operatore
      // a chi intestare il preventivo. I dati veicolo (vehicle_*) sono comuni, vengono applicati comunque.
      if (coOwnersRaw.length > 0) {
        // Estrai i campi veicolo+altri non-owner da `d` (questi vanno sempre nel form)
        const ownerKeys = new Set([
          "owner_name", "owner_surname", "owner_fiscal_code", "owner_birth_date", "owner_birth_place",
          "owner_address", "owner_house_number", "owner_city", "owner_province", "owner_cap",
        ]);
        const vehicleAndOther = Object.fromEntries(
          Object.entries(d).filter(([k, v]) => !ownerKeys.has(k) && v !== null && v !== undefined && v !== ""),
        );
        const primaryOwner = Object.fromEntries(
          [...ownerKeys].filter((k) => d[k] !== null && d[k] !== undefined && d[k] !== "").map((k) => [k, d[k]]),
        );
        const ownersList = [
          {
            label: `${(d.owner_surname || "").toUpperCase()} ${(d.owner_name || "").toUpperCase()}`.trim() || "Intestatario principale",
            sub: d.owner_fiscal_code || "",
            data: primaryOwner,
          },
          ...coOwnersRaw.map((co, i) => ({
            label: `${(co.surname || "").toUpperCase()} ${(co.name || "").toUpperCase()}`.trim() || `Cointestatario ${i + 1}`,
            sub: co.fiscal_code || "",
            data: Object.fromEntries(
              Object.entries({
                owner_name: co.name,
                owner_surname: co.surname,
                owner_fiscal_code: co.fiscal_code,
                owner_birth_date: co.birth_date,
                owner_birth_place: co.birth_place,
                owner_address: co.address,
                owner_house_number: co.house_number,
                owner_city: co.city,
                owner_province: co.province,
                owner_cap: co.cap,
              }).filter(([, v]) => v !== null && v !== undefined && v !== ""),
            ),
          })),
        ];
        setOwnerChoice({
          owners: ownersList,
          filename: file.name,
          vehicleAndOther,
          file,
        });
        // Setta vehicleAndOther subito (così l'operatore vede già targa/marca/modello)
        setForm((f) => ({
          ...f,
          type: f.type || "auto",
          details: { ...(f.details || {}), ...vehicleAndOther },
        }));
        setExtractionDone(true);
        setExtractionError(null);
        toast.info(`🚗 Veicolo cointestato: scegli a chi intestare il preventivo (${ownersList.length} possibili)`);
        attachFileToQuoteOrQueue(file, "Estratto AI - libretto");
        return;
      }
      // Se trovato cliente esistente, chiedi conferma prima di associarlo (evita selezioni inaspettate)
      let associateClient = false;
      if (matchedClient) {
        associateClient = window.confirm(
          `Estratto CF: ${cfRaw}\n\nIl cliente "${matchedClient.surname} ${matchedClient.name}" è già presente in anagrafica con questo CF.\n\nVuoi associarlo automaticamente a questo preventivo?\n\n(Annulla = i dati estratti popolano solo i campi del form)`,
        );
      }
      setForm((f) => {
        // Tutti i campi non-null/empty dal libretto vengono mergiati nei details (campi dinamici inclusi).
        // 🆕 L'estrazione sovrascrive sempre i campi corrispondenti (sia in creazione sia in modifica):
        // l'utente ha trascinato un nuovo documento → vuole aggiornare i dati a partire da esso.
        const extracted = Object.fromEntries(
          Object.entries(d).filter(([, v]) => v !== null && v !== undefined && v !== ""),
        );
        return {
          ...f,
          type: f.type || "auto",
          client_id: associateClient ? matchedClient.id : f.client_id,
          details: { ...(f.details || {}), ...extracted },
        };
      });
      const filled = Object.keys(d).filter((k) => d[k] !== null && d[k] !== undefined && d[k] !== "").length;
      if (filled > 0) {
        setExtractionDone(true);
        setExtractionError(null);
        if (associateClient) toast.success(`Estratti ${filled} campi. Cliente "${matchedClient.surname} ${matchedClient.name}" associato al preventivo.`);
        else if (matchedClient) toast.success(`Estratti ${filled} campi. (Cliente esistente trovato ma non associato — i dati restano nel form)`);
        else toast.success(`Estratti ${filled} campi dal libretto` + (cfRaw ? " (nessun cliente in anagrafica con questo CF)" : ""));
        attachFileToQuoteOrQueue(file, "Estratto AI - libretto");
        // 🆕 Controllo duplicati per targa estratta dal libretto: avvisa subito l'operatore
        const extractedPlate = (d.vehicle_plate || "").toString().trim().toUpperCase();
        if (extractedPlate) {
          try {
            const { data: dup } = await api.get("/quotes/check-duplicate-plate", {
              params: { plate: extractedPlate, exclude_id: editing || undefined },
            });
            if (dup?.duplicates?.length > 0) {
              setDupCheckContext("extract");
              setDupCheckResult({ plate: extractedPlate, items: dup.duplicates });
              setDupCheckOpen(true);
            }
          } catch { /* ignora errori check */ }
        }
      } else {
        // 200 OK ma nessun campo riconosciuto: mostra avviso persistente, NON allegare il file
        setExtractionError({ filename: file.name, reason: "L'AI non è riuscita a leggere alcun dato dal documento. Controlla che l'immagine sia nitida o prova un altro file." });
      }
    } catch (e) {
      // Errore HTTP (400/502/500): file non leggibile o problema AI
      setExtractionError({ filename: file.name, reason: formatApiError(e) });
    } finally {
      setExtracting(false);
    }
  };

  const setStatus = async (id, s) => {
    try { await api.put(`/quotes/${id}/status`, null, { params: { status: s } }); toast.success("Stato aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare preventivo?")) return;
    try { await api.delete(`/quotes/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const printSingleQuote = (q) => {
    // Apre il PDF generato dal backend in una nuova scheda: il browser mostra il viewer PDF
    // con bottone "Stampa" e "Scarica" — soluzione veloce e standard.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/quotes/${q.id}/print-pdf`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  // (Helper legacy HTML print rimosso — ora si usa il PDF backend)
  const _legacyHtmlPrintSingleQuote = (q) => {
    const client = clients.find((c) => c.id === q.client_id);
    const typeLabel = TYPE_LABEL_LIVE[q.type] || q.type;
    const entries = meaningfulDetails(q.details);
    const detailsRows = entries.map(([k, v]) =>
      `<tr><td style="padding:6px 10px;color:#64748B;background:#F8FAFC;border-bottom:1px solid #E2E8F0;">${DETAIL_LABELS[k] || k}</td><td style="padding:6px 10px;font-weight:500;border-bottom:1px solid #E2E8F0;">${formatDetailValue(k, v)}</td></tr>`
    ).join("");
    const clientRows = client ? [
      ["Nome", `${client.surname || ""} ${client.name || ""}`.trim()],
      ["Codice Fiscale", client.fiscal_code || "—"],
      ["Email", client.email || "—"],
      ["Cellulare", client.mobile || client.phone || "—"],
      ["Indirizzo", `${client.address || ""} ${client.house_number || ""}`.trim() || "—"],
      ["Città", `${client.city || "—"} ${client.province ? `(${client.province})` : ""} ${client.cap || ""}`.trim()],
    ].map(([k, v]) => `<tr><td style="padding:6px 10px;color:#64748B;background:#F8FAFC;border-bottom:1px solid #E2E8F0;">${k}</td><td style="padding:6px 10px;font-weight:500;border-bottom:1px solid #E2E8F0;">${v}</td></tr>`).join("") : "";
    const premio = q.estimated_premium != null ? formatCurrency(q.estimated_premium) : "—";
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Preventivo ${typeLabel} — ${client?.surname || ""} ${client?.name || ""}</title>
<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#0F172A;max-width:780px;margin:32px auto;padding:0 24px;}h1{font-size:22px;margin:0 0 4px 0;}p.sub{color:#64748B;margin:0 0 24px 0;font-size:13px;text-transform:uppercase;letter-spacing:0.1em;}h2{font-size:14px;text-transform:uppercase;letter-spacing:0.1em;color:#2563EB;margin:24px 0 8px 0;border-bottom:1px solid #DBEAFE;padding-bottom:4px;}table{width:100%;border-collapse:collapse;font-size:13px;}.box{background:#F1F5F9;padding:12px 14px;border-radius:6px;font-size:14px;line-height:1.5;}.premio{font-size:18px;font-weight:600;color:#0F172A;}@media print{body{margin:0;}}</style>
</head><body>
<h1>Preventivo ${typeLabel}</h1>
<p class="sub">Richiesta del ${formatDate(q.created_at)} · Stato: ${q.status.replace(/_/g, " ")}</p>
${client ? `<h2>Dati cliente</h2><table>${clientRows}</table>` : ""}
<h2>Richiesta copertura</h2>
<div class="box">${(q.coverage_request || "—").replace(/</g, "&lt;")}</div>
${detailsRows ? `<h2>Dati tecnici della richiesta</h2><table>${detailsRows}</table>` : ""}
<h2>Premio stimato</h2>
<p class="premio">${premio}</p>
${""}
<p style="margin-top:32px;color:#94A3B8;font-size:11px;">Documento generato automaticamente dal CRM Assicura.</p>
<script>window.onload=function(){setTimeout(function(){window.print();},300);};</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Abilita i popup per stampare"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="quotes-page">
      <header className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Preventivi</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3"><FileText className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600 bg-emerald-100 rounded-lg p-1.5" />Richieste preventivo</h1>
          <p className="text-slate-500 mt-1">{items.length} richieste {trashView ? "nel cestino" : "totali"}</p>
          {/* Tab switch Attive / Cestino */}
          <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-0.5 mt-3">
            <button
              data-testid="quotes-tab-active"
              onClick={() => setTrashView(false)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${!trashView ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
            >
              Attive
            </button>
            <button
              data-testid="quotes-tab-trash"
              onClick={() => setTrashView(true)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${trashView ? "bg-white shadow text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
            >
              <Trash2 className="h-3.5 w-3.5" /> Cestino
              {trashCount > 0 && (
                <span
                  data-testid="quotes-trash-count"
                  className="ml-1 inline-flex items-center justify-center bg-rose-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px]"
                >
                  {trashCount > 99 ? "99+" : trashCount}
                </span>
              )}
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => (allCollapsed ? expandAll() : collapseAll())}
            data-testid="quotes-toggle-all"
            className="text-xs"
            title={allCollapsed ? "Espandi tutte le schede" : "Comprimi tutte le schede"}
          >
            {allCollapsed ? <Maximize2 className="h-3.5 w-3.5 mr-1.5" /> : <Minimize2 className="h-3.5 w-3.5 mr-1.5" />}
            {allCollapsed ? "Espandi tutte" : "Comprimi tutte"}
          </Button>
          <Button variant="outline" data-testid="print-quotes" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" /> Stampa</Button>
          {canWrite("quotes") && (
            <Button
              data-testid="quote-wizard-btn"
              variant="outline"
              onClick={() => navigate("/quotes/wizard")}
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Sparkles className="h-4 w-4 mr-1.5" /> Wizard<span className="hidden sm:inline"> guidato</span>
            </Button>
          )}
          <Dialog open={open} onOpenChange={(o) => { if (!o) closeDialog(); else setOpen(true); }}>
          <DialogTrigger asChild>
            {canWrite("quotes") ? (
              <Button data-testid="add-quote-btn" className="bg-[#0F172A] hover:bg-[#1E293B] text-xs sm:text-sm" onClick={() => { setEditing(null); setForm(empty); setPendingFiles([]); setPendingExisting([]); setExtractionDone(false); setExtractionError(null); }}>
                <Plus className="h-4 w-4 mr-1.5" /> <span className="hidden sm:inline">Nuovo </span>preventivo
              </Button>
            ) : <span />}
          </DialogTrigger>
          <DialogContent className="w-[95vw] max-w-6xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Modifica preventivo" : "Nuovo preventivo"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {/* Upload + AI extract in cima */}
              <div className="rounded-lg border-2 border-purple-200 bg-purple-50/40 p-3 space-y-2" data-testid="quote-ai-zone">
                <div className="flex items-center gap-2 text-sm font-semibold text-purple-900">
                  <Sparkles className="h-4 w-4" /> Importa documenti e auto-compila il preventivo
                </div>
                <p className="text-[11px] text-slate-600">
                  Carica un libretto, una carta d&apos;identità o un PDF della vecchia polizza: i dati verranno estratti e i campi del form si compileranno automaticamente.
                </p>
                <div className="flex flex-wrap gap-2">
                  <label className={`inline-flex items-center gap-1.5 text-xs ${extracting ? "cursor-wait bg-amber-100 border-amber-400 text-amber-900 animate-pulse" : "cursor-pointer bg-white border-purple-300 hover:bg-purple-100 text-purple-800"} border px-3 py-1.5 rounded-md font-medium transition-colors`}>
                    <input type="file" accept="image/*,application/pdf" className="hidden" disabled={extracting} data-testid="quote-libretto-input"
                      onChange={(e) => { extractFromLibretto(e.target.files?.[0]); e.target.value = ""; }} />
                    {extracting ? (
                      <span className="inline-flex items-center gap-1.5" data-testid="quote-extract-loading">
                        <span className="inline-block animate-spin">⏳</span>
                        Estrazione in corso…
                      </span>
                    ) : (
                      <>📁 Carica documenti</>
                    )}
                  </label>
                  <ScanButton
                    testid="scan-quote-libretto"
                    label="Importa da scanner"
                    variant="outline"
                    disabled={extracting}
                    onFile={(f) => extractFromLibretto(f)}
                  />
<CameraButton testid="cam-scan-quote-libretto" label="Foto da fotocamera" variant="outline" disabled={extracting} onFile={(f) => extractFromLibretto(f)} />
                  <DropZone
                    testid="quote-dropzone"
                    onFiles={async (files) => {
                      if (!files || files.length === 0) return;
                      // 🆕 Se l'estrazione non è ancora avvenuta, tenta l'AI sul PRIMO file (PDF/immagine)
                      // così come fa il pulsante "Carica documenti". Gli altri file vengono allegati.
                      const first = files[0];
                      const rest = files.slice(1);
                      if (!extractionDone && first && /^(application\/pdf|image\/)/.test(first.type || "")) {
                        await extractFromLibretto(first);
                      } else {
                        const processed = await askMultiFileAction([first]);
                        setPendingFiles((arr) => [...arr, ...processed]);
                      }
                      if (rest.length > 0) {
                        const processedRest = await askMultiFileAction(rest);
                        setPendingFiles((arr) => [...arr, ...processedRest]);
                      }
                    }}
                    accept={["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"]}
                    className="flex-1 min-w-[200px] border-2 border-dashed border-purple-300 hover:border-purple-500 rounded-md px-3 py-1.5 cursor-pointer transition-colors bg-white/60"
                  >
                    <div className="text-center text-xs text-slate-600">
                      📎 Trascina qui un libretto/polizza per estrarre i dati, o
                      <label className="inline-block ml-1 text-purple-700 hover:underline cursor-pointer font-medium">
                        sfoglia
                        <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="quote-browse-files"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            e.target.value = "";
                            if (!files.length) return;
                            const first = files[0];
                            const rest = files.slice(1);
                            if (!extractionDone && first && /^(application\/pdf|image\/)/.test(first.type || "")) {
                              await extractFromLibretto(first);
                            } else {
                              const processed = await askMultiFileAction([first]);
                              setPendingFiles((arr) => [...arr, ...processed]);
                            }
                            if (rest.length > 0) {
                              const processedRest = await askMultiFileAction(rest);
                              setPendingFiles((arr) => [...arr, ...processedRest]);
                            }
                          }} />
                      </label>
                    </div>
                  </DropZone>
                </div>
                {extractionError && (
                  <div className="rounded-md border-2 border-rose-300 bg-rose-50 p-3 space-y-2" data-testid="quote-extraction-error">
                    <div className="flex items-start gap-2">
                      <span className="text-xl">⚠️</span>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-rose-900">Estrazione dati non riuscita</p>
                        <p className="text-xs text-rose-800 mt-0.5">
                          File: <span className="font-mono font-semibold">{extractionError.filename}</span>
                        </p>
                        <p className="text-xs text-rose-700 mt-1">{extractionError.reason}</p>
                        <p className="text-xs text-rose-900 mt-2 font-semibold">
                          💡 Cambia file e riprova: usa un&apos;immagine nitida e ben illuminata, oppure un PDF testuale (non scansione di bassa qualità).
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid="dismiss-extraction-error"
                        onClick={() => setExtractionError(null)}
                        className="text-rose-500 hover:text-rose-700 text-lg leading-none"
                        aria-label="Chiudi"
                      >×</button>
                    </div>
                  </div>
                )}
                {pendingFiles.length > 0 && (
                  <ul className="space-y-1" data-testid="quote-pending-files">
                    {pendingFiles.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between text-xs bg-white border border-purple-200 rounded px-2 py-1">
                        <span className="truncate flex-1">📄 {f.name} <span className="text-slate-500">({formatFileSize(f.size)})</span></span>
                        <button type="button" onClick={() => setPendingFiles((arr) => arr.filter((_, j) => j !== i))} className="text-rose-600 hover:text-rose-800 ml-2" aria-label="Rimuovi">×</button>
                      </li>
                    ))}
                  </ul>
                )}
                {/* Suggerimento: dopo il primo file, ricorda di allegare gli altri documenti tipici */}
                {(pendingFiles.length + pendingExisting.length) >= 1 && (() => {
                  const allFiles = [
                    ...pendingFiles.map((f) => f.name.toLowerCase()),
                    ...pendingExisting.map((a) => (a.filename || "").toLowerCase()),
                  ];
                  const checks = [
                    { key: "libretto", label: "📕 Libretto di circolazione", words: ["libretto", "circolazione"] },
                    { key: "patente", label: "🪪 Patente di guida", words: ["patente"] },
                    { key: "carta_identita", label: "🆔 Carta d'identità", words: ["carta", "identit", " ci ", "_ci_", "ci."] },
                    { key: "polizza", label: "📄 Vecchia polizza", words: ["polizza", "policy"] },
                  ];
                  const missing = checks.filter((c) => !allFiles.some((name) => c.words.some((w) => name.includes(w))));
                  if (missing.length === 0) {
                    return (
                      <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2.5 flex items-center gap-2" data-testid="quote-attachments-complete">
                        ✓ <span>Hai allegato tutti i documenti utili (libretto, patente, carta d&apos;identità, polizza).</span>
                      </div>
                    );
                  }
                  return (
                    <div className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2.5 space-y-1.5" data-testid="quote-attachments-hint">
                      <div className="font-semibold">💡 Per completare la richiesta, valuta di allegare anche:</div>
                      <ul className="list-disc list-inside text-blue-700 space-y-0.5">
                        {missing.map((m) => <li key={m.key}>{m.label}</li>)}
                      </ul>
                      <div className="text-[11px] text-blue-600/80 mt-1">Non sono obbligatori, ma velocizzano la valutazione del partner. I file successivi al primo verranno solo allegati (i dati restano quelli del primo documento).</div>
                    </div>
                  );
                })()}

                {/* 🆕 Drop-zone dedicata SOLO per aggiungere allegati (senza estrazione AI).
                    Coesistente con la drop-zone "Carica documenti" sopra che invece esegue anche l'OCR. */}
                <div className="mt-2">
                  <DropZone
                    testid="quote-attachments-dropzone"
                    onFiles={async (files) => {
                      if (!files || files.length === 0) return;
                      const processed = await askMultiFileAction(files);
                      await startUploadWithRename(processed);
                    }}
                    accept={["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"]}
                    className="border-2 border-dashed border-blue-300 hover:border-blue-500 rounded-md px-3 py-3 cursor-pointer transition-colors bg-blue-50/40"
                  >
                    <div className="text-center text-xs text-slate-700">
                      📁 <strong>Aggiungi altri allegati</strong> — trascina qui i file
                      <span className="text-slate-400 mx-1">·</span>
                      <label className="inline-block text-blue-700 hover:underline cursor-pointer font-medium">
                        sfoglia
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.png,.jpg,.jpeg,.webp"
                          className="hidden"
                          data-testid="quote-attachments-browse"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            e.target.value = "";
                            if (!files.length) return;
                            const processed = await askMultiFileAction(files);
                            await startUploadWithRename(processed);
                          }}
                        />
                      </label>
                      <div className="text-[10px] text-slate-500 mt-1">
                        {editing
                          ? "PDF, immagini · rinomina prima dell'upload, caricamento immediato"
                          : "PDF, immagini · in coda finché non salvi il preventivo"}
                      </div>
                    </div>
                  </DropZone>
                </div>

                {editing && pendingExisting.length > 0 && (
                  <div className="mt-2" data-testid="quote-existing-attachments">
                    <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Allegati già salvati ({pendingExisting.length})</p>
                      <div className="flex items-center gap-2">
                        <ApplyPdfTemplateButton
                          quoteId={editing?.id}
                          variant="outline"
                          testid="quote-apply-pdf-tpl"
                          onApplied={async () => { try { const { data } = await api.get(`/quotes/${editing.id}/attachments`); setPendingExisting(data); } catch { /* noop */ } }}
                        />
                        <Button
                          type="button"
                          size="sm"
                          data-testid="quote-send-att-btn"
                          onClick={openQuoteAttSend}
                          disabled={!Object.values(qattSelected).some(Boolean)}
                          className="bg-blue-600 hover:bg-blue-700 text-white h-6 text-[10px] px-2"
                        >
                          📧 Invia via email ({Object.values(qattSelected).filter(Boolean).length})
                        </Button>
                      </div>
                    </div>
                    <ul className="space-y-1">
                      {pendingExisting.map((a) => (
                        <li key={a.id} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                          <input
                            type="checkbox"
                            data-testid={`quote-att-check-${a.id}`}
                            checked={!!qattSelected[a.id]}
                            onChange={(e) => setQattSelected({ ...qattSelected, [a.id]: e.target.checked })}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                          <span className="truncate flex-1">📎 {a.filename} <span className="text-slate-400">({formatFileSize(a.size_bytes || 0)} · {formatDate(a.created_at)})</span></span>
                          <button
                            type="button"
                            data-testid={`preview-existing-attachment-${a.id}`}
                            onClick={() => {
                              const qid = a.quote_id || editing;
                              if (!qid) return;
                              openAttachmentPreview(`/quotes/${qid}/attachments/${a.id}/download`, {
                                filename: a.filename,
                                content_type: a.content_type,
                              });
                            }}
                            className="h-7 px-2 flex items-center justify-center rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 text-[11px] font-medium"
                            aria-label="Anteprima allegato"
                            title="Anteprima inline"
                          >👁️ Anteprima</button>
                          <button
                            type="button"
                            data-testid={`download-existing-attachment-${a.id}`}
                            onClick={() => openExistingAttachment(a)}
                            className="h-7 px-2 flex items-center justify-center rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 text-[11px] font-medium"
                            aria-label="Scarica allegato"
                            title="Apri in nuova scheda / scarica"
                          >⬇ Apri</button>
                          <button
                            type="button"
                            data-testid={`rename-existing-attachment-${a.id}`}
                            onClick={() => renameExistingAttachment(a)}
                            className="h-7 px-2 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 hover:border-slate-300 text-[11px] font-medium"
                            aria-label="Rinomina allegato"
                            title="Rinomina"
                          >✏️ Rinomina</button>
                          <button
                            type="button"
                            data-testid={`remove-existing-attachment-${a.id}`}
                            onClick={() => removeExistingAttachment(a)}
                            className="h-7 px-2 flex items-center justify-center rounded border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:border-rose-400 text-[11px] font-semibold gap-1"
                            aria-label="Elimina allegato"
                            title="Elimina"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Elimina
                          </button>
                        </li>
                      ))}
                    </ul>
                    {qattSendLogs.length > 0 && (
                      <div className="mt-2 bg-slate-50 border border-slate-200 rounded p-2" data-testid="quote-send-logs">
                        <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">📧 Storico invii ({qattSendLogs.length})</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {qattSendLogs.map((l) => (
                            <div key={l.id} className="text-[11px] flex items-start gap-1.5 border-l-2 border-emerald-400 pl-1.5 py-0.5">
                              <span className={`text-[9px] uppercase font-bold rounded px-1 ${l.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                                {l.status === "sent" ? "✓" : "✗"}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="truncate"><span className="font-semibold">{l.recipient}</span> · {formatDateTime(l.sent_at)}</div>
                                <div className="text-slate-500 truncate">{(l.attachment_names || []).join(", ")}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label>
                  Cliente{(form.details?.owner_name && form.details?.owner_surname)
                    ? <span className="ml-1 text-[10px] font-normal text-emerald-700">(non necessario — i Dati personali compilati creeranno automaticamente il cliente)</span>
                    : <span className="text-red-500"> *</span>}
                </Label>
                <div data-testid="quote-client-wrap" className={(form.client_id || (form.details?.owner_name && form.details?.owner_surname)) ? "" : "rounded-md bg-yellow-50"}>
                  <ClientCombobox
                    clients={clients}
                    value={form.client_id}
                    onChange={(v) => {
                      const c = clients.find((cl) => cl.id === v);
                      setForm((f) => ({
                        ...f,
                        client_id: v,
                        details: { ...prefillQuoteDetails(c, f.type), ...(f.details || {}) },
                      }));
                    }}
                    onClientCreated={(c) => {
                      setClients((prev) => (prev.some((x) => x.id === c.id) ? prev : [...prev, c]));
                      setForm((f) => ({
                        ...f,
                        client_id: c.id,
                        details: { ...prefillQuoteDetails(c, f.type), ...(f.details || {}) },
                      }));
                    }}
                    placeholder="Dato obbligatorio · seleziona cliente o compila i Dati personali"
                    testid="quote-client"
                  />
                </div>
                {clientSummary && form.client_id && (
                  <div
                    className="mt-2 rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-xs flex flex-wrap items-center gap-x-4 gap-y-1"
                    data-testid="quote-client-summary"
                  >
                    <span className="font-semibold text-indigo-900">📋 {clientSummary.client?.surname} {clientSummary.client?.name}</span>
                    <span className="text-indigo-700">
                      🛡️ <strong>{clientSummary.active_policies}</strong> polizze attive
                      {clientSummary.total_policies > clientSummary.active_policies && <span className="text-indigo-400"> / {clientSummary.total_policies} totali</span>}
                    </span>
                    <span className="text-indigo-700">
                      📝 <strong>{clientSummary.total_quotes}</strong> preventivi totali
                    </span>
                    {clientSummary.last_quote && (
                      <span className="text-indigo-700">
                        🕐 Ultimo: <span className="font-mono text-[11px]">{clientSummary.last_quote.quote_number || clientSummary.last_quote.type}</span> del {formatDate(clientSummary.last_quote.created_at)}
                      </span>
                    )}
                    {/* 🆕 Pulsante per importare un veicolo già collegato al cliente */}
                    {(form.type === "auto" || form.type === "moto") && (
                      <button
                        type="button"
                        data-testid="import-client-vehicle-btn"
                        onClick={openVehiclesCatalog}
                        className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-indigo-400 bg-white hover:bg-indigo-100 text-indigo-800 shadow-sm transition-colors"
                        title="Scegli un veicolo già collegato al cliente da polizze o preventivi precedenti"
                      >
                        <Car className="h-3.5 w-3.5" /> Importa veicolo cliente
                      </button>
                    )}
                  </div>
                )}
              </div>
              {/* 🆕 Tipo polizza + Situazione assicurativa + Durata contratto affiancati */}
              {((form.type === "auto" || form.type === "moto") && insuranceStatusOpts.length > 0) ? (
                <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <Label>Tipo polizza</Label>
                    <Select value={form.type} onValueChange={(v) => {
                      const c = clients.find((cl) => cl.id === form.client_id);
                      setForm((f) => ({
                        ...f,
                        type: v,
                        details: { ...prefillQuoteDetails(c, v), ...(f.details || {}) },
                      }));
                    }}>
                      <SelectTrigger data-testid="quote-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES_LIVE.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Situazione assicurativa <span className="text-red-500">*</span></Label>
                    <Select
                      value={form.details?.insurance_status || ""}
                      onValueChange={(v) => setForm((f) => ({ ...f, details: { ...(f.details || {}), insurance_status: v } }))}
                    >
                      <SelectTrigger data-testid="quote-insurance-status" className={!form.details?.insurance_status ? "bg-amber-100 border-amber-400 ring-2 ring-amber-300 focus-visible:border-amber-500 shadow-sm" : ""}>
                        <SelectValue placeholder="Seleziona…" />
                      </SelectTrigger>
                      <SelectContent>
                        {insuranceStatusOpts.map((o) => (
                          <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!form.details?.insurance_status && (
                      <p className="text-[10px] text-amber-800 mt-0.5 font-semibold">⚠️ Obbligatorio</p>
                    )}
                  </div>
                  {/* 🆕 Feb 2026 — Attestato di rischio altro veicolo (visibile solo se Situazione Assicurativa ha flag `enables_other_attestato`) */}
                  {bersaniSourceOpts.length > 0 && !!insuranceStatusOpts.find((o) => o.code === form.details?.insurance_status)?.enables_other_attestato && (
                    <div>
                      <Label>Attestato altro veicolo? <span className="text-red-500">*</span></Label>
                      <Select
                        value={form.details?.bersani_source || ""}
                        onValueChange={(v) => setForm((f) => ({ ...f, details: { ...(f.details || {}), bersani_source: v } }))}
                      >
                        <SelectTrigger
                          data-testid="quote-bersani-source"
                          className={!form.details?.bersani_source ? "bg-amber-100 border-amber-400 ring-2 ring-amber-300 focus-visible:border-amber-500 shadow-sm" : ""}
                        >
                          <SelectValue placeholder="Seleziona…" />
                        </SelectTrigger>
                        <SelectContent>
                          {bersaniSourceOpts.map((o) => (
                            <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!form.details?.bersani_source && (
                        <p className="text-[10px] text-amber-800 mt-0.5 font-semibold">⚠️ Obbligatorio</p>
                      )}
                    </div>
                  )}
                  <div>
                    <Label>Durata contratto (anni)</Label>
                    <Input
                      type="number" min="0" step="0.5"
                      data-testid="quote-duration-years"
                      value={form.details?.duration_years ?? 1}
                      onChange={(e) => {
                        const yrs = e.target.value === "" ? "" : Number(e.target.value);
                        let ed = form.details?.end_date || "";
                        const sd = form.details?.start_date;
                        if (sd && typeof yrs === "number" && yrs > 0) {
                          const d = new Date(sd);
                          if (!isNaN(d)) {
                            const whole = Math.floor(yrs);
                            const extra = Math.round((yrs - whole) * 12);
                            d.setFullYear(d.getFullYear() + whole);
                            d.setMonth(d.getMonth() + extra);
                            ed = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                          }
                        }
                        setForm((f) => ({ ...f, details: { ...(f.details || {}), duration_years: yrs, end_date: ed } }));
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <Label>Tipo polizza</Label>
                  <Select value={form.type} onValueChange={(v) => {
                    const c = clients.find((cl) => cl.id === form.client_id);
                    setForm((f) => ({
                      ...f,
                      type: v,
                      details: { ...prefillQuoteDetails(c, v), ...(f.details || {}) },
                    }));
                  }}>
                    <SelectTrigger data-testid="quote-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES_LIVE.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {/* 🆕 Feb 2026 — Mini-form "Dati veicolo di origine" quando `bersani_source` richiede targa altro veicolo */}
              {!!bersaniSourceOpts.find((o) => o.code === form.details?.bersani_source)?.requires_other_plate && (
                <div data-testid="quote-other-vehicle-box" className="col-span-2 rounded-lg border-2 border-sky-300 bg-gradient-to-r from-sky-50/70 to-cyan-50/70 p-4">
                  <div className="flex items-center gap-2 text-sky-800 font-semibold text-sm mb-3">
                    🚘 Dati veicolo di origine
                    <span className="text-[10px] font-normal text-sky-700">({(bersaniSourceOpts.find((o) => o.code === form.details?.bersani_source)?.label) || ""})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div className="sm:col-span-2">
                      <Label>Targa veicolo di origine *</Label>
                      <Input
                        value={form.details?.other_vehicle_plate || ""}
                        onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), other_vehicle_plate: (e.target.value || "").toUpperCase() } }))}
                        placeholder="es. AB123CD" maxLength={10}
                        className="h-9 text-sm font-mono uppercase"
                        data-testid="quote-other-vehicle-plate"
                      />
                    </div>
                    <div>
                      <Label>Marca</Label>
                      <Input value={form.details?.other_vehicle_brand || ""} onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), other_vehicle_brand: e.target.value } }))} placeholder="es. Fiat" className="h-9 text-sm" data-testid="quote-other-vehicle-brand" />
                    </div>
                    <div>
                      <Label>Modello</Label>
                      <Input value={form.details?.other_vehicle_model || ""} onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), other_vehicle_model: e.target.value } }))} placeholder="es. Panda" className="h-9 text-sm" data-testid="quote-other-vehicle-model" />
                    </div>
                    <div>
                      <Label>Anno 1ª immatricolazione</Label>
                      <Input type="date" value={form.details?.other_vehicle_first_registration || ""} onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), other_vehicle_first_registration: e.target.value } }))} className="h-9 text-sm" data-testid="quote-other-vehicle-first-reg" />
                    </div>
                    <div className="sm:col-span-3">
                      <Label>Note aggiuntive</Label>
                      <Input value={form.details?.other_vehicle_notes || ""} onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), other_vehicle_notes: e.target.value } }))} placeholder="es. Data cessione, demolizione, denuncia furto…" className="h-9 text-sm" data-testid="quote-other-vehicle-notes" />
                    </div>
                  </div>
                </div>
              )}
              {/* 🆕 Feb 2026 — Sezione Bersani / RC Familiare: visibile se la voce `bersani_source` ha flag `opens_bersani_section` (fallback: `insurance_status.triggers_bersani`) */}
              {(
                !!bersaniSourceOpts.find((o) => o.code === form.details?.bersani_source)?.opens_bersani_section
                || (!form.details?.bersani_source && !!insuranceStatusOpts.find((o) => o.code === form.details?.insurance_status)?.triggers_bersani)
              ) && (
                <div data-testid="quote-bersani-section" className="col-span-2 rounded-lg border-2 border-emerald-300 bg-gradient-to-r from-emerald-50/70 to-teal-50/70 p-4">
                  <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm mb-3">
                    ✨ Agevolazione: Legge Bersani o RC Familiare
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Tipo di agevolazione</Label>
                      <Select
                        value={form.details?.bersani_type || "_none"}
                        onValueChange={(v) => setForm((f) => ({ ...f, details: { ...(f.details || {}), bersani_type: v === "_none" ? "" : v } }))}
                      >
                        <SelectTrigger data-testid="quote-bersani-type"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">— Seleziona —</SelectItem>
                          <SelectItem value="legge_bersani">Legge Bersani</SelectItem>
                          <SelectItem value="rc_familiare">RC Familiare</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Targa del veicolo di riferimento</Label>
                      <Input
                        data-testid="quote-bersani-ref-plate"
                        value={form.details?.bersani_ref_plate || ""}
                        onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), bersani_ref_plate: (e.target.value || "").toUpperCase() } }))}
                        placeholder="es. AB123CD"
                      />
                    </div>
                    <div>
                      <Label>Compagnia assicurativa del veicolo di riferimento</Label>
                      <Input
                        data-testid="quote-bersani-ref-company"
                        value={form.details?.bersani_ref_company || ""}
                        onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), bersani_ref_company: e.target.value } }))}
                        placeholder="es. Generali, Allianz…"
                      />
                    </div>
                    <div>
                      <Label>Classe di merito del veicolo di riferimento</Label>
                      <Select
                        value={form.details?.bersani_ref_merit_class || "_none"}
                        onValueChange={(v) => setForm((f) => ({ ...f, details: { ...(f.details || {}), bersani_ref_merit_class: v === "_none" ? "" : v } }))}
                      >
                        <SelectTrigger data-testid="quote-bersani-ref-merit-class"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value="_none">— Non specificata —</SelectItem>
                          {["1","2","3","4","5","6","7","8","9","10","11","12","13","14",
                            "CU1","CU2","CU3","CU4","CU5","CU6","CU7","CU8","CU9",
                            "CU10","CU11","CU12","CU13","CU14","CU15","CU16","CU17","CU18"]
                            .map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Intestatario del veicolo di riferimento</Label>
                      <Input
                        data-testid="quote-bersani-ref-owner"
                        value={form.details?.bersani_ref_owner || ""}
                        onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), bersani_ref_owner: e.target.value } }))}
                        placeholder="Nome e cognome dell'intestatario"
                      />
                    </div>
                    <div>
                      <Label>Codice fiscale dell'intestatario di riferimento</Label>
                      <Input
                        data-testid="quote-bersani-ref-owner-cf"
                        value={form.details?.bersani_ref_owner_fiscal_code || ""}
                        onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), bersani_ref_owner_fiscal_code: (e.target.value || "").toUpperCase() } }))}
                        placeholder="es. RSSMRA80A01H501U"
                        maxLength={16}
                      />
                    </div>
                    <div>
                      <Label>Data di nascita dell'intestatario di riferimento</Label>
                      <DatePicker
                        data-testid="quote-bersani-ref-owner-birth-date"
                        value={form.details?.bersani_ref_owner_birth_date || ""}
                        onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), bersani_ref_owner_birth_date: e.target.value } }))}
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* 🆕 Feb 2026 — Classe di merito + Frazionamento affiancati (stessa riga, uguale larghezza).
                  Il campo Classe di merito viene NASCOSTO quando la voce selezionata attiva Bersani (flag `triggers_bersani`). */}
              <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {!insuranceStatusOpts.find((o) => o.code === form.details?.insurance_status)?.triggers_bersani && (
                  <div>
                    <Label>Classe di merito</Label>
                    <Select
                      value={form.details?.merit_class || "_none"}
                      onValueChange={(v) => setForm((f) => ({ ...f, details: { ...(f.details || {}), merit_class: v === "_none" ? "" : v } }))}
                    >
                      <SelectTrigger data-testid="quote-merit-class"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="_none">— Non specificata —</SelectItem>
                        {["1","2","3","4","5","6","7","8","9","10","11","12","13","14",
                          "CU1","CU2","CU3","CU4","CU5","CU6","CU7","CU8","CU9",
                          "CU10","CU11","CU12","CU13","CU14","CU15","CU16","CU17","CU18"]
                          .map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Frazionamento <span className="text-[10px] font-normal text-slate-500">(periodicità rate)</span></Label>
                  <Select
                    value={form.details?.frazionamento || "annuale"}
                    onValueChange={(v) => setForm((f) => ({ ...f, details: { ...(f.details || {}), frazionamento: v } }))}
                  >
                    <SelectTrigger data-testid="quote-frazionamento" className="bg-yellow-50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="annuale">Annuale</SelectItem>
                      <SelectItem value="semestrale">Semestrale</SelectItem>
                      <SelectItem value="quadrimestrale">Quadrimestrale</SelectItem>
                      <SelectItem value="trimestrale">Trimestrale</SelectItem>
                      <SelectItem value="mensile">Mensile</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-0.5">Sincronizzato automaticamente con le offerte collegate.</p>
                </div>
              </div>
              {/* 🆕 Decorrenza + Scadenza affiancati (Scadenza è auto-calcolata da Durata × Decorrenza) */}
              <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Decorrenza</Label>
                  <DatePicker data-testid="quote-start-date"
                    value={form.details?.start_date || ""}
                    onChange={(e) => {
                      const sd = e.target.value;
                      const yrs = Number(form.details?.duration_years ?? 1);
                      let ed = form.details?.end_date || "";
                      if (sd && yrs > 0) {
                        const d = new Date(sd);
                        if (!isNaN(d)) {
                          const whole = Math.floor(yrs);
                          const extra = Math.round((yrs - whole) * 12);
                          d.setFullYear(d.getFullYear() + whole);
                          d.setMonth(d.getMonth() + extra);
                          ed = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                        }
                      }
                      setForm((f) => ({ ...f, details: { ...(f.details || {}), start_date: sd, end_date: ed } }));
                    }} />
                </div>
                <div>
                  <Label>Scadenza <span className="text-[10px] font-normal text-slate-500">(calcolata dalla durata)</span></Label>
                  <DatePicker data-testid="quote-end-date"
                    value={form.details?.end_date || ""}
                    readOnly
                    disabled
                    className="bg-slate-100 cursor-not-allowed"
                    title="campo auto-calcolato" />
                  <p className="text-[10px] text-blue-600 mt-0.5 italic">🔒 campo auto-calcolato</p>
                </div>
              </div>

              {/* 🆕 Card "Durata contratto" + "Prossima rata" */}
              {form.details?.start_date && (
                <div className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(() => {
                    const fmt = (iso) => {
                      if (!iso) return "—";
                      const [y, m, d] = iso.split("-");
                      return `${d}/${m}/${y}`;
                    };
                    const monthsBetween = (s, e) => {
                      if (!s || !e) return null;
                      const sd = new Date(s), ed = new Date(e);
                      if (isNaN(sd) || isNaN(ed) || ed < sd) return null;
                      let months = (ed.getFullYear() - sd.getFullYear()) * 12 + (ed.getMonth() - sd.getMonth());
                      if (ed.getDate() < sd.getDate()) months -= 1;
                      // 🆕 28/07/2026 — Scadenza = anniversario esatto (no −1 giorno): giorni = differenza netta.
                      const dayDiff = Math.round((ed - sd) / (1000 * 60 * 60 * 24));
                      return { months, days: dayDiff };
                    };
                    const sd = form.details.start_date, ed = form.details.end_date;
                    const durationLabel = (() => {
                      const r = monthsBetween(sd, ed);
                      if (!r) return ed ? "—" : "Imposta la scadenza per calcolare la durata";
                      const { months, days } = r;
                      // 🆕 usa duration_years esplicita se intera
                      const yrs = Number(form.details?.duration_years);
                      if (yrs && yrs > 0 && Number.isInteger(yrs)) {
                        return `${yrs === 1 ? "Annuale" : `${yrs} anni`} (${days} giorni)`;
                      }
                      if (months >= 12 && months % 12 === 0) {
                        const y = months / 12;
                        return `${y === 1 ? "Annuale" : `${y} anni`} (${days} giorni)`;
                      }
                      if (months >= 1) return `${months} mes${months === 1 ? "e" : "i"} (${days} giorni)`;
                      return `${days} giorn${days === 1 ? "o" : "i"}`;
                    })();
                    const FRAZ_MONTHS = { annuale: 12, semestrale: 6, quadrimestrale: 4, trimestrale: 3, mensile: 1 };
                    const fr = form.details?.frazionamento || "annuale";
                    const step = FRAZ_MONTHS[fr] || 12;
                    const nextInstallment = (() => {
                      const d = new Date(sd);
                      if (isNaN(d)) return null;
                      d.setMonth(d.getMonth() + step);
                      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    })();
                    const frLabel = { annuale: "Annuale", semestrale: "Semestrale", quadrimestrale: "Quadrimestrale", trimestrale: "Trimestrale", mensile: "Mensile" }[fr] || fr;
                    return (
                      <>
                        <div data-testid="quote-duration-card" className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-3">
                          <div className="text-[11px] uppercase tracking-wide font-semibold text-indigo-700 mb-1">📋 Durata contratto</div>
                          <div className="text-lg font-bold text-indigo-900">{durationLabel}</div>
                          <div className="text-[11px] text-indigo-700/80 mt-1">Calcolata da Decorrenza → Scadenza</div>
                        </div>
                        <div data-testid="quote-installment-card" className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3">
                          <div className="text-[11px] uppercase tracking-wide font-semibold text-emerald-700 mb-1">💶 Prossima rata ({frLabel})</div>
                          <div className="text-sm text-emerald-900 flex items-center gap-2 flex-wrap">
                            <span><span className="text-emerald-700/80">Decorrenza:</span> <strong>{fmt(sd)}</strong></span>
                            <span className="text-emerald-400">→</span>
                            <span><span className="text-emerald-700/80">Scadenza prossima rata:</span> <strong>{fmt(nextInstallment)}</strong></span>
                          </div>
                          <div className="text-[11px] text-emerald-700/80 mt-1">Frazionamento: +{step} mes{step === 1 ? "e" : "i"} dalla decorrenza</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
              <fieldset className="rounded-lg border border-blue-200 bg-blue-50/40" data-testid="personal-fieldset">
                <legend className="px-2 mx-3 text-[11px] font-semibold uppercase tracking-wider text-blue-700 bg-white rounded">
                  👤 Dati personali
                </legend>
                <button
                  type="button"
                  onClick={() => setPersonalCollapsed((v) => !v)}
                  data-testid="personal-toggle"
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-blue-100/50 rounded-md transition-colors"
                  title={personalCollapsed ? "Espandi dati personali" : "Comprimi dati personali"}
                >
                  <span className="text-xs text-blue-800 font-medium">
                    {personalCollapsed ? (() => {
                      const sn = (form.details?.owner_surname || "").toUpperCase();
                      const nm = (form.details?.owner_name || "").toUpperCase();
                      const who = `${sn} ${nm}`.trim();
                      return `▸ Mostra dati personali${who ? ` — ${who}` : ""}`;
                    })() : "▾ Nascondi dati personali"}
                  </span>
                  {personalCollapsed ? <ChevronDown className="h-4 w-4 text-blue-700" /> : <ChevronUp className="h-4 w-4 text-blue-700" />}
                </button>
                {!personalCollapsed && (
                  <div className="px-3 pb-3">
                <p className="text-[11px] text-slate-500 mb-3">
                  I dati estratti dai documenti (libretto, carta d&apos;identità, vecchia polizza) verranno inseriti qui automaticamente. Puoi modificarli prima di salvare.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="quote-personal-section">
                  {/* 🆕 Feb 2026 — Selettore Persona Fisica / Azienda (in cima al form) */}
                  <div className="col-span-2 flex flex-col sm:flex-row gap-2 sm:items-center bg-slate-50 border border-slate-200 rounded-md p-2.5">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 sm:min-w-[130px]">Tipo Contraente</Label>
                    <div className="flex gap-2 flex-1" role="radiogroup" aria-label="Tipo contraente">
                      <button
                        type="button"
                        data-testid="qp-subject-type-private"
                        role="radio"
                        aria-checked={form.details?.subject_type !== "company"}
                        onClick={() => setForm((f) => ({ ...f, details: { ...(f.details || {}), subject_type: "private" } }))}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition ${(form.details?.subject_type || "private") !== "company"
                          ? "bg-blue-600 text-white border border-blue-700 shadow-sm"
                          : "bg-white text-slate-700 border border-slate-300 hover:bg-blue-50"}`}
                      >
                        👤 Persona Fisica
                      </button>
                      <button
                        type="button"
                        data-testid="qp-subject-type-company"
                        role="radio"
                        aria-checked={form.details?.subject_type === "company"}
                        onClick={() => setForm((f) => ({ ...f, details: { ...(f.details || {}), subject_type: "company" } }))}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition ${form.details?.subject_type === "company"
                          ? "bg-purple-600 text-white border border-purple-700 shadow-sm"
                          : "bg-white text-slate-700 border border-slate-300 hover:bg-purple-50"}`}
                      >
                        🏢 Azienda
                      </button>
                    </div>
                  </div>
                  {/* 🆕 Feb 2026 — Riga 1: Cognome/Nome oppure Ragione Sociale/P.IVA (Azienda) */}
                  {form.details?.subject_type === "company" ? (
                    <>
                      <div className="col-span-2">
                        <Label>Ragione Sociale</Label>
                        <div className="flex gap-1.5">
                          <Input data-testid="qp-ragione-sociale" value={form.details?.owner_ragione_sociale || ""} className="uppercase flex-1"
                            onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_ragione_sociale: e.target.value.toUpperCase() } }))} />
                          <CopyButton testid="qp-ragione-sociale-copy" label="Ragione Sociale" value={form.details?.owner_ragione_sociale} />
                        </div>
                      </div>
                      <div>
                        <Label>Partita IVA</Label>
                        <div className="flex gap-1.5">
                          <Input data-testid="qp-partita-iva" value={form.details?.owner_partita_iva || ""} maxLength={16} className="font-mono uppercase flex-1"
                            onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_partita_iva: e.target.value.toUpperCase() } }))} />
                          <CopyButton testid="qp-partita-iva-copy" label="P.IVA" value={form.details?.owner_partita_iva} />
                        </div>
                      </div>
                      <div>
                        <Label>PEC</Label>
                        <Input data-testid="qp-pec-email" type="email" value={form.details?.pec_email || ""}
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), pec_email: e.target.value } }))} />
                      </div>
                      <div className="col-span-2">
                        <Label>Codice SDI</Label>
                        <Input data-testid="qp-sdi-code" value={form.details?.sdi_code || ""} maxLength={7} className="font-mono uppercase"
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), sdi_code: e.target.value.toUpperCase() } }))} />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <Label>Cognome</Label>
                        <div className="flex gap-1.5">
                          <Input data-testid="qp-surname" value={form.details?.owner_surname || ""} className="uppercase flex-1"
                            onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_surname: e.target.value.toUpperCase() } }))} />
                          <CopyButton testid="qp-surname-copy" label="Cognome" value={form.details?.owner_surname} />
                        </div>
                      </div>
                      <div>
                        <Label>Nome</Label>
                        <div className="flex gap-1.5">
                          <Input data-testid="qp-name" value={form.details?.owner_name || ""} className="uppercase flex-1"
                            onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_name: e.target.value.toUpperCase() } }))} />
                          <CopyButton testid="qp-name-copy" label="Nome" value={form.details?.owner_name} />
                        </div>
                      </div>
                    </>
                  )}
                  {/* Riga 2: Sesso | Data di nascita — solo per Persona Fisica */}
                  {form.details?.subject_type !== "company" && (<>
                  <div>
                    <Label>Sesso</Label>
                    <select
                      data-testid="qp-gender"
                      value={form.details?.owner_gender || ""}
                      onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_gender: e.target.value } }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                    >
                      <option value="">—</option>
                      <option value="M">Maschio</option>
                      <option value="F">Femmina</option>
                    </select>
                  </div>
                  <div>
                    <Label>Data di nascita</Label>
                    <DatePicker data-testid="qp-birth-date" value={form.details?.owner_birth_date || ""}
                      onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_birth_date: e.target.value } }))} />
                  </div>
                  {/* 🆕 Feb 2026 — Riga 2b: Stato civile | Professione (da liste preventivo) */}
                  <div>
                    <Label>💍 Stato civile</Label>
                    <select
                      data-testid="qp-marital-status"
                      value={form.details?.owner_marital_status || form.details?.marital_status || ""}
                      onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_marital_status: e.target.value, marital_status: e.target.value } }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                    >
                      <option value="">— Non specificato —</option>
                      {maritalOpts.map((m) => (
                        <option key={m.code} value={m.code}>{m.label}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 mt-0.5">Voci gestibili in <em>Liste preventivo → Stato civile</em>.</p>
                  </div>
                  <div>
                    <Label>💼 Professione</Label>
                    <select
                      data-testid="qp-profession"
                      value={form.details?.profession || ""}
                      onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), profession: e.target.value } }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                    >
                      <option value="">— Non specificato —</option>
                      {professionOpts.map((p) => (
                        <option key={p.code} value={p.code}>{p.label}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 mt-0.5">Voci gestibili in <em>Liste preventivo → Professione</em>.</p>
                  </div>
                  {/* Riga 3: Luogo di nascita (autocomplete) */}
                  <div className="col-span-2">
                    <Label>Luogo di nascita</Label>
                    <CityAutocomplete
                      testid="qp-birth-place"
                      value={form.details?.owner_birth_place || ""}
                      onChange={(v) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_birth_place: (v || "").toUpperCase() } }))}
                      inputClassName="uppercase"
                    />
                  </div>
                  {/* Riga 4: Codice fiscale (full width) + Calcola */}
                  <div className="col-span-2">
                    <Label className="flex items-center gap-2">Codice fiscale
                      <span className="text-xs text-slate-400 normal-case">(auto-calcolato)</span>
                    </Label>
                    <div className="flex gap-2 items-stretch">
                      <Input data-testid="qp-cf" maxLength={16} className="font-mono uppercase flex-1"
                        value={form.details?.owner_fiscal_code || ""}
                        onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_fiscal_code: e.target.value.toUpperCase() } }))} />
                      <CopyButton testid="qp-cf-copy" label="Codice Fiscale" value={form.details?.owner_fiscal_code} />
                      <button
                        type="button"
                        data-testid="qp-calc-cf"
                        onClick={async () => {
                          const d = form.details || {};
                          const missing = [];
                          if (!d.owner_name) missing.push("Nome");
                          if (!d.owner_surname) missing.push("Cognome");
                          if (!d.owner_gender) missing.push("Sesso");
                          if (!d.owner_birth_date) missing.push("Data di nascita");
                          if (!d.owner_birth_place) missing.push("Luogo di nascita");
                          if (missing.length) { toast.error(`Per calcolare il CF compila: ${missing.join(", ")}`); return; }
                          try {
                            const { data: r } = await api.post("/utils/fiscal-code", {
                              name: d.owner_name, surname: d.owner_surname,
                              gender: d.owner_gender, birth_date: d.owner_birth_date,
                              birth_place: d.owner_birth_place,
                            });
                            if (r?.fiscal_code) {
                              const cf = r.fiscal_code;
                              setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_fiscal_code: cf } }));
                              const existing = (clients || []).find((c) => (c.fiscal_code || "").toUpperCase() === cf.toUpperCase());
                              if (existing && existing.id !== form.client_id) {
                                if (window.confirm(`CF calcolato: ${cf}\n\nIl cliente "${existing.surname} ${existing.name}" è già presente in anagrafica con questo CF.\n\nVuoi associarlo a questo preventivo?`)) {
                                  setForm((f) => ({
                                    ...f,
                                    client_id: existing.id,
                                    details: { ...prefillQuoteDetails(existing, f.type), ...(f.details || {}), owner_fiscal_code: cf },
                                  }));
                                  toast.success(`Cliente "${existing.surname} ${existing.name}" associato al preventivo`);
                                } else {
                                  toast.success(`CF calcolato: ${cf}`);
                                }
                              } else {
                                toast.success(`CF calcolato: ${cf}`);
                              }
                            } else toast.error("Calcolo CF non riuscito");
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 rounded-md whitespace-nowrap"
                        title="Calcola codice fiscale dai dati anagrafici"
                      >🧮 Calcola</button>
                    </div>
                    <p className="text-[10px] text-blue-600 mt-1 italic">🔒 campo auto-calcolato — Servono: Nome, Cognome, Sesso, Data e Luogo di nascita.</p>
                  </div>
                  </>)}
                  <div className="col-span-2">
                    <Label>{form.details?.subject_type === "company" ? "Sede legale (via) · N. civico" : "Indirizzo (via) · N. civico"}</Label>
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-9">
                        <Input data-testid="qp-address" value={form.details?.owner_address || ""} className="uppercase"
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_address: e.target.value.toUpperCase() } }))} />
                      </div>
                      <div className="col-span-3">
                        <Input data-testid="qp-house-number" value={form.details?.owner_house_number || ""} className="uppercase"
                          placeholder="Civico"
                          maxLength={10}
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_house_number: e.target.value.toUpperCase() } }))} />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Città</Label>
                    <CityAutocomplete
                      testid="qp-city"
                      value={form.details?.owner_city || ""}
                      onChange={(v) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_city: (v || "").toUpperCase() } }))}
                      onSelect={(c) => {
                        setForm((f) => ({ ...f, details: {
                          ...(f.details || {}),
                          owner_city: (c.name || "").toUpperCase(),
                          owner_province: (c.province || "").toUpperCase(),
                          owner_cap: c.cap || (f.details?.owner_cap || ""),
                        } }));
                      }}
                      inputClassName="uppercase"
                    />
                  </div>
                  <div>
                    <Label>CAP</Label>
                    <Input data-testid="qp-cap" maxLength={5} value={form.details?.owner_cap || ""}
                      onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_cap: e.target.value.replace(/\D/g, "").slice(0, 5) } }))} />
                    <p className="text-[10px] text-blue-600 mt-0.5 italic">🔒 auto-calcolato dalla città</p>
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input data-testid="qp-province" maxLength={2} className="uppercase" value={form.details?.owner_province || ""}
                      onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_province: e.target.value.toUpperCase().slice(0, 2) } }))} />
                    <p className="text-[10px] text-blue-600 mt-0.5 italic">🔒 auto-calcolato dalla città</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <Label>Email {!form.details?.owner_email_unavailable && <span className="text-rose-500">*</span>}</Label>
                      <label className="inline-flex items-center gap-1.5 text-[10px] text-slate-600 cursor-pointer select-none" title="Spunta se il proprietario non ha un indirizzo email">
                        <input
                          type="checkbox"
                          data-testid="qp-email-unavailable"
                          checked={!!form.details?.owner_email_unavailable}
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_email_unavailable: e.target.checked } }))}
                          className="h-3 w-3"
                        />
                        Non disponibile
                      </label>
                    </div>
                    <Input
                      data-testid="qp-email"
                      type="email"
                      value={form.details?.owner_email || ""}
                      onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_email: e.target.value } }))}
                      className={!form.details?.owner_email_unavailable && !(form.details?.owner_email || "").trim() ? "bg-rose-50/40 border-rose-200 focus-visible:border-rose-400" : ""}
                    />
                    {!form.details?.owner_email_unavailable && <p className="text-[10px] text-rose-600 mt-0.5 italic">⚠️ campo obbligatorio</p>}
                  </div>
                  <div>
                    <Label>Cellulare / Telefono</Label>
                    <MobileInputWithWA
                      value={form.details?.owner_phone || ""}
                      onChange={(v) => setForm((f) => ({ ...f, details: { ...(f.details || {}), owner_phone: v } }))}
                      testid="qp-phone"
                      clientName={`${form.details?.owner_name || ""} ${form.details?.owner_surname || ""}`.trim()}
                    />
                  </div>
                </div>
                {/* 🆕 Feb 2026 — Legale Rappresentante (visibile solo per aziende) */}
                {form.details?.subject_type === "company" && (
                  <CollapsiblePanel
                    title="Legale Rappresentante"
                    emoji="👤"
                    subtitle="Dati anagrafici del rappresentante legale dell'azienda"
                    testid="qp-legale-rappresentante"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label>Cognome</Label>
                        <Input data-testid="qp-lr-surname" className="uppercase"
                          value={form.details?.legale_rappresentante_surname || ""}
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), legale_rappresentante_surname: e.target.value.toUpperCase() } }))} />
                      </div>
                      <div>
                        <Label>Nome</Label>
                        <Input data-testid="qp-lr-name" className="uppercase"
                          value={form.details?.legale_rappresentante_name || ""}
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), legale_rappresentante_name: e.target.value.toUpperCase() } }))} />
                      </div>
                      <div className="col-span-2">
                        <Label>Codice Fiscale</Label>
                        <Input data-testid="qp-lr-cf" maxLength={16} className="font-mono uppercase"
                          value={form.details?.legale_rappresentante_fiscal_code || ""}
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), legale_rappresentante_fiscal_code: e.target.value.toUpperCase() } }))} />
                      </div>
                      <div>
                        <Label>Sesso</Label>
                        <select
                          data-testid="qp-lr-gender"
                          value={form.details?.legale_rappresentante_gender || ""}
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), legale_rappresentante_gender: e.target.value } }))}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                        >
                          <option value="">—</option>
                          <option value="M">Maschio</option>
                          <option value="F">Femmina</option>
                        </select>
                      </div>
                      <div>
                        <Label>Data di nascita</Label>
                        <DatePicker data-testid="qp-lr-birth-date"
                          value={form.details?.legale_rappresentante_birth_date || ""}
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), legale_rappresentante_birth_date: e.target.value } }))} />
                      </div>
                      <div>
                        <Label>Luogo di nascita</Label>
                        <CityAutocomplete
                          testid="qp-lr-birth-place"
                          value={form.details?.legale_rappresentante_birth_place || ""}
                          onChange={(v) => setForm((f) => ({ ...f, details: { ...(f.details || {}), legale_rappresentante_birth_place: (v || "").toUpperCase() } }))}
                          onSelect={(c) => setForm((f) => ({ ...f, details: {
                            ...(f.details || {}),
                            legale_rappresentante_birth_place: (c.name || "").toUpperCase(),
                            legale_rappresentante_birth_province: (c.province || "").toUpperCase(),
                          }}))}
                          inputClassName="uppercase"
                        />
                      </div>
                      <div>
                        <Label>Provincia di nascita</Label>
                        <Input data-testid="qp-lr-birth-province" maxLength={2} className="uppercase"
                          value={form.details?.legale_rappresentante_birth_province || ""}
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), legale_rappresentante_birth_province: e.target.value.toUpperCase() } }))} />
                      </div>
                      <div className="col-span-2">
                        <Label>Ruolo / Carica</Label>
                        <Input data-testid="qp-lr-role" placeholder="es. Amministratore Unico, Presidente CdA, Procuratore"
                          value={form.details?.legale_rappresentante_role || ""}
                          onChange={(e) => setForm((f) => ({ ...f, details: { ...(f.details || {}), legale_rappresentante_role: e.target.value } }))} />
                      </div>
                    </div>
                  </CollapsiblePanel>
                )}
                  </div>
                )}
              </fieldset>
              {/* 🆕 Pannello "sandwich" Cointestatari (visibile solo se ci sono co_owners salvati) */}
              {Array.isArray(form.details?.co_owners) && form.details.co_owners.length > 0 && (
                <fieldset className="rounded-lg border border-violet-200 bg-violet-50/40" data-testid="co-owners-panel">
                  <legend className="px-2 mx-3 text-[11px] font-semibold uppercase tracking-wider text-violet-700 bg-white rounded">
                    👥 Cointestatari ({form.details.co_owners.length})
                  </legend>
                  <button
                    type="button"
                    onClick={() => setCoOwnersCollapsed((v) => !v)}
                    data-testid="co-owners-toggle"
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-violet-100/50 rounded-md transition-colors"
                    title={coOwnersCollapsed ? "Espandi cointestatari" : "Comprimi cointestatari"}
                  >
                    <span className="text-xs text-violet-800 font-medium">
                      {coOwnersCollapsed
                        ? `Mostra ${form.details.co_owners.length} cointestatario/i del veicolo`
                        : `Nascondi cointestatari`}
                    </span>
                    {coOwnersCollapsed ? <ChevronDown className="h-4 w-4 text-violet-700" /> : <ChevronUp className="h-4 w-4 text-violet-700" />}
                  </button>
                  {!coOwnersCollapsed && (
                    <div className="px-3 pb-3 space-y-3" data-testid="co-owners-list">
                      {form.details.co_owners.map((co, idx) => (
                        <div key={idx} className="rounded-md border border-violet-300 bg-white p-3 space-y-2" data-testid={`co-owner-${idx}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] uppercase tracking-wide text-violet-700 font-semibold">
                                Cointestatario {idx + 1}
                              </div>
                              <div className="font-semibold uppercase">
                                {(co.surname || "—")} {(co.name || "")}
                              </div>
                              <div className="text-[11px] font-mono text-slate-500">{co.fiscal_code || "(CF non disponibile)"}</div>
                            </div>
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                data-testid={`co-owner-promote-${idx}`}
                                onClick={() => {
                                  // Promuovi questo cointestatario a intestatario principale: scambia i dati
                                  setForm((f) => {
                                    const det = f.details || {};
                                    const currentOwner = {
                                      name: det.owner_name || "",
                                      surname: det.owner_surname || "",
                                      fiscal_code: det.owner_fiscal_code || "",
                                      birth_date: det.owner_birth_date || "",
                                      birth_place: det.owner_birth_place || "",
                                      address: det.owner_address || "",
                                      house_number: det.owner_house_number || "",
                                      city: det.owner_city || "",
                                      province: det.owner_province || "",
                                      cap: det.owner_cap || "",
                                    };
                                    const otherCoOwners = (det.co_owners || []).filter((_, j) => j !== idx);
                                    return {
                                      ...f,
                                      details: {
                                        ...det,
                                        owner_name: co.name || "",
                                        owner_surname: co.surname || "",
                                        owner_fiscal_code: co.fiscal_code || "",
                                        owner_birth_date: co.birth_date || "",
                                        owner_birth_place: co.birth_place || "",
                                        owner_address: co.address || "",
                                        owner_house_number: co.house_number || "",
                                        owner_city: co.city || "",
                                        owner_province: co.province || "",
                                        owner_cap: co.cap || "",
                                        co_owners: [currentOwner, ...otherCoOwners],
                                      },
                                    };
                                  });
                                  toast.success(`${co.surname || ""} ${co.name || ""} promosso a intestatario principale`);
                                }}
                                className="text-[10px] font-semibold px-2 py-1 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                title="Imposta come intestatario principale del preventivo"
                              >
                                ⇅ Intesta a costui
                              </button>
                              <button
                                type="button"
                                data-testid={`co-owner-remove-${idx}`}
                                onClick={() => {
                                  setForm((f) => ({
                                    ...f,
                                    details: {
                                      ...(f.details || {}),
                                      co_owners: (f.details?.co_owners || []).filter((_, j) => j !== idx),
                                    },
                                  }));
                                  toast.info("Cointestatario rimosso");
                                }}
                                className="text-[10px] font-semibold px-2 py-1 rounded border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                title="Rimuovi questo cointestatario dalla scheda"
                              >
                                <Trash2 className="h-3 w-3 inline" /> Rimuovi
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                            <div>
                              <Label className="text-[10px]">Nato il</Label>
                              <DatePicker className="h-8 text-xs"
                                value={co.birth_date || ""}
                                onChange={(e) => setForm((f) => ({
                                  ...f,
                                  details: {
                                    ...(f.details || {}),
                                    co_owners: (f.details?.co_owners || []).map((x, j) => j === idx ? { ...x, birth_date: e.target.value } : x),
                                  },
                                }))} />
                            </div>
                            <div>
                              <Label className="text-[10px]">Luogo</Label>
                              <Input
                                className="h-8 text-xs uppercase"
                                value={co.birth_place || ""}
                                onChange={(e) => setForm((f) => ({
                                  ...f,
                                  details: {
                                    ...(f.details || {}),
                                    co_owners: (f.details?.co_owners || []).map((x, j) => j === idx ? { ...x, birth_place: e.target.value.toUpperCase() } : x),
                                  },
                                }))}
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <Label className="text-[10px]">Indirizzo</Label>
                              <Input
                                className="h-8 text-xs uppercase"
                                value={`${co.address || ""} ${co.house_number || ""}`.trim()}
                                onChange={(e) => {
                                  const txt = (e.target.value || "").toUpperCase();
                                  setForm((f) => ({
                                    ...f,
                                    details: {
                                      ...(f.details || {}),
                                      co_owners: (f.details?.co_owners || []).map((x, j) => j === idx ? { ...x, address: txt, house_number: "" } : x),
                                    },
                                  }));
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px]">Città</Label>
                              <Input
                                className="h-8 text-xs uppercase"
                                value={co.city || ""}
                                onChange={(e) => setForm((f) => ({
                                  ...f,
                                  details: {
                                    ...(f.details || {}),
                                    co_owners: (f.details?.co_owners || []).map((x, j) => j === idx ? { ...x, city: e.target.value.toUpperCase() } : x),
                                  },
                                }))}
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-[10px]">Prov.</Label>
                                <Input
                                  className="h-8 text-xs uppercase"
                                  maxLength={2}
                                  value={co.province || ""}
                                  onChange={(e) => setForm((f) => ({
                                    ...f,
                                    details: {
                                      ...(f.details || {}),
                                      co_owners: (f.details?.co_owners || []).map((x, j) => j === idx ? { ...x, province: e.target.value.toUpperCase().slice(0, 2) } : x),
                                    },
                                  }))}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px]">CAP</Label>
                                <Input
                                  className="h-8 text-xs"
                                  maxLength={5}
                                  value={co.cap || ""}
                                  onChange={(e) => setForm((f) => ({
                                    ...f,
                                    details: {
                                      ...(f.details || {}),
                                      co_owners: (f.details?.co_owners || []).map((x, j) => j === idx ? { ...x, cap: e.target.value.replace(/\D/g, "").slice(0, 5) } : x),
                                    },
                                  }))}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </fieldset>
              )}
              <fieldset className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
                <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 bg-white rounded">
                  📋 Dati specifici {TYPES_LIVE.find((t) => t.v === form.type)?.l}
                </legend>
                <QuoteDetailsForm
                  type={form.type}
                  showDrivingTypeWarning
                  details={form.details}
                  onChange={(newDetails) => setForm((f) => ({ ...f, details: newDetails }))}
                  onAddFile={(file) => {
                    if (file) setPendingFiles((arr) => [...arr, file]);
                  }}
                />
              </fieldset>
              <fieldset className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-3">
                <legend className="px-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700 bg-white rounded">
                  🛡️ Coperture & Premio
                </legend>
                <div>
                  <Label>Garanzie richieste <span className="text-[10px] font-normal text-slate-500">(selezionale in base alle scelte del cliente)</span></Label>
                  <CoveragePicker
                    testid="quote-coverages"
                    policyType={form.type}
                    value={form.details?.selected_coverages || []}
                    onChange={(codes, labels) => {
                      setForm((f) => {
                        const composedText = labels.join(" · ");
                        // Aggiorna sia i codici (per analytics/futuro) sia il testo libero (compatibilità email/PDF)
                        const prevAuto = f.details?._coverages_auto_text || "";
                        const currentText = (f.coverage_request || "").trim();
                        // Se il testo attuale è vuoto o uguale alla precedente auto-composizione, lo sovrascrive.
                        // Altrimenti lascia il testo manuale digitato dall'operatore.
                        const nextText = (!currentText || currentText === prevAuto) ? composedText : currentText;
                        return {
                          ...f,
                          details: { ...(f.details || {}), selected_coverages: codes, _coverages_auto_text: composedText },
                          coverage_request: nextText,
                        };
                      });
                    }}
                  />
                </div>
                {/* 🆕 Feb 2026 — Garanzie richieste dal Wizard preventivo (con massimale/franchigia/note) */}
                {Array.isArray(form.details?.requested_coverages) && form.details.requested_coverages.length > 0 && (
                  <div data-testid="quote-requested-coverages">
                    <Label>🛡️ Garanzie da quotare (dal Wizard) <span className="text-[10px] font-normal text-slate-500">(inseriti in fase di creazione preventivo)</span></Label>
                    <div className="space-y-1.5 mt-1.5">
                      {form.details.requested_coverages.map((rc, idx) => (
                        <div key={idx} className="bg-violet-50 border border-violet-200 rounded p-2.5" data-testid={`quote-requested-cov-${idx}`}>
                          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                            <div className="sm:col-span-2">
                              <Label className="text-[10px] uppercase tracking-wide text-violet-700">Garanzia</Label>
                              <Input
                                data-testid={`quote-req-cov-name-${idx}`}
                                value={rc?.name || ""}
                                onChange={(e) => setForm((f) => ({
                                  ...f,
                                  details: {
                                    ...(f.details || {}),
                                    requested_coverages: (f.details?.requested_coverages || []).map((x, j) => j === idx ? { ...x, name: e.target.value } : x),
                                  },
                                }))}
                                className="h-8 text-sm bg-white"
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-wide text-violet-700">Massimale (€)</Label>
                              <Input
                                data-testid={`quote-req-cov-sum-${idx}`}
                                type="number"
                                value={rc?.sum_insured ?? ""}
                                onChange={(e) => setForm((f) => ({
                                  ...f,
                                  details: {
                                    ...(f.details || {}),
                                    requested_coverages: (f.details?.requested_coverages || []).map((x, j) => j === idx ? { ...x, sum_insured: e.target.value === "" ? null : Number(e.target.value) } : x),
                                  },
                                }))}
                                className="h-8 text-sm bg-white"
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-wide text-violet-700">Franchigia</Label>
                              <Input
                                data-testid={`quote-req-cov-ded-${idx}`}
                                value={rc?.deductible || ""}
                                onChange={(e) => setForm((f) => ({
                                  ...f,
                                  details: {
                                    ...(f.details || {}),
                                    requested_coverages: (f.details?.requested_coverages || []).map((x, j) => j === idx ? { ...x, deductible: e.target.value } : x),
                                  },
                                }))}
                                className="h-8 text-sm bg-white"
                              />
                            </div>
                          </div>
                          {rc?.notes && (
                            <p className="text-[11px] text-violet-700 mt-1.5"><strong>Note:</strong> {rc.notes}</p>
                          )}
                          <div className="flex justify-end mt-1.5">
                            <button
                              type="button"
                              data-testid={`quote-req-cov-remove-${idx}`}
                              onClick={() => setForm((f) => ({
                                ...f,
                                details: {
                                  ...(f.details || {}),
                                  requested_coverages: (f.details?.requested_coverages || []).filter((_, j) => j !== idx),
                                },
                              }))}
                              className="text-[10px] text-rose-600 hover:underline"
                            >Rimuovi</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label>Richiesta copertura <span className="text-red-500">*</span> <span className="text-[10px] font-normal text-slate-500">(testo libero — sintesi finale per email/PDF)</span></Label>
                  <Textarea data-testid="quote-coverage" value={form.coverage_request} onChange={(e) => setForm({ ...form, coverage_request: e.target.value })} rows={3} placeholder="Dato obbligatorio · es. RCA + furto/incendio per Fiat Panda 2020, uso urbano…" className="bg-yellow-50" />
                </div>
                <div>
                  <Label>Premio stimato (EUR)</Label>
                  <MoneyInput data-testid="quote-premium" value={form.estimated_premium} onChange={(v) => setForm({ ...form, estimated_premium: v })} />
                </div>
                {/* 🆕 Note personali — uso interno agenzia: NON vengono mai inviate a cliente/partner */}
                <div>
                  <Label className="flex items-center gap-1.5">
                    <span className="text-base leading-none">🔒</span>
                    Note personali <span className="text-[10px] font-normal text-slate-500">(uso interno agenzia — non incluse in email, PDF, portali)</span>
                  </Label>
                  <Textarea
                    data-testid="quote-internal-notes"
                    value={form.internal_notes || ""}
                    onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                    rows={3}
                    placeholder="Es. Cliente chiede sconto extra, contattare martedì, dato bancario da verificare…"
                    className="bg-amber-50/60 border-amber-200 focus-visible:ring-amber-400"
                  />
                </div>
              </fieldset>

              {/* 🆕 Sezione Comunicazioni: timeline cronologica email in ingresso + invii a partner */}
              {editing && (
                <QuoteCommunications quoteId={editing} />
              )}
            </div>
            <DialogFooter>
              {editing && (
                <span className="text-[11px] text-slate-500 mr-auto inline-flex items-center gap-1" data-testid="quote-autosave-indicator">
                  {quoteAutoSaveAt
                    ? <>💾 Bozza salvata automaticamente alle {quoteAutoSaveAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</>
                    : <>💾 Autosave attivo (ogni 30s)</>}
                </span>
              )}
              {(() => {
                const missing = [];
                if (!form.coverage_request) missing.push("Richiesta copertura");
                if (!form.client_id && !(form.details?.owner_name && form.details?.owner_surname)) {
                  missing.push("Cliente (oppure Nome + Cognome nei Dati personali)");
                }
                if (missing.length === 0) return null;
                return (
                  <div data-testid="quote-missing-fields" className="w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 mr-auto">
                    <div className="font-semibold mb-0.5">⚠️ Per salvare il preventivo compila:</div>
                    <ul className="list-disc list-inside space-y-0.5">
                      {missing.map((m) => <li key={m}>{m}</li>)}
                    </ul>
                  </div>
                );
              })()}
              <Button variant="outline" onClick={() => closeDialog()}>Annulla</Button>
              {editing && (
                <Button
                  data-testid="download-quote-pdf-dialog-btn"
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const q = items.find((it) => it.id === editing);
                    if (q) downloadQuoteRequestPdf(q);
                  }}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  title="Scarica il modulo richiesta preventivo in PDF"
                >
                  <Download className="h-4 w-4 mr-1.5" /> Scarica PDF
                </Button>
              )}
              <Button
                data-testid="save-quote-btn"
                disabled={!form.coverage_request || (!form.client_id && !(form.details?.owner_name && form.details?.owner_surname))}
                onClick={submit}
                className="bg-[#0F172A] hover:bg-[#1E293B]"
              >
                {editing ? "Salva modifiche" : "Crea"}{pendingFiles.length > 0 ? ` + ${pendingFiles.length} allegat${pendingFiles.length === 1 ? "o" : "i"}` : ""}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      {/* Source filter */}
      <div className="flex items-center gap-2 flex-wrap print:hidden">
        {/* 🆕 Campo ricerca preventivi */}
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Input
            data-testid="quotes-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per n° preventivo, cliente, targa, marca/modello…"
            className="pl-9 pr-9 text-sm h-9"
          />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" /></svg>
          {search && (
            <button
              type="button"
              data-testid="quotes-search-clear"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 inline-flex items-center justify-center rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 hover:text-slate-800 transition-colors"
              title="Cancella ricerca"
              aria-label="Cancella ricerca"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          )}
        </div>
        <span className="text-xs uppercase tracking-wide text-slate-500 mr-1">Origine:</span>
        {(() => {
          // Calcola "nuovi oggi" per origine
          const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
          const isToday = (iso) => {
            if (!iso) return false;
            const d = new Date(iso);
            return d.getTime() >= todayStart.getTime();
          };
          const counts = {
            all: items.length,
            public: items.filter((q) => q.from_public === true).length,
            portal: items.filter((q) => q.from_portal === true && q.from_public !== true).length,
            crm: items.filter((q) => !q.from_portal && !q.from_public).length,
          };
          const todayCounts = {
            all: items.filter((q) => isToday(q.created_at)).length,
            public: items.filter((q) => q.from_public === true && isToday(q.created_at)).length,
            portal: items.filter((q) => q.from_portal === true && q.from_public !== true && isToday(q.created_at)).length,
            crm: items.filter((q) => !q.from_portal && !q.from_public && isToday(q.created_at)).length,
          };
          return [
            { v: "all", l: "Tutti", color: "blue" },
            { v: "crm", l: "Da CRM", color: "slate" },
            { v: "portal", l: "Portale cliente", color: "indigo" },
            { v: "public", l: "Area pubblica", color: "emerald" },
          ].map((opt) => {
            const active = sourceFilter === opt.v;
            const n = counts[opt.v] ?? 0;
            const newToday = todayCounts[opt.v] ?? 0;
            return (
              <button
                key={opt.v}
                data-testid={`source-filter-${opt.v}`}
                onClick={() => setSourceFilter(opt.v)}
                className={`text-xs px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-2 ${
                  active
                    ? "bg-blue-600 text-white border-blue-600"
                    : "border-slate-200 text-slate-600 hover:border-slate-300 bg-white"
                }`}
              >
                <span>{opt.l}</span>
                <span
                  data-testid={`source-filter-count-${opt.v}`}
                  className={`text-[10px] font-bold rounded-full px-1.5 min-w-[20px] h-[18px] inline-flex items-center justify-center ${
                    active
                      ? "bg-white/25 text-white"
                      : n === 0
                      ? "bg-slate-100 text-slate-400"
                      : opt.color === "emerald"
                      ? "bg-emerald-100 text-emerald-700"
                      : opt.color === "indigo"
                      ? "bg-indigo-100 text-indigo-700"
                      : opt.color === "slate"
                      ? "bg-slate-100 text-slate-600"
                      : "bg-blue-100 text-blue-700"
                  }`}
                >
                  {n}
                </span>
                {newToday > 0 && (
                  <span
                    data-testid={`source-filter-today-${opt.v}`}
                    title={`${newToday} ${newToday === 1 ? "nuovo arrivato" : "nuovi arrivati"} oggi`}
                    className={`text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] inline-flex items-center gap-0.5 animate-pulse ${
                      active
                        ? "bg-amber-300 text-amber-900"
                        : "bg-rose-500 text-white"
                    }`}
                  >
                    🔥 {newToday}
                  </span>
                )}
              </button>
            );
          });
        })()}
        <span className="text-xs text-slate-400 ml-2">({filteredItems.length} di {items.length})</span>
      </div>

      {/* Filtro veloce per stato */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="status-filter-bar">
        {[
          { v: "all", l: "Tutti", cls: "bg-slate-700 text-white", clsInactive: "border-slate-200 text-slate-600 bg-white" },
          { v: "open", l: "📂 Aperti", cls: "bg-indigo-600 text-white", clsInactive: "border-indigo-200 text-indigo-700 bg-indigo-50" },
          { v: "nuovo", l: "🆕 Nuovi", cls: "bg-blue-600 text-white", clsInactive: "border-blue-200 text-blue-700 bg-blue-50" },
          { v: "in_lavorazione", l: "⚙️ In lavorazione", cls: "bg-amber-600 text-white", clsInactive: "border-amber-200 text-amber-700 bg-amber-50" },
          { v: "accettato", l: "✓ Accettati", cls: "bg-emerald-600 text-white", clsInactive: "border-emerald-200 text-emerald-700 bg-emerald-50" },
          { v: "rifiutato", l: "✗ Rifiutati", cls: "bg-rose-600 text-white", clsInactive: "border-rose-200 text-rose-700 bg-rose-50" },
        ].map((s) => {
          const active = statusFilter === s.v;
          const n = s.v === "open"
            ? (statusCounts["nuovo"] ?? 0) + (statusCounts["in_lavorazione"] ?? 0)
            : (statusCounts[s.v] ?? 0);
          return (
            <button
              key={s.v}
              data-testid={`status-filter-${s.v}`}
              onClick={() => setStatusFilter(s.v)}
              className={`text-xs px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-2 ${active ? s.cls + " border-transparent" : s.clsInactive + " hover:opacity-80"}`}
            >
              <span>{s.l}</span>
              <span className={`text-[10px] font-bold rounded-full px-1.5 min-w-[20px] h-[18px] inline-flex items-center justify-center ${active ? "bg-white/25 text-white" : "bg-white/70 text-current"}`}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {isAdmin && (
        <div className="flex items-center justify-between gap-3 mb-2">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              data-testid="quotes-select-all"
              checked={filteredItems.length > 0 && filteredItems.every((q) => selectedIds.has(q.id))}
              onChange={(e) => selectAllVisible(filteredItems, e.target.checked)}
              className="h-4 w-4 cursor-pointer accent-rose-600"
            />
            Seleziona tutti
          </label>
          {selectedIds.size > 0 && (
            <div
              data-testid="quotes-bulk-bar"
              className="flex items-center gap-3 px-3 py-1.5 bg-rose-50 border border-rose-200 rounded-md text-sm"
            >
              <span className="font-semibold text-rose-900" data-testid="quotes-bulk-count">
                {selectedIds.size} {selectedIds.size === 1 ? "selezionato" : "selezionati"}
              </span>
              <button onClick={clearSelection} className="text-xs text-rose-700 hover:underline">Annulla</button>
              <Button size="sm" variant="destructive" onClick={requestBulkAction} disabled={bulkBusy} data-testid="quotes-bulk-delete-trigger">
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                {trashView
                  ? `Elimina definitivamente ${selectedIds.size} 🔐`
                  : `Sposta nel cestino ${selectedIds.size}`}
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="space-y-3" data-testid="quotes-list">
        {filteredItems.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400">
            {items.length === 0 ? "Nessun preventivo ancora. Aggiungi il primo." : "Nessun preventivo per il filtro selezionato."}
          </div>
        )}
        {filteredItems.map((q) => (
          <div
            key={q.id}
            id={`quote-card-${q.id}`}
            className={`border rounded-lg p-4 transition-shadow duration-500 ${selectedIds.has(q.id) ? "border-rose-300 bg-rose-50/30" : ((q._plateGroupKey && q._plateGroupSize > 1) ? "border-slate-200 bg-sky-50/60" : "border-slate-200 bg-white")} ${flashId === q.id ? "ring-4 ring-emerald-400 shadow-lg" : ""} ${q._plateGroupKey ? "border-l-4 border-l-blue-400" : ""}`}
          >
            {q._plateGroupIsFirst && (
              <div className="-mx-4 -mt-4 mb-3 px-4 py-1.5 bg-blue-50 border-b border-blue-200 rounded-t-lg flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-blue-800">
                <span className="text-base leading-none">📋</span>
                <span>Storico targa <span className="font-mono">{q._plateGroupKey}</span> · {q._plateGroupSize} preventivi (dal più vecchio al più recente)</span>
              </div>
            )}
            {q._plateGroupKey && q._plateGroupIndex > 0 && (
              <div className="-mt-2 mb-2 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-blue-700">
                <span>↳ Stessa targa <span className="font-mono">{q._plateGroupKey}</span> · #{q._plateGroupIndex + 1} di {q._plateGroupSize}</span>
              </div>
            )}
            {isAdmin && (
              <div className="flex items-center justify-end mb-2">
                <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`quote-select-${q.id}`}
                    checked={selectedIds.has(q.id)}
                    onChange={() => toggleSelect(q.id)}
                    className="h-4 w-4 cursor-pointer accent-rose-600"
                  />
                  Seleziona
                </label>
              </div>
            )}
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="min-w-0 flex-1">
                {/* 🆕 09/07/2026 — Nome cliente in cima; 10/07/2026 — accanto: numero preventivo, targa e badge sintesi */}
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <h3
                    className="font-display text-xl font-bold text-slate-900 cursor-pointer hover:text-blue-700 select-none uppercase truncate"
                    onDoubleClick={() => openEdit(q)}
                    data-testid={`quote-client-name-${q.id}`}
                    title="Doppio click per modificare il preventivo"
                  >
                    {q.client_name || "Cliente"}
                  </h3>
                  {q.quote_number && (
                    <span data-testid={`quote-number-inline-${q.id}`} className="inline-flex items-center font-mono text-xs font-bold bg-slate-900 text-white px-2 py-0.5 rounded">
                      {q.quote_number}
                    </span>
                  )}
                  {(() => {
                    const plate = (q.details && (q.details.vehicle_plate || q.details.targa) || "").toString().toUpperCase();
                    if (!plate) return null;
                    const isMoto = q.type === "moto";
                    return (
                      <span
                        data-testid={`quote-plate-inline-${q.id}`}
                        className="inline-flex items-center gap-1 font-mono text-xs font-bold tracking-wide px-2 py-0.5 rounded border border-slate-400 bg-slate-100 text-slate-800 uppercase"
                        title="Targa veicolo"
                      >
                        <span className="text-[13px] leading-none">{isMoto ? "🏍️" : q.type === "auto" ? "🚗" : "🚙"}</span>
                        {plate}
                      </span>
                    );
                  })()}
                  {(() => {
                    // Badge sintesi (Tipo · Situazione assicurativa · Durata) inline al nome
                    const typeLabel = TYPE_LABEL_IT[q.type] || q.type;
                    const statusCode = q.details?.insurance_status;
                    const statusLabel = statusCode
                      ? (insuranceStatusOpts.find((o) => o.code === statusCode)?.label || statusCode)
                      : null;
                    const yrs = q.details?.duration_years;
                    const yrsLabel = (yrs !== undefined && yrs !== null && yrs !== "")
                      ? `${Number(yrs) === 1 ? "1 anno" : `${yrs} anni`}`
                      : null;
                    const parts = [typeLabel, statusLabel, yrsLabel].filter(Boolean);
                    if (parts.length < 2) return null;
                    return (
                      <span
                        data-testid={`quote-summary-badge-inline-${q.id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold bg-indigo-50 text-indigo-800 border border-indigo-300 rounded-full px-2 py-0.5"
                        title="Riepilogo preventivo: tipo · situazione · durata"
                      >
                        {parts.map((p, i) => (
                          <span key={i} className="inline-flex items-center">
                            {i > 0 && <span className="mx-0.5 text-indigo-400">·</span>}
                            {p}
                          </span>
                        ))}
                      </span>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => toggleCollapsed(q.id)}
                    data-testid={`toggle-${q.id}`}
                    className="h-7 w-7 rounded border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors"
                    title={collapsedIds.has(q.id) ? "Espandi scheda" : "Comprimi scheda"}
                  >
                    {collapsedIds.has(q.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                  </button>
                  {/* 🆕 Pulsante Modifica visibile SEMPRE nell'header (anche con scheda compressa) */}
                  {!trashView && (
                    <button
                      onClick={() => openEdit(q)}
                      data-testid={`edit-quote-header-${q.id}`}
                      className="h-7 px-2.5 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-xs font-semibold inline-flex items-center gap-1"
                      title="Modifica richiesta preventivo"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Modifica
                    </button>
                  )}
                  {/* 🆕 Pulsante "Ripristina" sempre visibile in vista cestino */}
                  {trashView && (
                    <button
                      onClick={() => restoreQuote(q)}
                      data-testid={`restore-quote-header-${q.id}`}
                      className="h-7 px-2.5 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-semibold inline-flex items-center gap-1"
                      title="Ripristina dal cestino"
                    >
                      ↻ Ripristina
                    </button>
                  )}
                  {/* 🆕 Pulsante "Apri offerta": apre l'offerta collegata più recente (se esiste) */}
                  {!trashView && (quoteOffersMap[q.id] || []).length > 0 && (() => {
                    const linkedOffers = quoteOffersMap[q.id] || [];
                    const target = linkedOffers[0]; // più recente (ordinato DESC dal backend)
                    // 🆕 Cerca un'offerta NON ancora pagata da cui poter forzare il pagamento
                    const unpaidOffer = linkedOffers.find(
                      (o) => o.status !== "pagata" && o.status !== "annullata" && o.status !== "eliminata"
                    );
                    return (
                      <span className="inline-flex items-center gap-1.5">
                        <button
                          onClick={() => navigate(`/quotes/offers?focus=${target.id}`)}
                          data-testid={`open-linked-offer-${q.id}`}
                          className="h-7 px-2.5 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-semibold inline-flex items-center gap-1"
                          title={linkedOffers.length === 1
                            ? `Apri offerta collegata ${target.offer_number}`
                            : `Apri offerta più recente (${target.offer_number}) — ce ne sono ${linkedOffers.length} collegate`}
                        >
                          <Send className="h-3.5 w-3.5" /> Apri offerta
                          {linkedOffers.length > 1 && (
                            <span className="ml-0.5 inline-flex items-center justify-center text-[9px] font-bold bg-emerald-700 text-white rounded-full h-4 min-w-[16px] px-1">
                              {linkedOffers.length}
                            </span>
                          )}
                        </button>
                        {unpaidOffer && (
                          <button
                            type="button"
                            data-testid={`force-paid-from-quote-${q.id}`}
                            onClick={() => { setForcePaidOffer(unpaidOffer); setForcePaidMethod("bonifico"); }}
                            className="h-7 px-2 rounded border border-fuchsia-300 bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-700 text-xs font-semibold inline-flex items-center gap-1"
                            title={`Forza pagamento offerta ${unpaidOffer.offer_number} (segna come pagata immediatamente)`}
                          >
                            <Zap className="h-3.5 w-3.5" /> Forza pagam.
                          </button>
                        )}
                      </span>
                    );
                  })()}
                  {q.quote_number && (
                    <span data-testid={`quote-number-${q.id}`} className="hidden">
                      {q.quote_number}
                    </span>
                  )}
                  {(() => {
                    // Badge originale nascosto: già mostrato inline al nome cliente
                    return null;
                  })()}
                  {(() => {
                    // Badge originale nascosto: già mostrato inline al nome cliente
                    return null;
                  })()}
                  <StatusBadge status={q.status} />
                  {q.is_owner_only && (
                    <span
                      data-testid={`quote-owner-only-${q.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5"
                      title="Dati proprietario salvati nel preventivo. Il cliente verrà creato in anagrafica solo alla conversione in polizza."
                    >
                      👤 Solo proprietario
                    </span>
                  )}
                  {(q.duplicate_plate_count || 0) > 0 && (
                    <button
                      type="button"
                      data-testid={`quote-dup-plate-${q.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold bg-rose-50 text-rose-800 border border-rose-300 rounded-full px-2 py-0.5 hover:bg-rose-100 cursor-pointer"
                      title={`Targa già presente in altri ${q.duplicate_plate_count} preventivi. Clicca per vedere e gestire.`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        const plate = (q.details?.vehicle_plate || "").toString().trim().toUpperCase();
                        if (!plate) return;
                        try {
                          const { data: dup } = await api.get("/quotes/check-duplicate-plate", {
                            params: { plate, exclude_id: q.id },
                          });
                          if (dup?.duplicates?.length > 0) {
                            setDupCheckContext("extract");
                            setDupCheckResult({ plate, items: dup.duplicates });
                            setDupCheckOpen(true);
                          } else {
                            toast.info("Nessun duplicato residuo per questa targa.");
                            // Pulisci il flag stale lato UI ricaricando la lista
                            load();
                          }
                        } catch (err) { toast.error(formatApiError(err)); }
                      }}
                    >
                      ⚠️ Targa: {q.duplicate_plate_count} duplicat{q.duplicate_plate_count === 1 ? "o" : "i"}
                    </button>
                  )}
                  {q.policy_number && (
                    <a
                      href="/policies"
                      data-testid={`quote-policy-link-${q.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-300 rounded-full px-2 py-0.5 hover:bg-emerald-100 transition-colors"
                      title={`Polizza emessa da questo preventivo: ${q.policy_number}`}
                    >
                      → {q.policy_number}
                    </a>
                  )}
                  {(q.attachments_count || 0) > 0 && (
                    <AttachmentsHoverPreview
                      endpoint={`/quotes/${q.id}/attachments`}
                      count={q.attachments_count || 0}
                      placement="right"
                      testid={`attach-hover-quote-${q.id}`}
                    >
                      <span
                        data-testid={`attach-quote-${q.id}`}
                        className="relative inline-flex items-center text-slate-600 hover:text-blue-700 cursor-help"
                        title={`${q.attachments_count} allegat${q.attachments_count === 1 ? "o" : "i"}`}
                      >
                        <Paperclip className="h-4 w-4" />
                        <span className="ml-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                          {q.attachments_count}
                        </span>
                      </span>
                    </AttachmentsHoverPreview>
                  )}
                  <ApplyPdfTemplateButton
                    quoteId={q.id}
                    variant="ghost"
                    testid={`quote-gen-doc-${q.id}`}
                    iconOnly
                    onApplied={() => { try { load(); } catch { /* noop */ } }}
                  />
                  {/* Badge cronologia invii — sempre visibile se ci sono stati invii.
                      Verde: tutte le risposte ricevute · Ambra: in attesa · Rosso: oltre 5 giorni senza risposta */}
                  {(sendsSummary[q.id]?.count || 0) > 0 && (() => {
                    const s = sendsSummary[q.id];
                    const isStale = (s.stale_replies || 0) > 0;
                    const isPending = (s.pending_replies || 0) > 0;
                    const cls = isStale
                      ? "bg-rose-50 border-rose-300 text-rose-800 hover:bg-rose-100"
                      : isPending
                      ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                      : "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100";
                    const subBadgeCls = isStale ? "bg-rose-600 text-white" : "bg-amber-500 text-white";
                    return (
                      <button
                        type="button"
                        data-testid={`sends-badge-${q.id}`}
                        onClick={(e) => { e.stopPropagation(); openSendsHistory(q); }}
                        title={`${s.count} invio/i a partner — ultimo ${s.last_sent_at ? formatDateTime(s.last_sent_at) : ""}${
                          isStale ? ` · ⚠️ ${s.stale_replies} senza risposta da oltre 5 giorni`
                          : isPending ? ` · ${s.pending_replies} in attesa di risposta`
                          : " · tutte le risposte ricevute"
                        }`}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors ${cls}`}
                      >
                        {isStale ? "⚠️" : "📤"} {s.count} invio{s.count === 1 ? "" : "i"}
                        {(isPending || isStale) && (
                          <span className={`ml-1 text-[10px] font-bold rounded-full px-1.5 py-0 ${subBadgeCls}`}>
                            {isStale ? `${s.stale_replies}+5g` : `${s.pending_replies} ⏳`}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                  {/* 🆕 Badge email Inbox collegate al preventivo (auto-linking) */}
                  {(inboxCounts[q.id]?.total || 0) > 0 && (() => {
                    const ic = inboxCounts[q.id];
                    const unread = ic.unread || 0;
                    const hasUnread = unread > 0;
                    const cls = hasUnread
                      ? "bg-rose-50 border-rose-300 text-rose-800 hover:bg-rose-100"
                      : "bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100";
                    return (
                      <button
                        type="button"
                        data-testid={`inbox-badge-${q.id}`}
                        onClick={(e) => { e.stopPropagation(); openEdit(q); }}
                        title={`${ic.total} email collegat${ic.total === 1 ? "a" : "e"}${hasUnread ? ` — ⚠ ${unread} non letta/e (status pending)` : " — tutte processate"}. Clicca per aprire il preventivo e vedere la timeline.`}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-colors ${cls}`}
                      >
                        📩 {ic.total}
                        {hasUnread && (
                          <span className="ml-0.5 bg-rose-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0" data-testid={`inbox-unread-${q.id}`}>
                            {unread} nuov{unread === 1 ? "a" : "e"}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                  {q.from_public && (
                    <span
                      data-testid={`from-public-${q.id}`}
                      className="inline-flex items-center rounded-full border bg-amber-50 border-amber-200 text-amber-700 px-2 py-0.5 text-xs font-medium"
                    >
                      🌐 Pubblico
                    </span>
                  )}
                  {q.from_portal && !q.from_public && (
                    <span
                      data-testid={`from-portal-${q.id}`}
                      className="inline-flex items-center rounded-full border bg-amber-50 border-amber-200 text-amber-700 px-2 py-0.5 text-xs font-medium"
                    >
                      🌐 Da portale cliente
                    </span>
                  )}
                  {!q.from_public && !q.from_portal && (
                    <span
                      data-testid={`from-crm-${q.id}`}
                      className="inline-flex items-center rounded-full border bg-slate-100 border-slate-300 text-slate-700 px-2 py-0.5 text-xs font-medium"
                      title="Inserita manualmente da operatore del CRM"
                    >
                      🏢 Da CRM
                    </span>
                  )}
                  {q.share_link && (() => {
                    const exp = new Date(q.share_link.expires_at);
                    const daysLeft = Math.max(0, Math.ceil((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                    const url = `${window.location.origin}/api/public/quote-pdf/${q.share_link.token}`;
                    return (
                      <span
                        data-testid={`share-link-active-${q.id}`}
                        className="inline-flex items-center gap-1 rounded-full border bg-green-50 border-green-300 text-green-800 px-2 py-0.5 text-xs font-medium"
                        title={`Link PDF condivisibile attivo · scade ${exp.toLocaleDateString("it-IT")} · ${q.share_link.access_count || 0} aperture`}
                      >
                        🔗 Link attivo · {daysLeft}g · {q.share_link.access_count || 0} apert.
                        <button
                          type="button"
                          data-testid={`share-link-stats-${q.id}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const { data } = await api.get(`/quotes/${q.id}/share-link/stats`);
                              setShareStats({ quote: q, ...data });
                            } catch (err) {
                              toast.error(formatApiError(err) || "Errore caricamento statistiche");
                            }
                          }}
                          className="ml-0.5 hover:text-green-900 hover:underline"
                          title="Vedi statistiche aperture"
                        >
                          [stats]
                        </button>
                        <button
                          type="button"
                          data-testid={`share-link-copy-${q.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard?.writeText(url).then(
                              () => toast.success("Link copiato"),
                              () => toast.error("Impossibile copiare il link"),
                            );
                          }}
                          className="ml-0.5 hover:text-green-900 hover:underline"
                          title="Copia link negli appunti"
                        >
                          [copia]
                        </button>
                        <button
                          type="button"
                          data-testid={`share-link-revoke-${q.id}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!confirm("Revocare il link PDF condivisibile? Chi ce l'ha non potrà più aprirlo.")) return;
                            try {
                              await api.delete(`/quotes/${q.id}/share-link`);
                              toast.success("Link revocato");
                              load();
                            } catch (err) {
                              toast.error(formatApiError(err) || "Errore revoca");
                            }
                          }}
                          className="ml-0.5 text-rose-700 hover:text-rose-900 hover:underline"
                          title="Revoca il link"
                        >
                          [revoca]
                        </button>
                      </span>
                    );
                  })()}
                </div>
                <p className="text-xs text-slate-500 mt-1 uppercase tracking-wide flex items-center gap-2 flex-wrap">
                  <PolicyTypeBadge code={q.type} size="xs" testid={`quote-type-badge-${q.id}`} />
                  {q.type === "auto" && !["accettato", "rifiutato", "convertito", "scaduto", "annullato"].includes(q.status) && !(q.details?.driving_type || q.driving_type) && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openEdit(q); }}
                      className="normal-case tracking-normal inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-orange-300 bg-orange-100 text-orange-800 text-[10px] font-semibold hover:bg-orange-200 transition-colors"
                      title="Preventivo incompleto: clic per aprire la modifica e compilare il Tipo di guida"
                      data-testid={`quote-missing-driving-type-${q.id}`}
                    >
                      ⚠ Manca tipo di guida
                    </button>
                  )}
                  <span>creato il {formatDateTime(q.created_at)}</span>
                  {q.updated_at && (
                    <span className="text-amber-700">· modificato il {formatDateTime(q.updated_at)}{q.updated_by_name ? ` da ${q.updated_by_name}` : ""}</span>
                  )}
                </p>
                {!collapsedIds.has(q.id) && (
                  <>
                <p className="mt-3 text-sm text-slate-700">{q.coverage_request}</p>
                {(() => {
                  // 🆕 Feb 2026 — Clone shallow per evitare mutazioni sull'oggetto originale (dedup).
                  const det = { ...(q.details || {}) };
                  // Chiavi tecniche/interne da non mostrare
                  const HIDDEN_KEYS = new Set([
                    "_coverages_auto_text", "_attachment_ids", "_validity_accepted_at",
                    "selected_coverages", "driving_type_manual", "coverages auto text",
                    "validity accepted at", "attachment ids",
                    // 🆕 Feb 2026 — nascondi ID tecnici e strutture complesse dalla sezione "Altri dati"
                    "company_id", "broker_id",
                    "requested_coverages", // ha una sua sezione dedicata nel form; nel riepilogo pubblico è ridondante
                    // 🆕 Feb 2026 — flag interni non da mostrare
                    "owner_email_unavailable", "email_unavailable",
                  ]);
                  // 🆕 Feb 2026 — Dedup delle coppie "duplicate" (stesso dato, chiavi diverse):
                  //   • fuel ⇄ vehicle_fuel
                  //   • kw ⇄ vehicle_kw
                  //   • displacement ⇄ vehicle_displacement
                  //   • marital_status ⇄ owner_marital_status
                  //   • gender ⇄ owner_gender
                  // Regola: se entrambe presenti, mostra la chiave "canonica" (owner_*/vehicle_*/fuel/kw/displacement)
                  //         e nascondi l'altra. Se solo una presente, mostra quella (mappandola alla canonica).
                  const dedupPairs = [
                    ["fuel", "vehicle_fuel"],
                    ["kw", "vehicle_kw"],
                    ["displacement", "vehicle_displacement"],
                    ["owner_marital_status", "marital_status"],
                    ["owner_gender", "gender"],
                  ];
                  dedupPairs.forEach(([primary, secondary]) => {
                    const hasPrimary = det[primary] !== "" && det[primary] != null;
                    const hasSecondary = det[secondary] !== "" && det[secondary] != null;
                    if (hasPrimary && hasSecondary) {
                      HIDDEN_KEYS.add(secondary);
                    } else if (!hasPrimary && hasSecondary) {
                      // Copia il valore sulla chiave canonica per farlo apparire nella sezione giusta,
                      // e nascondi la duplicata così non finisce in "Altri dati".
                      det[primary] = det[secondary];
                      HIDDEN_KEYS.add(secondary);
                    }
                  });
                  // Raggruppa per sezione (in ordine), mostra solo sezioni non-vuote
                  const renderedSections = DETAIL_SECTIONS.map((sec) => {
                    // 🆕 Feb 2026 — Bersani/RC Familiare visibile SOLO se la situazione assicurativa selezionata ha flag `triggers_bersani`.
                    if (sec.title === "Bersani / RC Familiare" && !insuranceStatusOpts.find((o) => o.code === det.insurance_status)?.triggers_bersani) return null;
                    const rows = sec.keys
                      .filter((k) => !HIDDEN_KEYS.has(k))
                      .map((k) => [k, det[k]])
                      .filter(([, v]) => v !== "" && v != null && !(Array.isArray(v) && v.length === 0));
                    return rows.length ? { sec, rows } : null;
                  }).filter(Boolean);
                  // Chiavi non mappate
                  const usedKeys = new Set(DETAIL_SECTIONS.flatMap((s) => s.keys));
                  const leftover = Object.entries(det)
                    .filter(([k, v]) => !usedKeys.has(k) && !HIDDEN_KEYS.has(k) && v !== "" && v != null && !(Array.isArray(v) && v.length === 0));
                  // Garanzie selezionate dal cliente (chip pills)
                  const selectedCoverages = Array.isArray(det.selected_coverages) ? det.selected_coverages : [];
                  if (renderedSections.length === 0 && leftover.length === 0 && selectedCoverages.length === 0) return null;
                  return (
                    <div data-testid={`quote-details-${q.id}`} className="mt-3 space-y-1.5">
                      {selectedCoverages.length > 0 && (
                        <div className="rounded-md border border-emerald-200 overflow-hidden">
                          <div className="bg-emerald-600 text-white px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold">
                            Garanzie richieste · {selectedCoverages.length}
                          </div>
                          <div className="bg-white px-2.5 py-1.5 flex flex-wrap gap-1.5">
                            {selectedCoverages.map((code) => (
                              <span key={code} className="inline-flex items-center text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-2 py-0.5">
                                {coveragesByCode[code] || code}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {renderedSections.map(({ sec, rows }) => (
                        <div key={sec.title} className={`rounded-md border ${sec.border} overflow-hidden`}>
                          <div className={`${sec.headerBg} text-white px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold`}>
                            {sec.title}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 text-[11.5px] bg-white">
                            {rows.map(([k, v], idx) => (
                              <div key={k} className={`flex justify-between gap-2 px-2.5 py-1 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"} border-b border-slate-100`}>
                                <span className="text-slate-500 truncate">{DETAIL_LABELS[k] || k.replace(/_/g, " ")}</span>
                                <span className="font-medium text-slate-800 text-right truncate">{formatDetailValue(k, v)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {leftover.length > 0 && (
                        <div className="rounded-md border border-slate-200 overflow-hidden">
                          <div className="bg-slate-500 text-white px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold">
                            Altri dati
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 text-[11.5px] bg-white">
                            {leftover.map(([k, v], idx) => (
                              <div key={k} className={`flex justify-between gap-2 px-2.5 py-1 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"} border-b border-slate-100`}>
                                <span className="text-slate-500 truncate">{DETAIL_LABELS[k] || k.replace(/_/g, " ")}</span>
                                <span className="font-medium text-slate-800 text-right truncate">{formatDetailValue(k, v)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {q.estimated_premium != null && (
                  <p className="mt-2 text-sm"><span className="text-slate-500">Premio stimato:</span> <span className="font-medium tabular-nums">{formatCurrency(q.estimated_premium)}</span></p>
                )}
                  </>
                )}
              </div>
              {!collapsedIds.has(q.id) && (
              <div className="flex flex-col gap-2 items-end ml-auto self-start">
                <Select value={q.status} onValueChange={(v) => setStatus(q.id, v)}>
                  <SelectTrigger data-testid={`quote-status-${q.id}`} className="w-44 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
                <Button
                  data-testid={`edit-quote-${q.id}`}
                  onClick={() => openEdit(q)}
                  size="sm"
                  variant="outline"
                  className="border-amber-200 text-amber-800 hover:bg-amber-50"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Modifica
                </Button>
                <Button
                  data-testid={`print-quote-${q.id}`}
                  onClick={() => printSingleQuote(q)}
                  size="sm"
                  variant="outline"
                >
                  <Printer className="h-3.5 w-3.5 mr-1.5" />
                  Stampa preventivo
                </Button>
                <Button
                  data-testid={`messages-${q.id}`}
                  onClick={() => openMessages(q)}
                  size="sm"
                  variant="outline"
                >
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                  Messaggi
                </Button>
                <Button
                  data-testid={`send-partner-${q.id}`}
                  onClick={() => sendToPartner(q)}
                  size="sm"
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                >
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Invia a partner
                </Button>
                {!q.converted_policy_id && q.status !== "rifiutato" && (
                  <Button
                    data-testid={`convert-quote-${q.id}`}
                    onClick={() => convertToPolicy(q)}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    title="Crea una nuova polizza con i dati di questo preventivo e apri la scheda"
                  >
                    <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Trasforma in Polizza
                  </Button>
                )}
                {q.status !== "accettato" && q.status !== "rifiutato" && !q.converted_policy_id && (
                  <Button
                    data-testid={`send-accept-link-${q.id}`}
                    onClick={() => sendAcceptLink(q)}
                    size="sm"
                    variant="outline"
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    title="Invia al cliente un link riservato per accettare il preventivo online"
                  >
                    <Send className="h-3.5 w-3.5 mr-1.5" /> Link cliente
                  </Button>
                )}
                {(q.view_count || 0) > 0 && q.status !== "accettato" && q.status !== "rifiutato" && !q.converted_policy_id && (
                  <span
                    data-testid={`quote-view-tracker-${q.id}`}
                    className="inline-flex items-center gap-1 text-[11px] bg-amber-100 text-amber-800 px-2 py-1 rounded border border-amber-200"
                    title={q.last_viewed_at ? `Ultima visita: ${formatDateTime(q.last_viewed_at)}` : ""}
                  >
                    👁 Visto {q.view_count} {q.view_count === 1 ? "volta" : "volte"} · non accettato
                  </span>
                )}
                {q.converted_policy_id && (
                  <span
                    data-testid={`quote-converted-${q.id}`}
                    className="inline-flex items-center gap-1 text-[11px] bg-emerald-100 text-emerald-800 px-2 py-1 rounded border border-emerald-200"
                    title="Preventivo già convertito in polizza"
                  >
                    <ShieldCheck className="h-3 w-3" /> Polizza creata
                  </span>
                )}
                <Button
                  data-testid={`download-quote-pdf-${q.id}`}
                  onClick={() => downloadQuoteRequestPdf(q)}
                  size="sm"
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  title="Scarica/stampa il modulo richiesta preventivo in PDF"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Scarica PDF
                </Button>
                {(() => {
                  const client = clients.find((c) => c.id === q.client_id);
                  const rawPhone = client?.mobile || client?.phone;
                  if (!rawPhone) return null;
                  const typeLabel = TYPE_LABEL_LIVE[q.type] || q.type;
                  const defaultMessage =
                    `Ciao ${client?.name || ""}, ti scrivo a proposito della tua richiesta di preventivo ${typeLabel}. ` +
                    `Ho qualche aggiornamento per te — fammi sapere quando hai un minuto.`;
                  return (
                    <WhatsAppButton rawPhone={rawPhone} defaultMessage={defaultMessage} testid={`whatsapp-${q.id}`}>
                      <span
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-green-500 hover:bg-green-600 text-white shadow-sm transition-colors cursor-pointer"
                        title={`Apri WhatsApp con ${client?.name || ""}`}
                      >
                        💬 WhatsApp
                      </span>
                    </WhatsAppButton>
                  );
                })()}
                {!trashView && (
                  <button
                    data-testid={`create-offer-from-quote-${q.id}`}
                    onClick={async () => {
                      try {
                        // 🆕 Verifica se esistono già offerte per questo preventivo
                        const existingRes = await api.get(`/quote-offers?quote_id=${q.id}`);
                        const existing = (existingRes.data || []).filter((o) => o.quote_id === q.id && o.status !== "eliminata");
                        if (existing.length > 0) {
                          const proceed = window.confirm(
                            `⚠️ Esistono già ${existing.length} offerta${existing.length === 1 ? "" : "/e"} per questo preventivo:\n\n`
                            + existing.slice(0, 5).map((o) => `• ${o.offer_number} (${o.status}) · ${formatCurrency(o.amount)}`).join("\n")
                            + `\n\nVuoi comunque creare una NUOVA offerta?\n\n• OK = crea ulteriore offerta\n• Annulla = apri le offerte esistenti`
                          );
                          if (!proceed) {
                            navigate(`/quotes/offers?focus=${existing[0].id}`);
                            return;
                          }
                        }
                        const t = toast.loading("Creo offerta collegata…");
                        const initialAmount = Math.max(1, parseFloat(q.estimated_premium) || 1);
                        const { data } = await api.post("/quote-offers", { quote_id: q.id, amount: initialAmount, coverages: [] });
                        toast.dismiss(t);
                        toast.success(`Offerta ${data.offer_number} creata · aggiungi le varianti compagnia`);
                        navigate(`/quotes/offers?focus=${data.id}&fresh=1`);
                      } catch (e) {
                        toast.error(formatApiError(e));
                      }
                    }}
                    className="text-xs font-semibold px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5"
                    title="Crea una nuova offerta collegata a questo preventivo (avvisa se ne esistono già)"
                  >
                    📊 Crea offerta
                  </button>
                )}
                {!trashView && (
                  <button data-testid={`delete-quote-${q.id}`} onClick={() => remove(q.id)} className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1" title="Sposta nel cestino">
                    <Trash2 className="h-3 w-3" /> elimina
                  </button>
                )}
                {trashView && (
                  <>
                    <button data-testid={`restore-quote-${q.id}`} onClick={() => restoreQuote(q)} className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-300 hover:bg-emerald-50" title="Ripristina">
                      ↻ ripristina
                    </button>
                    <button data-testid={`permanent-delete-quote-${q.id}`} onClick={() => requestSinglePermanentOtp(q)} className="text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1 px-2 py-1 rounded border border-red-400 hover:bg-red-50" title="Elimina definitivamente (con OTP)">
                      <Trash2 className="h-3 w-3" /> elimina definitivamente
                    </button>
                  </>
                )}
              </div>
              )}
              {/* 🆕 Feb 2026 — Offerte Ricevute da partner online: sempre visibile quando la card non è collassata */}
              {!collapsedIds.has(q.id) && (quoteOffersMap[q.id] || []).filter((o) => o.submitted_by_partner).length > 0 && (
                <div className="mt-3" data-testid={`partner-online-offers-wrap-${q.id}`}>
                  <details open className="rounded-md border-2 border-emerald-300 bg-gradient-to-r from-emerald-50/70 to-teal-50/70 group" data-testid={`partner-online-offers-${q.id}`}>
                    <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between hover:bg-emerald-100/50 rounded-md">
                      <span className="text-[11px] uppercase tracking-wide text-emerald-800 font-bold inline-flex items-center gap-1.5">
                        🌐 Offerte ricevute da partner online ({(quoteOffersMap[q.id] || []).filter((o) => o.submitted_by_partner).length})
                        {(() => {
                          const unread = (quoteOffersMap[q.id] || []).filter((o) => o.submitted_by_partner && !o.viewed_by_agency).length;
                          return unread > 0 ? (
                            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-rose-600 text-white rounded-full animate-pulse ml-1" data-testid={`partner-online-offers-unread-badge-${q.id}`} title={`${unread} non ancora visualizzat${unread === 1 ? "a" : "e"}`}>
                              {unread} NUOV{unread === 1 ? "A" : "E"}
                            </span>
                          ) : null;
                        })()}
                      </span>
                      <ChevronDown className="h-4 w-4 text-emerald-700 group-open:rotate-180 transition-transform" />
                    </summary>
                    <ul className="px-3 pb-3 pt-1 space-y-1.5">
                      {(quoteOffersMap[q.id] || []).filter((o) => o.submitted_by_partner).map((o) => {
                        const isNew = !o.viewed_by_agency;
                        return (
                        <li
                          key={o.id}
                          className={`flex items-center justify-between gap-3 text-xs rounded px-2.5 py-2 bg-white border-2 cursor-pointer transition ${isNew ? "border-rose-400 ring-2 ring-rose-300/60 animate-pulse hover:animate-none hover:ring-rose-400" : "border-emerald-200 hover:border-emerald-400"}`}
                          onClick={() => openPartnerOfferDetail(o)}
                          data-testid={`partner-online-offer-row-${o.id}`}
                          title={isNew ? "🔔 Nuova offerta — clicca per aprire" : "Apri offerta ricevuta"}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-slate-800 truncate">
                                📄 {o.offer_number} · {formatCurrency(o.amount || o.premium)}
                              </span>
                              <span className={`inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 ${isNew ? "bg-rose-600 text-white animate-pulse" : "bg-emerald-600 text-white"}`}>
                                🌐 Da partner online
                              </span>
                              {isNew && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-rose-500 text-white rounded-full px-2 py-0.5 animate-pulse" data-testid={`partner-offer-new-badge-${o.id}`}>
                                  🔔 NUOVA
                                </span>
                              )}
                              {o.frazionamento && o.frazionamento !== "annuale" && (
                                <span className="text-[10px] font-semibold bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">
                                  {o.frazionamento}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {o.company_name || "Compagnia non specificata"}
                              {o.created_at && <> · Ricevuta {formatDate(o.created_at)}</>}
                              {o.valid_until && <> · Valida fino {formatDate(o.valid_until)}</>}
                            </div>
                          </div>
                          <StatusBadge status={o.status} />
                        </li>
                        );
                      })}
                    </ul>
                  </details>
                </div>
              )}
            </div>
            {expanded === q.id && !collapsedIds.has(q.id) && (
              <div className="mt-4 border-t border-slate-100 pt-4 space-y-3" data-testid={`thread-${q.id}`}>
                {/* 🆕 Feb 2026 — Offerte Ricevute da partner online (submitted_by_partner via link pubblico) */}
                {(quoteOffersMap[q.id] || []).filter((o) => o.submitted_by_partner).length > 0 && (
                  <details open className="rounded-md border-2 border-emerald-300 bg-gradient-to-r from-emerald-50/70 to-teal-50/70 group" data-testid={`partner-online-offers-thread-${q.id}`}>
                    <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between hover:bg-emerald-100/50 rounded-md">
                      <span className="text-[11px] uppercase tracking-wide text-emerald-800 font-bold inline-flex items-center gap-1.5">
                        🌐 Offerte ricevute da partner online ({(quoteOffersMap[q.id] || []).filter((o) => o.submitted_by_partner).length})
                        {(() => {
                          const unread = (quoteOffersMap[q.id] || []).filter((o) => o.submitted_by_partner && !o.viewed_by_agency).length;
                          return unread > 0 ? (
                            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-rose-600 text-white rounded-full animate-pulse ml-1" data-testid={`partner-online-offers-unread-badge-thread-${q.id}`} title={`${unread} non ancora visualizzat${unread === 1 ? "a" : "e"}`}>
                              {unread} NUOV{unread === 1 ? "A" : "E"}
                            </span>
                          ) : null;
                        })()}
                      </span>
                      <ChevronDown className="h-4 w-4 text-emerald-700 group-open:rotate-180 transition-transform" />
                    </summary>
                    <ul className="px-3 pb-3 pt-1 space-y-1.5">
                      {(quoteOffersMap[q.id] || []).filter((o) => o.submitted_by_partner).map((o) => {
                        const isNew = !o.viewed_by_agency;
                        return (
                        <li
                          key={o.id}
                          className={`flex items-center justify-between gap-3 text-xs rounded px-2.5 py-2 bg-white border-2 cursor-pointer transition ${isNew ? "border-rose-400 ring-2 ring-rose-300/60 animate-pulse hover:animate-none hover:ring-rose-400" : "border-emerald-200 hover:border-emerald-400"}`}
                          onClick={() => openPartnerOfferDetail(o)}
                          data-testid={`partner-online-offer-row-thread-${o.id}`}
                          title={isNew ? "🔔 Nuova offerta — clicca per aprire" : "Apri offerta ricevuta"}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-slate-800 truncate">
                                📄 {o.offer_number} · {formatCurrency(o.amount || o.premium)}
                              </span>
                              <span className={`inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 ${isNew ? "bg-rose-600 text-white animate-pulse" : "bg-emerald-600 text-white"}`}>
                                🌐 Da partner online
                              </span>
                              {isNew && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-rose-500 text-white rounded-full px-2 py-0.5 animate-pulse" data-testid={`partner-offer-new-badge-thread-${o.id}`}>
                                  🔔 NUOVA
                                </span>
                              )}
                              {o.frazionamento && o.frazionamento !== "annuale" && (
                                <span className="text-[10px] font-semibold bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">
                                  {o.frazionamento}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {o.company_name || "Compagnia non specificata"}
                              {o.created_at && <> · Ricevuta {formatDate(o.created_at)}</>}
                              {o.valid_until && <> · Valida fino {formatDate(o.valid_until)}</>}
                            </div>
                          </div>
                          <StatusBadge status={o.status} />
                        </li>
                        );
                      })}
                    </ul>
                  </details>
                )}
                {/* 🆕 Offerte accettate (pagate) collegate al preventivo — sezione collassabile, chiusa di default */}
                {(quoteOffersMap[q.id] || []).filter((o) => o.status === "pagata").length > 0 && (
                  <details className="rounded-md border border-emerald-200 bg-emerald-50/40 group" data-testid={`accepted-offers-${q.id}`}>
                    <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between hover:bg-emerald-100/40 rounded-md">
                      <span className="text-[11px] uppercase tracking-wide text-emerald-800 font-semibold inline-flex items-center gap-1.5">
                        ✅ Offerte accettate ({(quoteOffersMap[q.id] || []).filter((o) => o.status === "pagata").length})
                      </span>
                      <ChevronDown className="h-4 w-4 text-emerald-700 group-open:rotate-180 transition-transform" />
                    </summary>
                    <ul className="px-3 pb-3 pt-1 space-y-1.5">
                      {(quoteOffersMap[q.id] || []).filter((o) => o.status === "pagata").map((o) => (
                        <li
                          key={o.id}
                          className="flex items-center justify-between gap-3 text-xs rounded px-2.5 py-1.5 bg-white border border-emerald-100 hover:border-emerald-300 cursor-pointer"
                          onClick={() => navigate(`/quotes/offers?focus=${o.id}`)}
                          data-testid={`accepted-offer-row-${o.id}`}
                          title="Apri offerta"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-800 truncate">
                              📄 {o.offer_number} · {formatCurrency(o.amount)}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              Pagata {formatDate(o.paid_at)} · {o.payment_method || "—"}
                              {o.linked_policy_id && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); navigate(`/policies?focus=${o.linked_policy_id}`); }}
                                  className="ml-2 text-emerald-700 hover:underline font-medium"
                                >→ Apri polizza</button>
                              )}
                            </div>
                          </div>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">PAGATA</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                {/* 🆕 09/07/2026 — Storico Comunicazioni Partner: mostra data, ora, indirizzo email
                    di ogni invio/rinvio ai broker. Collassabile, chiusa di default. */}
                {(sends[q.id] || []).length > 0 && (
                  <details className="rounded-md border border-blue-200 bg-blue-50/40 group" data-testid={`partner-comms-history-${q.id}`}>
                    <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between hover:bg-blue-100/40 rounded-md">
                      <span className="text-[11px] uppercase tracking-wide text-blue-800 font-semibold inline-flex items-center gap-1.5">
                        📤 Storico Comunicazioni Partner ({(sends[q.id] || []).length})
                      </span>
                      <ChevronDown className="h-4 w-4 text-blue-700 group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="px-3 pb-3 pt-1">
                      <div className="max-h-[280px] overflow-y-auto space-y-1.5">
                        {(sends[q.id] || []).map((s) => {
                          const isReplied = !!s.replied_at;
                          const isResend = !!s.resend_of;
                          const isStale = !isReplied && s.sent_at && (Date.now() - new Date(s.sent_at).getTime()) > 5 * 24 * 60 * 60 * 1000;
                          return (
                            <div
                              key={s.id}
                              className={`flex items-start justify-between gap-3 text-xs rounded px-2.5 py-2 bg-white border ${isStale ? "border-rose-200" : isReplied ? "border-emerald-200" : "border-slate-200"} hover:border-blue-300`}
                              data-testid={`partner-comm-row-${s.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-slate-800 truncate">
                                    {isResend ? "🔁" : "📤"} {s.target_name || s.to_email || "—"}
                                  </span>
                                  {isReplied && (
                                    <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-300">
                                      ✓ risposto
                                    </span>
                                  )}
                                  {!isReplied && isStale && (
                                    <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-rose-100 text-rose-700 border border-rose-300">
                                      ⚠️ &gt; 5 giorni
                                    </span>
                                  )}
                                  {isResend && (
                                    <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-100 text-amber-700 border border-amber-300">
                                      rinvio
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5 truncate" title={s.to_email}>
                                  ✉️ {s.to_email || "—"}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">
                                  🕒 {formatDateTime(s.sent_at)}
                                  {s.attachments_count > 0 && <span className="ml-2 text-slate-600">· 📎 {s.attachments_count} allegat{s.attachments_count === 1 ? "o" : "i"}</span>}
                                  {s.sender_name && <span className="ml-2 text-slate-400">· da {s.sender_name}</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setResendConfirm({ qid: q.id, sendId: s.id, partnerName: s.target_name || s.to_email || "partner" }); }}
                                  className="text-[10px] text-blue-700 hover:text-blue-900 hover:underline font-medium"
                                  data-testid={`partner-comm-resend-${s.id}`}
                                  title="Reinvia lo stesso messaggio a questo partner"
                                >
                                  🔁 Rinvia
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-2 text-right">
                        <button
                          type="button"
                          onClick={() => openSendsHistory(q)}
                          className="text-[10px] text-blue-700 hover:underline"
                          data-testid={`partner-comm-open-full-${q.id}`}
                        >
                          Apri storico completo →
                        </button>
                      </div>
                    </div>
                  </details>
                )}
                {/* Tracciabilità invii a broker/compagnie */}
                <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3" data-testid={`sends-${q.id}`}>
                  <p className="text-[10px] uppercase tracking-wide text-emerald-800 font-semibold mb-2 inline-flex items-center gap-1.5">
                    <Send className="h-3 w-3" /> Tracciabilità invii a partner ({(sends[q.id] || []).length})
                  </p>
                  {(sends[q.id] || []).length === 0 ? (
                    <p className="text-xs text-slate-500">Nessun invio registrato.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {(sends[q.id] || []).map((s) => {
                        // Calcola giorni dall'invio
                        let daysWaiting = null;
                        if (!s.replied_at && s.sent_at) {
                          try {
                            const sent = new Date(s.sent_at);
                            daysWaiting = Math.floor((Date.now() - sent.getTime()) / 86400000);
                          } catch { /* ignore */ }
                        }
                        const isStale = daysWaiting !== null && daysWaiting >= 5;
                        const rowCls = s.replied_at
                          ? "bg-emerald-100 border border-emerald-300"
                          : isStale
                          ? "bg-rose-50 border border-rose-300"
                          : "bg-white border border-emerald-100";
                        return (
                          <li key={s.id} className={`flex items-start justify-between gap-3 text-xs rounded px-2.5 py-1.5 ${rowCls}`}>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-800 truncate">
                              {s.target_type === "broker" ? "🤝 " : "🏢 "}
                              {s.target_name}
                              <span className="ml-2 text-[10px] font-normal text-slate-500">{s.to_email}</span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              da <strong>{s.sender_name}</strong> · {formatDateTime(s.sent_at)}
                              {s.share_contacts ? " · recapiti inclusi" : " · senza recapiti"}
                              {s.attachments_count > 0 && ` · ${s.attachments_count} allegat${s.attachments_count === 1 ? "o" : "i"}`}
                            </div>
                            {Array.isArray(s.attachments_filenames) && s.attachments_filenames.length > 0 && (
                              <div className="text-[10px] text-slate-400 mt-0.5 truncate">
                                📎 {s.attachments_filenames.join(", ")}
                              </div>
                            )}
                            {s.replied_at && (
                              <div className="text-[10px] text-emerald-700 mt-0.5 font-semibold">
                                ✓ Risposta ricevuta il {formatDateTime(s.replied_at)}
                                {s.replied_by_name ? ` (segnalata da ${s.replied_by_name})` : ""}
                              </div>
                            )}
                            {s.resend_of && (
                              <div className="text-[10px] text-blue-700 mt-0.5 font-semibold inline-flex items-center gap-1">
                                🔁 Reinvio di un invio precedente
                              </div>
                            )}
                            {!s.replied_at && isStale && (
                              <div className="text-[10px] text-rose-700 mt-0.5 font-bold inline-flex items-center gap-1" data-testid={`stale-${s.id}`}>
                                ⚠️ In attesa da {daysWaiting} giorni — sollecita il partner
                              </div>
                            )}
                            {!s.replied_at && !isStale && daysWaiting !== null && daysWaiting >= 2 && (
                              <div className="text-[10px] text-amber-700 mt-0.5 font-medium">
                                ⏳ In attesa da {daysWaiting} giorni
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 self-center">
                            <button
                              type="button"
                              data-testid={`mark-reply-${s.id}`}
                              onClick={() => markReplied(q.id, s.id, !!s.replied_at)}
                              className={`text-[11px] font-medium px-2 py-1 rounded whitespace-nowrap ${s.replied_at ? "bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
                            >
                              {s.replied_at ? "Annulla risposta" : "✉ Segnala risposta"}
                            </button>
                            <button
                              type="button"
                              data-testid={`resend-${s.id}`}
                              onClick={() => resendSend(q.id, s.id, s.target_name)}
                              title="Reinvia lo stesso pacchetto (stessi allegati e note)"
                              className="text-[11px] font-medium px-2 py-1 rounded whitespace-nowrap bg-white border border-blue-300 text-blue-700 hover:bg-blue-50"
                            >
                              🔁 Reinvia
                            </button>
                          </div>
                        </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {/* Sezione messaggi: chat diretta con il CLIENTE (separata dalla tracciabilità invii a partner sopra) */}
                <div className="rounded-md border-2 border-blue-300 bg-blue-50/40 p-3 mt-3" data-testid={`client-chat-${q.id}`}>
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-blue-200">
                    <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <MessageSquare className="h-3.5 w-3.5 text-blue-700" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-1">
                        💬 Conversazione con il cliente
                      </p>
                      <p className="text-[11px] text-blue-700">
                        {q.client_name ? <>Stai chattando con <strong>{q.client_name}</strong></> : "Messaggi diretti con il cliente"} — questi messaggi <strong>NON</strong> vengono inviati al broker.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin">
                    {messages.length === 0 && <p className="text-xs text-slate-400">Nessun messaggio. Scrivi tu il primo al cliente.</p>}
                    {messages.map((m) => (
                      <div key={m.id} className={`flex ${m.author_role === "cliente" ? "justify-start" : "justify-end"}`}>
                        <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.author_role === "cliente" ? "bg-white border border-slate-200" : "bg-blue-600 text-white"}`}>
                          <div className="text-[10px] uppercase tracking-wide opacity-70 mb-0.5">
                            {m.author_role === "cliente" ? "👤 " : "🏢 "}{m.author_name} · {formatDateTime(m.created_at)}
                          </div>
                          <div className="whitespace-pre-line">{m.body}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                  {chatTemplates.length > 0 && (() => {
                    const suggestion = suggestTemplate(q);
                    return (
                      <div className="w-full mb-1.5">
                        {suggestion && (
                          <div className="mb-2 rounded-md bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 p-2 flex items-center gap-2 flex-wrap" data-testid={`tpl-suggestion-${q.id}`}>
                            <span className="text-base">💡</span>
                            <div className="flex-1 min-w-0 text-xs">
                              <div className="font-semibold text-amber-900">Template suggerito: {suggestion.tpl.title}</div>
                              <div className="text-[10px] text-amber-700 italic">{suggestion.reason}</div>
                            </div>
                            <button
                              type="button"
                              data-testid={`apply-suggestion-${q.id}`}
                              onClick={() => applyTemplateAndLog(suggestion.tpl, q, suggestion.tpl.id)}
                              className="text-[11px] font-semibold px-2 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
                            >
                              ⚡ Inserisci subito
                            </button>
                          </div>
                        )}
                        <details className="group">
                          <summary className="cursor-pointer text-[11px] text-blue-700 font-semibold hover:text-blue-900 inline-flex items-center gap-1 select-none">
                            ⚡ Template messaggi rapidi ({chatTemplates.length})
                          </summary>
                          <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid={`templates-${q.id}`}>
                            {chatTemplates.map((t) => {
                              const isSuggested = suggestion?.tpl?.id === t.id;
                              return (
                                <button
                                  key={t.id}
                                  type="button"
                                  data-testid={`template-${t.id}`}
                                  onClick={() => applyTemplateAndLog(t, q, suggestion?.tpl?.id)}
                                  title={applyTemplateVars(t.body, q) + (templateUsageStats.by_template[t.id]?.used_count ? ` · usato ${templateUsageStats.by_template[t.id].used_count} volte` : "")}
                                  className={`text-left text-[11px] px-2 py-1.5 rounded border transition-colors truncate ${
                                    isSuggested
                                      ? "border-amber-400 bg-amber-50 hover:bg-amber-100 font-semibold ring-1 ring-amber-300"
                                      : "border-blue-200 bg-white hover:bg-blue-50 hover:border-blue-400"
                                  }`}
                                >
                                  {isSuggested && "⭐ "}{t.title}
                                  {templateUsageStats.by_template[t.id]?.used_count > 0 && (
                                    <span className="ml-1 text-[9px] font-normal text-slate-500">· {templateUsageStats.by_template[t.id].used_count}×</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-[10px] text-slate-500 mt-1.5 italic">
                            💡 Variabili sostituite automaticamente: <code className="bg-slate-100 px-1 rounded">{"{nome_cliente}"}</code>, <code className="bg-slate-100 px-1 rounded">{"{numero_preventivo}"}</code>, <code className="bg-slate-100 px-1 rounded">{"{tipo_polizza}"}</code>, <code className="bg-slate-100 px-1 rounded">{"{data_oggi}"}</code>
                          </p>
                        </details>
                      </div>
                    );
                  })()}
                </div>
                <div className="flex gap-2">
                  <Textarea
                    data-testid={`reply-input-${q.id}`}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Scrivi un messaggio al cliente o scegli un template…"
                    rows={2}
                    className="flex-1"
                  />
                  <Button data-testid={`send-reply-${q.id}`} onClick={() => sendReply(q.id)} disabled={!reply.trim()} className="bg-blue-600 hover:bg-blue-700 text-white self-end">
                    <Send className="h-4 w-4 mr-1.5" /> Invia al cliente
                  </Button>
                </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!sendOpen} onOpenChange={(o) => { if (!o) setSendOpen(null); else if (!quotePinsLoadedRef.current) loadQuotePins(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-emerald-600" /> Invia richiesta preventivo a broker / compagnie</DialogTitle></DialogHeader>
          {sendOpen && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Seleziona uno o più partner: invieremo a ciascuno un&apos;email separata con il riepilogo della richiesta e (opzionalmente) gli allegati.
              </p>

              {/* Lista BROKER / AGENZIE */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Broker / Agenzie</Label>
                  <button type="button" data-testid="send-toggle-all-brokers"
                    onClick={() => {
                      const allIds = brokers.filter((b) => b.email || b.pec).map((b) => b.id);
                      const allSelected = allIds.every((id) => sendOpen.targets.some((t) => t.type === "broker" && t.id === id));
                      setSendOpen((s) => ({ ...s, targets: allSelected
                        ? s.targets.filter((t) => t.type !== "broker")
                        : [...s.targets.filter((t) => t.type !== "broker"), ...allIds.map((id) => ({ type: "broker", id }))],
                      }));
                    }}
                    className="text-xs text-blue-600 hover:underline">Seleziona / Deseleziona tutti</button>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50/50 max-h-44 overflow-y-auto p-2 space-y-1" data-testid="send-broker-list">
                  {brokers.length === 0 && <p className="text-xs text-slate-500 px-2 py-1">Nessun broker configurato.</p>}
                  {(() => {
                    const { pinned, rest } = sortWithPins(brokers, quotePins.brokers);
                    const renderRow = (b, pinnedFlag) => {
                      const hasEmail = b.email || b.pec;
                      const checked = sendOpen.targets.some((t) => t.type === "broker" && t.id === b.id);
                      return (
                        <label key={b.id} className={`flex items-center gap-2 px-2 py-1 rounded text-sm cursor-pointer ${hasEmail ? "hover:bg-blue-50" : "opacity-50 cursor-not-allowed"} ${pinnedFlag ? "bg-amber-50/60" : ""}`}>
                          <input
                            type="checkbox"
                            data-testid={`send-broker-${b.id}`}
                            checked={checked}
                            disabled={!hasEmail}
                            onChange={() => toggleSendTarget("broker", b.id)}
                            className="h-4 w-4 accent-emerald-600"
                          />
                          <span className="flex-1">{b.name}</span>
                          <span className="text-[11px] text-slate-500">{hasEmail ? (b.email || b.pec) : "no email"}</span>
                          <button
                            type="button"
                            data-testid={`pin-broker-${b.id}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleQuotePin("broker", b.id); }}
                            title={pinnedFlag ? "Rimuovi dai preferiti" : "Appunta in cima"}
                            aria-label={pinnedFlag ? "Rimuovi dai preferiti" : "Appunta in cima"}
                            className={`shrink-0 rounded p-1 transition-colors ${pinnedFlag ? "text-amber-600 bg-amber-100 hover:bg-amber-200" : "text-slate-400 hover:text-amber-600 hover:bg-amber-100"}`}
                          >
                            {pinnedFlag
                              ? <Pin className="h-3.5 w-3.5 fill-current" />
                              : <Pin className="h-3.5 w-3.5" />}
                          </button>
                        </label>
                      );
                    };
                    return (
                      <>
                        {pinned.map((b) => renderRow(b, true))}
                        {pinned.length > 0 && rest.length > 0 && (
                          <div className="flex items-center gap-2 my-1 px-2" data-testid="send-broker-pins-separator">
                            <div className="h-px flex-1 bg-slate-300" />
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Altri</span>
                            <div className="h-px flex-1 bg-slate-300" />
                          </div>
                        )}
                        {rest.map((b) => renderRow(b, false))}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Lista COMPAGNIE */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Compagnie assicurative</Label>
                  <button type="button" data-testid="send-toggle-all-companies"
                    onClick={() => {
                      const allIds = companies.filter((c) => c.email || c.pec).map((c) => c.id);
                      const allSelected = allIds.every((id) => sendOpen.targets.some((t) => t.type === "company" && t.id === id));
                      setSendOpen((s) => ({ ...s, targets: allSelected
                        ? s.targets.filter((t) => t.type !== "company")
                        : [...s.targets.filter((t) => t.type !== "company"), ...allIds.map((id) => ({ type: "company", id }))],
                      }));
                    }}
                    className="text-xs text-blue-600 hover:underline">Seleziona / Deseleziona tutte</button>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50/50 max-h-44 overflow-y-auto p-2 space-y-1" data-testid="send-company-list">
                  {companies.length === 0 && <p className="text-xs text-slate-500 px-2 py-1">Nessuna compagnia configurata.</p>}
                  {(() => {
                    const { pinned, rest } = sortWithPins(companies, quotePins.companies);
                    const renderRow = (c, pinnedFlag) => {
                      const hasEmail = c.email || c.pec;
                      const checked = sendOpen.targets.some((t) => t.type === "company" && t.id === c.id);
                      return (
                        <label key={c.id} className={`flex items-center gap-2 px-2 py-1 rounded text-sm cursor-pointer ${hasEmail ? "hover:bg-blue-50" : "opacity-50 cursor-not-allowed"} ${pinnedFlag ? "bg-amber-50/60" : ""}`}>
                          <input
                            type="checkbox"
                            data-testid={`send-company-${c.id}`}
                            checked={checked}
                            disabled={!hasEmail}
                            onChange={() => toggleSendTarget("company", c.id)}
                            className="h-4 w-4 accent-emerald-600"
                          />
                          <span className="flex-1">{c.name}</span>
                          <span className="text-[11px] text-slate-500">{hasEmail ? (c.email || c.pec) : "no email"}</span>
                          <button
                            type="button"
                            data-testid={`pin-company-${c.id}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleQuotePin("company", c.id); }}
                            title={pinnedFlag ? "Rimuovi dai preferiti" : "Appunta in cima"}
                            aria-label={pinnedFlag ? "Rimuovi dai preferiti" : "Appunta in cima"}
                            className={`shrink-0 rounded p-1 transition-colors ${pinnedFlag ? "text-amber-600 bg-amber-100 hover:bg-amber-200" : "text-slate-400 hover:text-amber-600 hover:bg-amber-100"}`}
                          >
                            {pinnedFlag
                              ? <Pin className="h-3.5 w-3.5 fill-current" />
                              : <Pin className="h-3.5 w-3.5" />}
                          </button>
                        </label>
                      );
                    };
                    return (
                      <>
                        {pinned.map((c) => renderRow(c, true))}
                        {pinned.length > 0 && rest.length > 0 && (
                          <div className="flex items-center gap-2 my-1 px-2" data-testid="send-company-pins-separator">
                            <div className="h-px flex-1 bg-slate-300" />
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Altre</span>
                            <div className="h-px flex-1 bg-slate-300" />
                          </div>
                        )}
                        {rest.map((c) => renderRow(c, false))}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Sommario destinatari */}
              <div className="text-xs text-slate-700 bg-slate-100 rounded px-3 py-1.5" data-testid="send-summary">
                Destinatari selezionati: <strong>{sendOpen.targets.length}</strong>
                {sendOpen.targets.length > 0 && (
                  <span className="ml-2 text-slate-500">
                    ({sendOpen.targets.filter((t) => t.type === "broker").length} broker, {sendOpen.targets.filter((t) => t.type === "company").length} compagnie)
                  </span>
                )}
              </div>

              <div>
                <Label>Note aggiuntive (facoltativo)</Label>
                <Textarea
                  data-testid="send-notes"
                  rows={3}
                  value={sendOpen.extra_notes}
                  onChange={(e) => setSendOpen({ ...sendOpen, extra_notes: e.target.value })}
                  placeholder="Es. Cliente già nostro, urgenza per scadenza..."
                />
              </div>

              {/* 🆕 10/07/2026 — Marcatore "Urgente" per PDF/email al broker */}
              <label className="flex items-start gap-2 cursor-pointer text-sm rounded-md border border-rose-300 bg-rose-50 p-3 hover:bg-rose-100/50">
                <input
                  type="checkbox"
                  data-testid="send-urgent"
                  checked={!!sendOpen.urgent}
                  onChange={(e) => setSendOpen({ ...sendOpen, urgent: e.target.checked })}
                  className="mt-1 h-4 w-4 cursor-pointer accent-rose-600"
                />
                <span>
                  <strong className="text-rose-800">🚨 Segnala come URGENTE</strong>
                  <span className="block text-xs text-slate-600 mt-0.5">
                    Aggiunge banner rosso &quot;URGENTE&quot; in cima al PDF/email del broker e riduce la deadline consigliata di risposta a 2 giorni.
                  </span>
                </span>
              </label>

              {/* Allegati al partner — 🆕 Feb 2026: selezione per singolo file */}
              <div className="rounded-md border border-sky-300 bg-sky-50 p-3">
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    data-testid="send-include-attachments"
                    checked={!!sendOpen.include_attachments}
                    onChange={(e) => setSendOpen({ ...sendOpen, include_attachments: e.target.checked })}
                    className="mt-1 h-4 w-4 cursor-pointer accent-sky-600"
                  />
                  <span>
                    <strong>Allega documenti al partner</strong>
                    <span className="block text-xs text-slate-600 mt-0.5">
                      Seleziona i file da inviare a ciascun partner. Tetto: 18 MB totali per email.
                    </span>
                  </span>
                </label>
                {sendOpen.include_attachments && (
                  <div className="mt-2 pl-6" data-testid="send-attachments-list">
                    {(!sendOpen.available_attachments || sendOpen.available_attachments.length === 0) ? (
                      <p className="text-xs text-slate-500 italic">Nessun allegato caricato per questo preventivo.</p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] text-slate-500">
                            {sendOpen.selected_attachment_ids?.length || 0} / {sendOpen.available_attachments.length} selezionat{sendOpen.selected_attachment_ids?.length === 1 ? "o" : "i"}
                          </span>
                          <button
                            type="button"
                            data-testid="send-toggle-all-attachments"
                            onClick={() => {
                              const allIds = sendOpen.available_attachments.map((a) => a.id);
                              const allSelected = allIds.every((id) => (sendOpen.selected_attachment_ids || []).includes(id));
                              setSendOpen((s) => ({ ...s, selected_attachment_ids: allSelected ? [] : allIds }));
                            }}
                            className="text-xs text-sky-700 hover:underline"
                          >
                            Seleziona / Deseleziona tutti
                          </button>
                        </div>
                        <div className="space-y-1 max-h-40 overflow-y-auto rounded border border-sky-200 bg-white p-2">
                          {sendOpen.available_attachments.map((a) => {
                            const checked = (sendOpen.selected_attachment_ids || []).includes(a.id);
                            const fname = a.original_filename || a.filename || "allegato";
                            const size = a.size_bytes || a.size || 0;
                            return (
                              <label key={a.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-sky-50 cursor-pointer text-sm">
                                <input
                                  type="checkbox"
                                  data-testid={`send-attachment-${a.id}`}
                                  checked={checked}
                                  onChange={() => setSendOpen((s) => {
                                    const cur = s.selected_attachment_ids || [];
                                    const next = checked ? cur.filter((x) => x !== a.id) : [...cur, a.id];
                                    return { ...s, selected_attachment_ids: next };
                                  })}
                                  className="h-4 w-4 accent-sky-600 shrink-0"
                                />
                                <span className="flex-1 truncate" title={fname}>{fname}</span>
                                <span className="text-[10px] text-slate-500 shrink-0">{formatFileSize(size)}</span>
                              </label>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Privacy: includere recapiti del cliente */}
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    data-testid="send-share-contacts"
                    checked={!!sendOpen.share_contacts}
                    onChange={(e) => setSendOpen({ ...sendOpen, share_contacts: e.target.checked })}
                    className="mt-1 h-4 w-4 cursor-pointer accent-amber-600"
                  />
                  <span>
                    <strong>Includi email e telefono del cliente</strong>
                    <span className="block text-xs text-slate-600 mt-0.5">
                      Privacy: se non spuntato, ai partner inviamo solo anagrafica e dettagli tecnici, senza recapiti diretti del cliente.
                    </span>
                  </span>
                </label>
              </div>

              {/* WhatsApp automatico dopo invio email */}
              <div className="rounded-md border border-green-300 bg-green-50 p-3">
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    data-testid="send-also-whatsapp"
                    checked={!!sendOpen.also_whatsapp}
                    onChange={(e) => setSendOpen({ ...sendOpen, also_whatsapp: e.target.checked })}
                    className="mt-1 h-4 w-4 cursor-pointer accent-green-600"
                  />
                  <span>
                    <strong>📱 Apri WhatsApp dopo l&apos;invio</strong>
                    <span className="block text-xs text-slate-600 mt-0.5">
                      Dopo l&apos;email apre una scheda WhatsApp per ogni partner con cellulare/telefono, con messaggio precompilato e <strong>link al PDF condivisibile</strong> (valido 30 giorni).
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(null)}>Annulla</Button>
            <Button data-testid="send-to-partner-btn" onClick={submitSendToPartner} disabled={!sendOpen?.targets?.length || partnerSendBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Send className="h-4 w-4 mr-2" /> {partnerSendBusy ? "Invio in corso…" : `Invia a ${sendOpen?.targets?.length || 0} partner`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog OTP per ELIMINAZIONE DEFINITIVA dal cestino */}
      <Dialog open={bulkStage === "otp_permanent"} onOpenChange={(o) => { if (!o && !bulkBusy) { setBulkStage(null); setBulkOtpCode(""); } }}>
        <DialogContent data-testid="quotes-permanent-otp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="h-5 w-5" /> Elimina DEFINITIVAMENTE dal cestino
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border-2 border-rose-300 bg-rose-50 p-4">
              <p className="text-rose-900 font-semibold">
                Stai per eliminare definitivamente <span className="text-2xl font-extrabold">{(bulkOtpInfo?.ids || []).length}</span> {(bulkOtpInfo?.ids || []).length === 1 ? "preventivo" : "preventivi"}.
              </p>
              <p className="text-rose-800 text-xs mt-1">
                I dati saranno rimossi dal database. <strong>Operazione IRREVERSIBILE.</strong>
              </p>
            </div>
            <p className="text-slate-700">
              Codice inviato a <strong>{bulkOtpEmail || bulkOtpInfo?.email}</strong>.
            </p>
            <Input
              data-testid="quotes-permanent-otp-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="6 cifre"
              value={bulkOtpCode}
              onChange={(e) => setBulkOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="text-center text-2xl font-mono tracking-[0.5em] py-6"
              autoFocus
            />
            <button
              type="button"
              onClick={() => requestPermanentDeleteOtp(bulkOtpInfo?.ids || [])}
              disabled={bulkBusy}
              className="text-xs text-blue-600 hover:underline"
              data-testid="quotes-permanent-otp-resend"
            >
              Rigenera codice
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkStage(null); setBulkOtpCode(""); }} disabled={bulkBusy}>Annulla</Button>
            <Button variant="destructive" onClick={confirmPermanentDelete} disabled={bulkBusy || bulkOtpCode.length < 4} data-testid="quotes-permanent-confirm">
              {bulkBusy ? "Eliminazione…" : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog invio allegati preventivo via email */}
      <Dialog open={qattSendOpen} onOpenChange={(o) => { if (!o && !qattSendBusy) setQattSendOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="quote-send-attachments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              📧 Invia allegati preventivo via email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded p-2.5 text-xs">
              <strong>{Object.values(qattSelected).filter(Boolean).length}</strong> file selezionati:
              <ul className="mt-1 list-disc list-inside text-slate-700 max-h-24 overflow-y-auto">
                {pendingExisting.filter((a) => qattSelected[a.id]).map((a) => (
                  <li key={a.id} className="truncate">{a.filename}</li>
                ))}
              </ul>
            </div>
            <div>
              <Label className="text-xs">Destinatario *</Label>
              <Input type="email" value={qattSendForm.to_email} onChange={(e) => setQattSendForm({ ...qattSendForm, to_email: e.target.value })} placeholder="cliente@example.it" data-testid="quote-send-email" />
            </div>
            <div>
              <Label className="text-xs">Oggetto</Label>
              <Input value={qattSendForm.subject} onChange={(e) => setQattSendForm({ ...qattSendForm, subject: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Messaggio</Label>
              <Textarea rows={5} value={qattSendForm.body} onChange={(e) => setQattSendForm({ ...qattSendForm, body: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQattSendOpen(false)} disabled={qattSendBusy}>Annulla</Button>
            <Button
              data-testid="confirm-quote-send-attachments"
              onClick={confirmQuoteAttSend}
              disabled={qattSendBusy || !qattSendForm.to_email}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {qattSendBusy ? "Invio…" : "📧 Invia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog: Forza pagamento offerta collegata */}
      <Dialog open={!!forcePaidOffer} onOpenChange={(o) => { if (!o) setForcePaidOffer(null); }}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="force-paid-quote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-fuchsia-700">
              <Zap className="h-5 w-5" /> Forza pagamento offerta
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-fuchsia-200 bg-fuchsia-50 p-3 text-sm text-fuchsia-900">
              Sei sicuro di voler <strong>forzare il pagamento</strong> dell&apos;offerta{" "}
              <strong>{forcePaidOffer?.offer_number}</strong>
              {forcePaidOffer?.amount != null && <> ({formatCurrency(forcePaidOffer?.amount)})</>}?
              <div className="mt-2 text-[12px] text-fuchsia-800">
                ⚠️ L&apos;offerta verrà <strong>immediatamente segnata come pagata</strong>: la polizza verrà generata in bozza e la ricevuta inviata via email al cliente.
              </div>
            </div>
            <div>
              <Label>Metodo di pagamento</Label>
              <PaymentMethodSelect value={forcePaidMethod} onChange={setForcePaidMethod} testid="force-paid-quote-method" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForcePaidOffer(null)} disabled={forcePaidBusy}>Annulla</Button>
            <Button
              data-testid="force-paid-quote-confirm"
              disabled={forcePaidBusy}
              onClick={async () => {
                if (!forcePaidOffer) return;
                setForcePaidBusy(true);
                try {
                  const { data } = await api.post(`/quote-offers/${forcePaidOffer.id}/mark-paid`, { payment_method: forcePaidMethod });
                  toast.success(`Offerta ${forcePaidOffer.offer_number} forzata a "pagata"${data?.policy_id ? " · polizza creata" : ""}`);
                  const policyId = data?.policy_id || null;
                  setForcePaidOffer(null);
                  load();
                  if (policyId) {
                    toast.info("📝 Polizza generata in bozza → completala con N° polizza e compagnia", { duration: 6000 });
                    setTimeout(() => navigate(`/policies?focus=${policyId}`), 300);
                  }
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setForcePaidBusy(false);
                }
              }}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
            >
              {forcePaidBusy ? "Forzo…" : "Sì, forza pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog: duplicati targa veicolo */}
      <Dialog open={dupCheckOpen} onOpenChange={(o) => { if (!o) { setDupCheckOpen(false); setDupCheckResult(null); } }}>        <DialogContent className="max-w-2xl" data-testid="duplicate-plate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              ⚠️ {dupCheckContext === "extract" ? "Targa già presente in altri preventivi" : "Trovati preventivi con la stessa targa"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              Esiston{(dupCheckResult?.items?.length || 0) === 1 ? "o" : "o"} <strong>{dupCheckResult?.items?.length || 0}</strong> preventiv{(dupCheckResult?.items?.length || 0) === 1 ? "o" : "i"} con targa <code className="px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded font-mono font-semibold">{dupCheckResult?.plate}</code>.
              Scegli cosa fare:
            </p>
            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {(dupCheckResult?.items || []).map((d) => (
                <div key={d.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`dup-row-${d.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      {d.quote_number ? `#${d.quote_number} · ` : ""}{d.client_name || "—"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {d.type?.toUpperCase()} · creato {formatDate(d.created_at)} · stato <span className="font-mono">{d.status}</span>
                      {d.policy_id && <span className="ml-1 inline-flex items-center gap-1 text-emerald-700 font-semibold">· ✓ convertito in polizza</span>}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid={`dup-delete-${d.id}`}
                    disabled={!!d.policy_id}
                    title={d.policy_id ? "Non eliminabile: collegato a una polizza" : "Sposta nel cestino"}
                    className="text-red-700 border-red-200 hover:bg-red-50"
                    onClick={async () => {
                      try {
                        await api.delete(`/quotes/${d.id}`);
                        toast.success(`Preventivo ${d.quote_number || d.id.slice(0, 8)} spostato nel cestino`);
                        setDupCheckResult((prev) => prev ? { ...prev, items: prev.items.filter((x) => x.id !== d.id) } : prev);
                        load();
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}
                  >
                    🗑 Elimina
                  </Button>
                </div>
              ))}
              {(dupCheckResult?.items?.length || 0) === 0 && (
                <div className="px-3 py-6 text-center text-sm text-slate-500">Nessun duplicato residuo. Puoi procedere al salvataggio.</div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            {dupCheckContext === "extract" ? (
              <Button
                type="button"
                data-testid="dup-ack"
                className="bg-slate-700 hover:bg-slate-800"
                onClick={() => { setDupCheckOpen(false); setDupCheckResult(null); }}
              >
                Ho capito, continua compilazione
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="dup-cancel"
                  onClick={() => { setDupCheckOpen(false); setDupCheckResult(null); }}
                >
                  Annulla salvataggio
                </Button>
                <Button
                  type="button"
                  data-testid="dup-continue"
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => {
                    setDupCheckOpen(false);
                    setDupCheckResult(null);
                    setDupCheckBypassed(true);
                    setTimeout(() => submit(), 0);
                  }}
                >
                  Continua e salva comunque
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Link Accettazione Pubblico */}
      <Dialog open={!!acceptLinkDlg} onOpenChange={(o) => { if (!o) setAcceptLinkDlg(null); }}>
        <DialogContent className="max-w-lg" data-testid="accept-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-indigo-600" /> Link accettazione preventivo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Invia questo link al cliente. Cliccandolo potrà vedere il preventivo e cliccare &quot;Accetto&quot; — il preventivo passerà automaticamente in stato <strong>Accettato</strong>, abilitando la conversione in polizza.</p>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 break-all text-xs font-mono text-slate-700" data-testid="accept-link-url">
              {acceptLinkDlg?.url}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="copy-accept-link" onClick={copyAcceptLink} variant="outline" className="flex-1">
                📋 Copia link
              </Button>
              {acceptLinkDlg?.whatsapp_url && (
                <Button
                  data-testid="whatsapp-accept-link"
                  onClick={() => window.open(acceptLinkDlg.whatsapp_url, "_blank")}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  💬 WhatsApp cliente
                </Button>
              )}
              {acceptLinkDlg?.quote?.client_email && (
                <Button
                  data-testid="email-accept-link"
                  onClick={() => {
                    const subj = `Preventivo ${acceptLinkDlg.quote.quote_number || ""}`;
                    const body = acceptLinkDlg.message;
                    window.location.href = `mailto:${acceptLinkDlg.quote.client_email}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
                  }}
                  variant="outline"
                  className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  ✉ Email cliente
                </Button>
              )}
            </div>
            <details className="mt-3">
              <summary className="text-xs text-slate-500 cursor-pointer">Anteprima messaggio</summary>
              <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-2 mt-2 whitespace-pre-wrap">{acceptLinkDlg?.message}</pre>
            </details>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptLinkDlg(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 🆕 Dialog scelta intestatario (veicolo cointestato) */}
      <Dialog open={!!ownerChoice} onOpenChange={(o) => { if (!o) setOwnerChoice(null); }}>
        <DialogContent className="max-w-lg" data-testid="owner-choice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🚗 Veicolo cointestato — scegli l&apos;intestatario
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-700">
              Il libretto <span className="font-mono">{ownerChoice?.filename}</span> riporta {ownerChoice?.owners?.length || 0} possibili intestatari.
              Scegli a chi intestare il preventivo: i dati dell&apos;intestatario scelto popoleranno il form.
            </p>
            <div className="space-y-2">
              {(ownerChoice?.owners || []).map((owner, i) => {
                const matched = (clients || []).find((c) => (c.fiscal_code || "").toUpperCase() === String(owner.sub || "").toUpperCase().trim());
                return (
                  <button
                    key={i}
                    type="button"
                    data-testid={`owner-choice-${i}`}
                    onClick={() => {
                      // Applica i campi owner_* scelti al form. Gli intestatari NON scelti
                      // vengono salvati in `details.co_owners` (oggetti con suffisso senza `owner_`)
                      // così l'operatore può vederli ed eventualmente promuoverli in seguito.
                      const otherOwners = (ownerChoice.owners || [])
                        .filter((_, j) => j !== i)
                        .map((other) => ({
                          name: other.data.owner_name || "",
                          surname: other.data.owner_surname || "",
                          fiscal_code: other.data.owner_fiscal_code || "",
                          birth_date: other.data.owner_birth_date || "",
                          birth_place: other.data.owner_birth_place || "",
                          address: other.data.owner_address || "",
                          house_number: other.data.owner_house_number || "",
                          city: other.data.owner_city || "",
                          province: other.data.owner_province || "",
                          cap: other.data.owner_cap || "",
                        }));
                      setForm((f) => ({
                        ...f,
                        client_id: matched ? matched.id : f.client_id,
                        details: {
                          ...(f.details || {}),
                          ...owner.data,
                          co_owners: otherOwners,
                        },
                      }));
                      if (matched) {
                        toast.success(`Intestatario "${owner.label}" associato (cliente in anagrafica). ${otherOwners.length} cointestatario/i salvato/i.`);
                      } else {
                        toast.success(`Intestatario "${owner.label}" applicato. ${otherOwners.length} cointestatario/i salvato/i nella scheda.`);
                      }
                      setOwnerChoice(null);
                    }}
                    className="w-full text-left border border-slate-300 hover:border-blue-500 hover:bg-blue-50 rounded-md px-3 py-2 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold bg-blue-600 text-white rounded-full h-6 w-6 inline-flex items-center justify-center shrink-0">
                        {i === 0 ? "1°" : `${i + 1}°`}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold uppercase">{owner.label}</div>
                        <div className="text-[11px] font-mono text-slate-500">{owner.sub || "(CF non disponibile)"}</div>
                        {matched && (
                          <div className="text-[10px] text-emerald-700 font-semibold mt-0.5">
                            ✓ Cliente presente in anagrafica
                          </div>
                        )}
                        <div className="text-[11px] text-slate-600 mt-1">
                          {owner.data.owner_birth_place ? `Nato a ${owner.data.owner_birth_place}` : null}
                          {owner.data.owner_city ? `${owner.data.owner_birth_place ? " · " : ""}Residente a ${owner.data.owner_city}${owner.data.owner_province ? ` (${owner.data.owner_province})` : ""}` : null}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="owner-choice-cancel"
              onClick={() => {
                setOwnerChoice(null);
                toast.info("Nessun intestatario scelto: i dati del veicolo sono comunque stati applicati.");
              }}
            >
              Decido dopo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog: catalogo veicoli del cliente per importazione rapida */}
      <Dialog open={vehiclesCatalogOpen} onOpenChange={setVehiclesCatalogOpen}>
        <DialogContent className="max-w-3xl" data-testid="vehicles-catalog-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-800">
              <Car className="h-5 w-5" /> Veicoli collegati al cliente
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {vehiclesCatalogLoading ? (
              <p className="text-sm text-slate-500 text-center py-6">Caricamento veicoli…</p>
            ) : vehiclesCatalog.length === 0 ? (
              <div className="text-center py-8">
                <Car className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Nessun veicolo trovato nelle polizze o preventivi del cliente.</p>
                <p className="text-xs text-slate-400 mt-1">Puoi comunque inserire i dati manualmente nel form.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 mb-2">Clicca un veicolo per importarne i dati nel preventivo. Fonti: polizze / preventivi / anagrafica.</p>
                {vehiclesCatalog.map((v) => (
                  <button
                    key={v.plate}
                    type="button"
                    data-testid={`import-vehicle-${v.plate}`}
                    onClick={() => importVehicleIntoForm(v)}
                    className="w-full text-left border border-slate-200 hover:border-indigo-500 hover:bg-indigo-50/40 rounded-lg p-3 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-extrabold tracking-wider px-2.5 py-1 rounded-md border-2 border-slate-500 bg-slate-200 text-black uppercase shadow-sm">
                          🚗 {v.plate}
                        </span>
                        <span className="text-sm font-semibold text-slate-800">
                          {[v.brand, v.model].filter(Boolean).join(" ") || <span className="text-slate-400 italic">Marca/modello non indicati</span>}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(v.sources || []).map((s) => (
                          <span
                            key={s}
                            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                              s === "policy" ? "bg-blue-100 text-blue-800 border-blue-300"
                                : s === "quote" ? "bg-amber-100 text-amber-800 border-amber-300"
                                : "bg-slate-100 text-slate-700 border-slate-300"
                            }`}
                          >
                            {s === "policy" ? "polizza" : s === "quote" ? "preventivo" : "anagrafica"}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                      {v.first_registration && <span>🗓️ 1ª imm.: {formatDate(v.first_registration)}</span>}
                      {v.purchase_date && <span>🛒 Acquisto: {formatDate(v.purchase_date)}</span>}
                      {v.fuel && <span>⛽ {v.fuel}</span>}
                      {v.body_type && <span>🚘 {v.body_type}</span>}
                      {v.kw && <span>⚡ {v.kw} kW</span>}
                      {v.fiscal_power && <span>🐎 {v.fiscal_power} CV</span>}
                      {v.displacement && <span>🔧 {v.displacement} cc</span>}
                      {v.chassis_number && <span className="font-mono">🔩 {v.chassis_number}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="vehicles-catalog-close" onClick={() => setVehiclesCatalogOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog: storico invii al partner per un preventivo */}
      <Dialog open={!!sendsHistoryOpen} onOpenChange={(o) => { if (!o) setSendsHistoryOpen(null); }}>
        <DialogContent className="max-w-2xl" data-testid="sends-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-800">
              <Send className="h-5 w-5" /> Invii al partner
              {sendsHistoryOpen?.quote?.quote_number && (
                <span className="text-xs font-mono bg-blue-100 text-blue-700 px-2 py-0.5 rounded ml-1">
                  {sendsHistoryOpen.quote.quote_number}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {sendsHistoryOpen?.loading ? (
              <p className="text-sm text-slate-500 text-center py-6">Caricamento invii…</p>
            ) : (sendsHistoryOpen?.sends || []).length === 0 ? (
              <div className="text-center py-8">
                <Send className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Nessun invio registrato per questo preventivo.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 mb-2">
                  {sendsHistoryOpen.sends.length} invii totali · ordinati dal più recente
                </p>
                {sendsHistoryOpen.sends.map((s) => {
                  const daysAgo = s.sent_at ? Math.floor((Date.now() - new Date(s.sent_at).getTime()) / 86400000) : null;
                  const replied = !!s.replied_at;
                  const isStale = !replied && daysAgo !== null && daysAgo > 5;
                  const partnerName = s.target_name || s.company_name || s.broker_name || "Partner sconosciuto";
                  return (
                    <div
                      key={s.id}
                      data-testid={`sends-history-item-${s.id}`}
                      className={`border rounded-lg p-3 ${
                        replied ? "bg-emerald-50 border-emerald-200"
                        : isStale ? "bg-rose-50 border-rose-300"
                        : "bg-amber-50 border-amber-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                            {s.target_type === "broker" ? "🤝" : "🏢"} {partnerName}
                            {s.is_resend && (
                              <span className="text-[10px] font-semibold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                                🔁 REINVIO
                              </span>
                            )}
                            {replied ? (
                              <span className="text-[10px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded">
                                ✓ RISPOSTA RICEVUTA
                              </span>
                            ) : isStale ? (
                              <span className="text-[10px] font-bold bg-rose-600 text-white px-1.5 py-0.5 rounded">
                                ⚠ {daysAgo}gg senza risposta
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded">
                                ⏳ IN ATTESA{daysAgo !== null ? ` (${daysAgo}gg)` : ""}
                              </span>
                            )}
                          </p>
                          <p className="text-[11px] text-slate-600 mt-1">
                            📅 Inviato: {s.sent_at ? formatDateTime(s.sent_at) : "—"}
                            {s.recipient_email && <> · 📧 {s.recipient_email}</>}
                          </p>
                          {s.replied_at && (
                            <p className="text-[11px] text-emerald-700 mt-0.5">
                              ✓ Risposto: {formatDateTime(s.replied_at)}
                            </p>
                          )}
                          {(s.attachment_count || 0) > 0 && (
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              📎 {s.attachment_count} allegat{s.attachment_count === 1 ? "o" : "i"}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`sends-history-resend-${s.id}`}
                          onClick={() => setResendConfirm({ qid: sendsHistoryOpen.quote.id, sendId: s.id, partnerName })}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50 shrink-0"
                          title="Reinvia lo stesso pacchetto a questo partner"
                        >
                          🔁 Reinvia
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="sends-history-close" onClick={() => setSendsHistoryOpen(null)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog conferma reinvio */}
      <Dialog open={!!resendConfirm} onOpenChange={(o) => { if (!o) setResendConfirm(null); }}>
        <DialogContent className="max-w-md" data-testid="resend-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              🔁 Confermi il reinvio?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>Stai per reinviare la richiesta preventivo a:</p>
            <p className="font-semibold bg-slate-50 border border-slate-200 rounded px-3 py-2">
              📨 {resendConfirm?.partnerName}
            </p>
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
              ℹ️ Verranno usati gli <strong>stessi allegati e note</strong> dell&apos;invio originale.
              Il partner potrebbe ricevere una comunicazione duplicata.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendConfirm(null)} data-testid="resend-confirm-cancel">
              Annulla
            </Button>
            <Button onClick={confirmResendSend} className="bg-blue-600 hover:bg-blue-700" data-testid="resend-confirm-ok">
              <Send className="h-4 w-4 mr-1.5" /> Sì, reinvia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 🆕 Dialog anteprima email prima dell'invio (partner) */}
      <EmailPreviewDialog
        open={!!emailPreviewCtx}
        onOpenChange={(o) => { if (!o) setEmailPreviewCtx(null); }}
        emailTypeKey={emailPreviewCtx?.typeKey}
        title={emailPreviewCtx?.title || "Anteprima email prima dell'invio"}
        fetchPreview={emailPreviewCtx?.fetchPreview || (async () => ({ subject: "", html_body: "", to: [] }))}
        onConfirmSend={async () => {
          if (emailPreviewCtx?.onConfirm) await emailPreviewCtx.onConfirm();
          setEmailPreviewCtx(null);
        }}
      />
      {/* 🆕 Anteprima inline PDF/immagine allegati (react-pdf) */}
      {AttachmentPreviewDialog}

      {/* 🆕 Feb 2026 — Overlay fullscreen notifica nuove offerte da partner online (con beep sonoro ripetuto).
          Resta visibile finché l'utente non clicca "Ho visto". */}
      {newPartnerOffersAlert.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          data-testid="partner-offer-notification-overlay"
        >
          <div className="bg-white rounded-2xl shadow-2xl ring-4 ring-rose-500/50 max-w-2xl w-full overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="bg-gradient-to-r from-rose-600 to-amber-500 text-white px-6 py-4 flex items-center gap-3 animate-pulse">
              <span className="text-3xl">🔔</span>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest opacity-90">Nuova offerta ricevuta</div>
                <div className="text-xl font-bold">
                  {newPartnerOffersAlert.length === 1
                    ? "1 offerta da partner online"
                    : `${newPartnerOffersAlert.length} offerte da partner online`}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 space-y-3 max-h-[55vh] overflow-y-auto" data-testid="partner-offer-notification-list">
              {newPartnerOffersAlert.map(({ offer, quote }) => (
                <div key={offer.id} className="rounded-lg border-2 border-rose-200 bg-rose-50/40 p-3" data-testid={`partner-offer-notification-item-${offer.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-rose-700 font-bold">Partner</div>
                      <div className="text-sm font-semibold text-slate-800 truncate" data-testid={`partner-offer-notification-partner-${offer.id}`}>
                        🤝 {offer.partner_name || offer.broker_name || offer.company_name || "Partner online"}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Cliente</div>
                      <div className="text-sm font-semibold text-slate-800 truncate" data-testid={`partner-offer-notification-client-${offer.id}`}>
                        👤 {quote?.client_name || offer.client_name || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Importo</div>
                      <div className="text-xl font-bold text-emerald-700 tabular-nums" data-testid={`partner-offer-notification-amount-${offer.id}`}>
                        {formatCurrency(offer.amount || offer.premium)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                    <span>📄 {offer.offer_number}{offer.company_name ? ` · ${offer.company_name}` : ""}</span>
                    <span>🕒 {formatDateTimeSeconds(offer.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-2">
              <p className="text-[11px] text-slate-500">Un suono di avviso continuerà finché non confermi.</p>
              <Button
                onClick={dismissPartnerOffersAlert}
                className="bg-rose-600 hover:bg-rose-700 text-white font-semibold"
                data-testid="partner-offer-notification-dismiss-btn"
              >
                ✓ Ho visto, chiudi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 🆕 Dialog Rinomina file prima dell'upload (solo in modalità Modifica Preventivo) */}
      <Dialog open={!!renameCurrent} onOpenChange={(o) => { if (!o && !renameUploading) { setRenameCurrent(null); setRenameQueue([]); } }}>
        <DialogContent className="max-w-md" data-testid="rename-before-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5 text-blue-600" /> Rinomina prima dell&apos;upload
            </DialogTitle>
          </DialogHeader>
          {renameCurrent && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2 flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-mono">{renameCurrent.file.name}</div>
                  <div className="text-[10px] text-slate-400">{Math.round((renameCurrent.file.size || 0) / 1024)} KB · {renameCurrent.file.type || "—"}</div>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium">Nome file finale</Label>
                <Input
                  value={renameCurrent.proposedName}
                  onChange={(e) => setRenameCurrent({ ...renameCurrent, proposedName: e.target.value })}
                  autoFocus
                  data-testid="rename-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !renameUploading) confirmRenameAndUpload(); }}
                />
                <p className="text-[10px] text-slate-500 mt-1">Verrà caricato subito sul server e comparirà nella lista &quot;Allegati già salvati&quot;.</p>
              </div>
              <div>
                <Label className="text-xs font-medium flex items-center gap-2">
                  Descrizione (opzionale)
                  {renameCurrent.classifying && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-violet-700 bg-violet-100 border border-violet-200 rounded px-1.5 py-0.5">
                      <Sparkles className="h-3 w-3 animate-pulse" /> Analisi AI…
                    </span>
                  )}
                  {!renameCurrent.classifying && renameCurrent.category && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5" title={`Confidenza ${Math.round((renameCurrent.confidence || 0) * 100)}%`}>
                      <Sparkles className="h-3 w-3" /> {renameCurrent.category}
                    </span>
                  )}
                </Label>
                <Input
                  value={renameCurrent.description}
                  onChange={(e) => setRenameCurrent({ ...renameCurrent, description: e.target.value })}
                  placeholder={renameCurrent.classifying ? "Analisi documento in corso…" : "es. Libretto, Patente, Contratto…"}
                  data-testid="rename-description-input"
                />
              </div>
              {renameQueue.length > 0 && (
                <div className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
                  📦 Altri {renameQueue.length} file in coda dopo questo.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={skipCurrentRename}
              disabled={renameUploading}
              data-testid="rename-skip-btn"
            >
              Salta questo
            </Button>
            <Button
              type="button"
              onClick={confirmRenameAndUpload}
              disabled={renameUploading || !renameCurrent?.proposedName?.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="rename-confirm-btn"
            >
              {renameUploading ? "Upload in corso…" : "Carica ora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Dialog dettaglio offerta ricevuta da partner online */}
      <Dialog open={!!partnerOfferDetail} onOpenChange={(o) => { if (!o) setPartnerOfferDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="partner-offer-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-800">
              🌐 Offerta da partner online — {partnerOfferDetail?.offer_number}
            </DialogTitle>
          </DialogHeader>
          {partnerOfferDetail && (() => {
            const o = partnerOfferDetail;
            const variant = (o.variants && o.variants[0]) || {};
            const coverages = variant.coverages || o.coverages || [];
            const totalGuarantees = coverages.reduce((s, c) => s + (parseFloat(c.premium) || 0), 0);
            const commissions = parseFloat(o.commissions_amount ?? variant.commissions_amount ?? 0) || 0;
            const totalOffer = totalGuarantees + commissions;
            return (
              <div className="space-y-4 text-sm">
                {/* Intestazione compagnia + tipo copertura */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-emerald-50/60 border border-emerald-200 rounded-lg p-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold mb-0.5">Compagnia</div>
                    <div className="font-semibold text-slate-900" data-testid="pod-company">{o.company_name || variant.company || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold mb-0.5">Tipo copertura</div>
                    <div className="font-semibold text-slate-900" data-testid="pod-policy-type">{o.policy_type || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold mb-0.5">Frazionamento</div>
                    <div className="text-slate-900" data-testid="pod-frazionamento">{o.frazionamento || variant.frazionamento || "annuale"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold mb-0.5">Numero rate</div>
                    <div className="text-slate-900" data-testid="pod-installments">{o.installments_count || variant.installments_count || "—"}</div>
                  </div>
                </div>

                {/* Garanzie + eventuali voci accessorie */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-700 font-bold mb-1.5">Garanzie proposte</div>
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 text-slate-700">
                        <tr>
                          <th className="text-left px-2 py-1.5">Garanzia</th>
                          <th className="text-right px-2 py-1.5">Premio (€)</th>
                          <th className="text-right px-2 py-1.5">Massimale (€)</th>
                          <th className="text-left px-2 py-1.5">Franchigia</th>
                        </tr>
                      </thead>
                      <tbody data-testid="pod-coverages">
                        {coverages.map((c, i) => (
                          <tr key={i} className={`border-t border-slate-100 ${c.kind === "accessory" || c.kind === "service" ? "bg-amber-50/50" : ""}`}>
                            <td className="px-2 py-1.5 font-medium text-slate-800">{c.name || "—"}{(c.kind === "accessory" || c.kind === "service") && <span className="ml-1 text-[10px] font-bold text-amber-700">(accessorio)</span>}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{formatCurrency(c.premium || 0)}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{c.sum_insured ? formatCurrency(c.sum_insured) : "—"}</td>
                            <td className="px-2 py-1.5 text-slate-600">{c.deductible || "—"}</td>
                          </tr>
                        ))}
                        {coverages.length === 0 && (
                          <tr><td colSpan={4} className="px-2 py-3 text-center text-slate-400 italic">Nessuna garanzia</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totali */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-0.5">Totale garanzie</div>
                    <div className="font-mono font-bold text-lg text-slate-900" data-testid="pod-total-guarantees">{formatCurrency(totalGuarantees)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-0.5">Commissioni</div>
                    <div className="font-mono font-bold text-lg text-slate-900" data-testid="pod-commissions">{formatCurrency(commissions)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-0.5">Totale offerta</div>
                    <div className="font-mono font-bold text-lg text-emerald-800" data-testid="pod-total-offer">{formatCurrency(totalOffer)}</div>
                  </div>
                </div>

                {/* Massimale + franchigie globali + validità */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(o.max_coverage || variant.max_coverage) && (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-0.5">Massimale complessivo</div>
                      <div className="font-mono text-slate-900" data-testid="pod-max-coverage">{formatCurrency(o.max_coverage || variant.max_coverage)}</div>
                    </div>
                  )}
                  {(o.deductibles || variant.deductibles) && (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-0.5">Franchigia complessiva</div>
                      <div className="text-slate-900" data-testid="pod-deductibles">{o.deductibles || variant.deductibles}</div>
                    </div>
                  )}
                  {o.created_at && (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-0.5">Data creazione</div>
                      <div className="text-slate-900" data-testid="pod-created-at">{formatDate(o.created_at)}</div>
                    </div>
                  )}
                  {(o.valid_until || variant.valid_until) && (
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold mb-0.5">Data scadenza</div>
                      <div className="text-slate-900" data-testid="pod-valid-until">{formatDate(o.valid_until || variant.valid_until)}</div>
                    </div>
                  )}
                </div>

                {/* Note */}
                {(o.notes || variant.notes) && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-700 font-bold mb-1">Note del partner</div>
                    <div className="text-sm bg-yellow-50 border border-yellow-200 rounded-md p-2.5 whitespace-pre-wrap" data-testid="pod-notes">{o.notes || variant.notes}</div>
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartnerOfferDetail(null)} data-testid="pod-close-btn">
              Chiudi
            </Button>
            {(() => {
              // 🆕 Feb 2026 — "Ricrea variante" visibile solo se la variante creata automaticamente
              // dalla submission partner è stata successivamente eliminata dall'admin.
              const o = partnerOfferDetail;
              if (!o || !o.submitted_by_partner) return null;
              const pvid = o.partner_variant_id;
              const stillExists = pvid && (o.variants || []).some((v) => v.id === pvid);
              // Se non c'è mai stato partner_variant_id (offerte pre-Feb2026) usa fallback: mostra se variants è vuoto
              const wasDeleted = pvid ? !stillExists : ((o.variants || []).length === 0);
              if (!wasDeleted) return null;
              return (
                <Button
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/quote-offers/${o.id}/recreate-partner-variant`);
                      toast.success("Variante ricreata dai dati del partner");
                      // Aggiorna localmente per riflettere subito la nuova variante e nascondere il bottone
                      setPartnerOfferDetail((prev) => prev ? ({
                        ...prev,
                        partner_variant_id: data.variant_id,
                        variants: [
                          ...(prev.variants || []),
                          {
                            id: data.variant_id,
                            company: prev.company_name,
                            amount: prev.amount,
                            coverages: prev.coverages,
                            frazionamento: prev.frazionamento,
                            installments_count: prev.installments_count,
                            commissions_amount: prev.commissions_amount,
                            max_coverage: prev.max_coverage,
                            deductibles: prev.deductibles,
                            notes: prev.notes,
                            valid_until: prev.valid_until,
                            created_at: prev.created_at,
                          },
                        ],
                      }) : prev);
                      // Refresh anche della lista offerte del preventivo per aggiornare i badge
                      if (o.quote_id) load();
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="pod-recreate-variant-btn"
                  title="Ricrea la variante originaria con tutti i dati inviati dal partner"
                >
                  🔄 Ricrea variante
                </Button>
              );
            })()}
            <Button
              onClick={() => { if (partnerOfferDetail) navigate(`/quotes/offers?focus=${partnerOfferDetail.id}`); }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="pod-open-full-btn"
            >
              Apri gestione offerte →
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
